import React, { useState, useEffect } from "react";
import { X, RotateCcw, Palette, Sliders, Eye, Sparkles, Shield, Zap, Layers, Info, Check, HelpCircle } from "lucide-react";
import { AVAILABLE_INDICATORS } from "./IndicatorLibraryModal";
import { useAuth } from "../contexts/AuthContext";

const STRATEGY_PRESETS = [
  {
    id: "scalping",
    label: "⚡ Scalping (M1 - M5)",
    desc: "Lướt sóng siêu nhanh, nhạy cảm với biến động ngắn",
    params: { emaFast: 5, emaSlow: 13, rsiPeriod: 9, tpMult: 1.2, slMult: 1.0, minConfidence: 35, cooldownBars: 15 },
  },
  {
    id: "daytrading",
    label: "📊 Day Trading (M5 - M15)",
    desc: "Giao dịch trong ngày cân bằng giữa tỷ lệ thắng và RR",
    params: { emaFast: 9, emaSlow: 21, rsiPeriod: 14, tpMult: 1.5, slMult: 1.2, minConfidence: 35, cooldownBars: 25 },
  },
  {
    id: "swing",
    label: "🌊 Swing Trading (H1 - H4)",
    desc: "Bắt sóng lớn, giữ lệnh theo xu hướng dài hạn",
    params: { emaFast: 12, emaSlow: 26, rsiPeriod: 21, tpMult: 2.5, slMult: 1.8, minConfidence: 65, cooldownBars: 35 },
  },
  {
    id: "high_winrate",
    label: "🎯 High Winrate (Chắt lọc)",
    desc: "Lọc nhiễu tối đa, chỉ vào lệnh khi có tín hiệu đồng thuận cao",
    params: { emaFast: 9, emaSlow: 21, rsiPeriod: 14, tpMult: 1.2, slMult: 1.0, minConfidence: 65, cooldownBars: 25 },
  },
];

const SMC_PRESETS = [
  {
    id: "m1_scalp_sniper",
    label: "⚡ M1 Scalp Sniper (Smart Hybrid + Revert)",
    desc: "Cấu hình chuẩn tối ưu M1: Smart Hybrid (<=80p Market, >80p Limit), Revert MID 1.5R (+1.700p / tháng)",
    params: {
      windowBars: 100,
      lenPH: 3,
      lenPL: 3,
      r1: 1.5,
      r2: 2.0,
      slMin: 10.0,
      slMax: 150.0,
      slExtraVal: 5.0,
      entryMode: "Smart Hybrid",
      midSamePct: 50.0,
      htfFilterEnable: false,
      htfTimeframe: "AUTO",
      htfBufferPips: 5.0,
      smcRevertEnable: true,
      smcRevertEntryMode: "LIMIT_MID",
      smcRevertRr: 1.5,
      smcRevertSlBufferPips: 10.0,
      smcRevertCancelBars: 20,
      smcSingleOrderPerSide: true,
      smcMaxOrdersMode: "ONE_PER_SIDE",
      smcHybridThresholdPips: 80.0,
      smcHybridPullbackPct: 50.0,
    },
  },
  {
    id: "m1_scalp_18r",
    label: "🎯 M1 High RR (Smart Hybrid + Revert 1.8R)",
    desc: "Phiên bản ăn dày: TP 1.8R, Revert 1.8R (+1.650p / tháng)",
    params: {
      windowBars: 100,
      lenPH: 3,
      lenPL: 3,
      r1: 1.8,
      r2: 2.2,
      slMin: 10.0,
      slMax: 150.0,
      slExtraVal: 15.0,
      entryMode: "Smart Hybrid",
      midSamePct: 50.0,
      htfFilterEnable: true,
      htfTimeframe: "AUTO",
      htfBufferPips: 5.0,
      smcRevertEnable: true,
      smcRevertEntryMode: "LIMIT_MID",
      smcRevertRr: 1.8,
      smcRevertSlBufferPips: 10.0,
      smcRevertCancelBars: 20,
      smcSingleOrderPerSide: true,
      smcMaxOrdersMode: "ONE_PER_SIDE",
      smcHybridThresholdPips: 80.0,
      smcHybridPullbackPct: 50.0,
    },
  },
  {
    id: "m5_swing_pro",
    label: "🌊 M5 Swing Pro",
    desc: "Cấu hình M5: TP1 1.5R, TP2 2.0R, Smart Hybrid 100p",
    params: {
      windowBars: 150,
      lenPH: 5,
      lenPL: 5,
      r1: 1.5,
      r2: 2.0,
      slMin: 15.0,
      slMax: 150.0,
      slExtraVal: 15.0,
      entryMode: "Smart Hybrid",
      midSamePct: 50.0,
      htfFilterEnable: false,
      htfTimeframe: "AUTO",
      htfBufferPips: 10.0,
      smcRevertEnable: true,
      smcRevertEntryMode: "LIMIT_MID",
      smcRevertRr: 1.5,
      smcRevertSlBufferPips: 10.0,
      smcRevertCancelBars: 20,
      smcSingleOrderPerSide: true,
      smcMaxOrdersMode: "ONE_PER_SIDE",
      smcHybridThresholdPips: 100.0,
      smcHybridPullbackPct: 50.0,
    },
  },
  {
    id: "daytrading_smc",
    label: "📊 Day Trading MSB (M15)",
    desc: "Cấu hình chuẩn M5 - M15 kết hợp Order Block HTF",
    params: {
      windowBars: 150,
      lenPH: 3,
      lenPL: 3,
      r1: 1.5,
      r2: 2.0,
      slMin: 15.0,
      slMax: 200.0,
      slExtraVal: 10.0,
      entryMode: "Smart Hybrid",
      midSamePct: 50.0,
      htfFilterEnable: true,
      htfTimeframe: "AUTO",
      htfBufferPips: 10.0,
      smcRevertEnable: true,
      smcRevertEntryMode: "LIMIT_MID",
      smcRevertRr: 1.5,
      smcRevertSlBufferPips: 10.0,
      smcRevertCancelBars: 20,
      smcSingleOrderPerSide: true,
      smcMaxOrdersMode: "ONE_PER_SIDE",
      smcHybridThresholdPips: 120.0,
      smcHybridPullbackPct: 50.0,
    },
  },
];

export const HMA_PRESETS = [
  {
    id: "m1_scalp_21_2r",
    label: "🥇 M1 Scalp Pro (HMA 21 - TP 2.0R)",
    desc: "HMA 21, TP 2.0R, SL Buffer 20p, Cooldown 15 nến (Lãi +3.917p / 50k nến)",
    params: {
      period: 21,
      hmaPeriod: 21,
      lookbackBars: 100,
      maxBarsFromExtreme: 35,
      r1: 2.0,
      r2: 2.5,
      slBufferMode: "FIXED",
      slBufferPips: 20.0,
      hmaSlBufferPips: 20.0,
      cooldownBars: 15,
      hmaCooldownBars: 15,
      hmaCurlFilter: false,
      cascadeFilter: false,
      dynamicBeEnable: false,
    },
  },
  {
    id: "m1_safe_pa_21",
    label: "🥈 M1 An Toàn (HMA 21 + TP 2.0R)",
    desc: "HMA 21, TP 2.0R, SL Buffer 20p, Cooldown 20 nến",
    params: {
      period: 21,
      hmaPeriod: 21,
      lookbackBars: 100,
      maxBarsFromExtreme: 35,
      r1: 2.0,
      r2: 2.5,
      slBufferMode: "FIXED",
      slBufferPips: 20.0,
      hmaSlBufferPips: 20.0,
      cooldownBars: 20,
      hmaCooldownBars: 20,
      dynamicBeEnable: false,
    },
  },
  {
    id: "m1_safe_be_24",
    label: "🥉 M1 Khóa Lời BE (HMA 24 - TP 1.8R)",
    desc: "HMA 24, TP 1.8R, SL Buffer 15p, Cooldown 20 nến, Dời BE 70% (Winrate 48.0%, Lãi +2.149p)",
    params: {
      period: 24,
      hmaPeriod: 24,
      lookbackBars: 100,
      maxBarsFromExtreme: 35,
      r1: 1.8,
      r2: 2.2,
      slBufferMode: "FIXED",
      slBufferPips: 15.0,
      hmaSlBufferPips: 15.0,
      cooldownBars: 20,
      hmaCooldownBars: 20,
      hmaCurlFilter: false,
      cascadeFilter: false,
      dynamicBeEnable: true,
      beTriggerPct: 70.0,
      beLockPips: 5.0,
    },
  },
  {
    id: "m5_sniper_24_2r",
    label: "👑 M5 Sniper Pro (HMA 24 - TP 2.0R)",
    desc: "Chuẩn M5: HMA 24, TP 2.0R, SL Max 150p, Buffer 20p, Cooldown 30, Dời BE 85%, Tắt Lọc Độ Dốc",
    params: {
      period: 24,
      hmaPeriod: 24,
      lookbackBars: 100,
      maxBarsFromExtreme: 15,
      r1: 2.0,
      r2: 2.5,
      slBufferMode: "FIXED",
      slBufferPips: 20.0,
      hmaSlBufferPips: 20.0,
      minRiskPips: 3.0,
      maxRiskPips: 150.0,
      cooldownBars: 30,
      hmaCooldownBars: 30,
      dynamicBeEnable: true,
      beTriggerPct: 85.0,
      beLockPips: 5.0,
      hmaSlopeFilter: false,
      candleStrengthFilter: false,
      dynamicLimitEnable: false,
      cascadeFilter: false,
      sessionFilterEnable: false,
    },
  },
];

export const VOLMAN_PRESETS = [
  {
    id: "scalping_20ema",
    label: "⚡ Scalping 20 EMA Chuẩn (M1 - M5)",
    desc: "Bob Volman chuẩn sách: EMA 20, Buildup nén chặt, TP Target 1.5R, SL sau Buildup (BB + TB)",
    params: {
      volmanEmaPeriod: 20,
      volmanR1: 1.5,
      volmanSlBufferPips: 5.0,
      volmanCooldownBars: 5,
      volmanEnableBB: true,
      volmanEnableTB: true,
      volmanMaFilterEnable: false,
      volmanMaPeriod: 200,
      volmanMaType: "EMA",
      volmanMaxConsecutiveEnable: false,
      volmanMaxConsecutiveSameDir: 3,
    },
  },
  {
    id: "ma200_trend_filter",
    label: "🎯 Lọc Xu Hướng Lớn MA 200 (Khuyên Dùng)",
    desc: "Lọc đa khung chuẩn: Trên MA200 chỉ Buy, Dưới MA200 chỉ Sell tránh bắt ngược trend lớn",
    params: {
      volmanEmaPeriod: 20,
      volmanR1: 1.5,
      volmanSlBufferPips: 5.0,
      volmanCooldownBars: 5,
      volmanEnableBB: true,
      volmanEnableTB: true,
      volmanMaFilterEnable: true,
      volmanMaPeriod: 200,
      volmanMaType: "EMA",
      volmanMaxConsecutiveEnable: false,
      volmanMaxConsecutiveSameDir: 3,
    },
  },
  {
    id: "trendline_focus",
    label: "📈 Chuyên Trendline Break (TB)",
    desc: "Tập trung phá vỡ đường xu hướng Trendline Break + Buildup nén bên dưới/trên EMA",
    params: {
      volmanEmaPeriod: 20,
      volmanR1: 1.8,
      volmanSlBufferPips: 4.0,
      volmanCooldownBars: 4,
      volmanEnableBB: false,
      volmanEnableTB: true,
      volmanMaFilterEnable: false,
      volmanMaPeriod: 200,
      volmanMaType: "EMA",
      volmanMaxConsecutiveEnable: false,
      volmanMaxConsecutiveSameDir: 3,
    },
  },
  {
    id: "block_break_focus",
    label: "🧱 Chuyên Block Break (BB)",
    desc: "Bắt phá vỡ ngưỡng cản nằm ngang (Support/Resistance Barrier) sau Buildup nén",
    params: {
      volmanEmaPeriod: 20,
      volmanR1: 1.5,
      volmanSlBufferPips: 5.0,
      volmanCooldownBars: 5,
      volmanEnableBB: true,
      volmanEnableTB: false,
      volmanMaFilterEnable: false,
      volmanMaPeriod: 200,
      volmanMaType: "EMA",
      volmanMaxConsecutiveEnable: false,
      volmanMaxConsecutiveSameDir: 3,
    },
  },
];

export const ORB_PRESETS = [
  {
    id: "smart_hybrid_3r",
    label: "⚡ Smart Hybrid 3.0R (Chuẩn Á + Âu)",
    desc: "Tự động: Phá vỡ <= 15p vào Market, > 15p đặt Limit 50% nến phá vỡ. TP 3.0R.",
    params: {
      orbAsiaEnable: true,
      orbLondonEnable: true,
      orbNyEnable: false,
      orbEntryMode: "SMART_HYBRID",
      orbRr: 3.0,
      orbSlBufferPips: 2.0,
      orbMaxBreakPips: 15.0,
      orbEma200Filter: false,
      orbEma200Mode: "OFF",
      orbMaxZonePips: 250.0,
      orbMinZonePips: 10.0,
    },
  },
  {
    id: "london_focus",
    label: "👑 Chuyên London Open (15h00 VN)",
    desc: "Tập trung thanh khoản cực mạnh mở phiên Luân Đôn, SL chặt mép nến M5, TP 3.0R.",
    params: {
      orbAsiaEnable: false,
      orbLondonEnable: true,
      orbNyEnable: false,
      orbEntryMode: "SMART_HYBRID",
      orbRr: 3.0,
      orbSlBufferPips: 2.0,
      orbMaxBreakPips: 15.0,
      orbEma200Filter: false,
      orbEma200Mode: "OFF",
      orbMaxZonePips: 250.0,
      orbMinZonePips: 10.0,
    },
  },
  {
    id: "asia_scalper",
    label: "🌅 Chuyên Tokyo/Asia Open (08h30 VN)",
    desc: "Bắt sóng mở phiên Á (Tokyo), TP 2.5R, SL mép hộp nến 5m.",
    params: {
      orbAsiaEnable: true,
      orbLondonEnable: false,
      orbNyEnable: false,
      orbEntryMode: "SMART_HYBRID",
      orbRr: 2.5,
      orbSlBufferPips: 2.0,
      orbMaxBreakPips: 15.0,
      orbEma200Filter: false,
      orbEma200Mode: "OFF",
      orbMaxZonePips: 250.0,
      orbMinZonePips: 10.0,
    },
  },
  {
    id: "all_sessions_hybrid",
    label: "🌐 Toàn Bộ Phiên (Á + Âu + Mỹ)",
    desc: "Bật cả 3 phiên giao dịch Á (08h30), Âu (15h00) và Mỹ (20h30) với Smart Hybrid Entry.",
    params: {
      orbAsiaEnable: true,
      orbLondonEnable: true,
      orbNyEnable: true,
      orbEntryMode: "SMART_HYBRID",
      orbRr: 3.0,
      orbSlBufferPips: 2.0,
      orbMaxBreakPips: 15.0,
      orbEma200Filter: false,
      orbEma200Mode: "OFF",
      orbMaxZonePips: 250.0,
      orbMinZonePips: 10.0,
    },
  },
];

export const SWEEP_PRESETS = [
  {
    id: "major_peaks_sniper",
    label: "👑 Sniper Đỉnh Cao Nhất / Đáy Thấp Nhất (Major Swing Only)",
    desc: "Chỉ quét phá vỡ cản tại đỉnh cao nhất hoặc đáy thấp nhất trong phân đoạn (30 nến). Loại bỏ hoàn toàn các đỉnh/đáy lọt thỏm.",
    params: {
      sweepLookback: 5,
      sweepHighestPeakFilter: true,
      sweepPeakLookback: 30,
      sweepOpt: "Only Outbreaks & Retest",
      sweepExtend: true,
      sweepMaxBars: 300,
      sweepEntryMode: "RETEST",
      sweepRr: 2.0,
      sweepSlBufferPips: 1.5,
      sweepMinRiskPips: 3.0,
      sweepMaxRiskPips: 150.0,
      sweepCooldownBars: 3,
      sweepTrendFilterEnable: false,
      sweepTrendEmaPeriod: 200,
      sweepLimitCancelBars: 30,
      sweepMaxWaitBars: 5,
    },
  },
  {
    id: "sweep_retest",
    label: "🎯 Phá Vỡ Cản & Retest Chuẩn (Khuyên dùng - Thắng 59.3% | PF 2.85)",
    desc: "Chờ nến đóng phá vỡ đỉnh/đáy rồi hồi retest xác nhận: L=5, Entry Retest Limit, RR 1:2.0. Tỷ lệ thắng 59.3%, PF 2.85!",
    params: {
      sweepLookback: 5,
      sweepHighestPeakFilter: false,
      sweepPeakLookback: 30,
      sweepOpt: "Only Outbreaks & Retest",
      sweepExtend: true,
      sweepMaxBars: 300,
      sweepEntryMode: "RETEST",
      sweepRr: 2.0,
      sweepSlBufferPips: 1.5,
      sweepMinRiskPips: 3.0,
      sweepMaxRiskPips: 150.0,
      sweepCooldownBars: 3,
      sweepTrendFilterEnable: false,
      sweepTrendEmaPeriod: 200,
      sweepLimitCancelBars: 30,
      sweepMaxWaitBars: 5,
    },
  },
  {
    id: "sweep_scalper",
    label: "⚡ Phá Vỡ Cản Scalper M1/M5 (Lookback 3)",
    desc: "Scalping vùng cản phá vỡ phản ứng nhanh: L=3, Entry Retest Limit, RR 1:1.8",
    params: {
      sweepLookback: 3,
      sweepHighestPeakFilter: false,
      sweepPeakLookback: 20,
      sweepOpt: "Only Outbreaks & Retest",
      sweepExtend: true,
      sweepMaxBars: 200,
      sweepEntryMode: "RETEST",
      sweepRr: 1.8,
      sweepSlBufferPips: 1.2,
      sweepMinRiskPips: 2.5,
      sweepMaxRiskPips: 100.0,
      sweepCooldownBars: 2,
      sweepTrendFilterEnable: false,
      sweepTrendEmaPeriod: 200,
      sweepLimitCancelBars: 25,
      sweepMaxWaitBars: 3,
    },
  },
];

export const SCALPING_PRESETS = [
  {
    id: "scalp_pro_default",
    label: "⚡ Scalping Pro Chuẩn (+8,262 Pips | Win 39.2% | PF 1.25)",
    desc: "Cấu hình chuẩn đồng bộ 100% với Sweep Pro gốc: Outbreaks & Retest, TP RR 1:2.0, SL Buffer 1.5p, Swing 25p.",
    params: {
      scalpLookback: 5,
      scalpHighestPeakFilter: false,
      scalpPeakLookback: 30,
      scalpOpt: "Only Outbreaks & Retest",
      scalpExtend: true,
      scalpMaxBars: 300,
      scalpEntryMode: "RETEST",
      scalpRr: 2.0,
      scalpSlBufferPips: 1.5,
      scalpMinRiskPips: 3.0,
      scalpMaxRiskPips: 150.0,
      scalpCooldownBars: 3,
      scalpMinSwingPips: 25.0,
      scalpLimitCancelBars: 30,
      scalpMaxWaitBars: 5,
      scalpTrendFilterEnable: false,
      scalpTrendEmaPeriod: 200,
      scalpSessionFilterEnable: false,
      scalpSessionStart: "00:00",
      scalpSessionEnd: "23:59",
      scalpCloseOnOpposite: false,
      scalpDynamicBeEnable: false,
      scalpBeTriggerMode: "PCT_TP",
      scalpBeTriggerPct: 50.0,
      scalpBeTriggerPips: 40.0,
      scalpBeLockPips: 2.0,
    },
  },
  {
    id: "m1_fast_scalper",
    label: "⚡ M1 Fast Scalper (Thắng 59.8% | 1,015 lệnh | +5,300 pips)",
    desc: "Chiến thuật Scalping siêu tốc trên M1: Săn thanh khoản Smart Hybrid, SL Buffer 1.5p, RR 1:2.0, đạt 1015 lệnh và +5,300 pips lợi nhuận!",
    params: {
      scalpLookback: 5,
      scalpHighestPeakFilter: false,
      scalpPeakLookback: 30,
      scalpOpt: "Only Outbreaks & Retest",
      scalpExtend: true,
      scalpMaxBars: 300,
      scalpEntryMode: "SMART_HYBRID",
      scalpHybridThresholdPips: 80.0,
      scalpHybridLimitMode: "RETEST",
      scalpHybridPullbackPct: 50.0,
      scalpRr: 2.0,
      scalpSlBufferPips: 1.5,
      scalpMinRiskPips: 3.0,
      scalpMaxRiskPips: 150.0,
      scalpCooldownBars: 3,
      scalpTrendFilterEnable: false,
      scalpTrendEmaPeriod: 200,
      scalpLimitCancelBars: 30,
      scalpMaxWaitBars: 20,
      scalpCloseOnOpposite: false,
      scalpDynamicBeEnable: false,
      scalpBeTriggerMode: "PCT_TP",
      scalpBeTriggerPct: 50.0,
      scalpBeTriggerPips: 40.0,
      scalpBeLockPips: 2.0,
    },
  },
  {
    id: "smart_hybrid_scalp",
    label: "🧠 Smart Hybrid Scalper (Tự động Market / Limit)",
    desc: "Vào lệnh thông minh: SL ngắn <= 80 pips vào Market ngay; SL dài > 80 pips tự động đổi sang Limit Retest.",
    params: {
      scalpLookback: 5,
      scalpHighestPeakFilter: false,
      scalpPeakLookback: 30,
      scalpOpt: "Wicks + Outbreaks & Retest",
      scalpExtend: true,
      scalpMaxBars: 300,
      scalpEntryMode: "SMART_HYBRID",
      scalpHybridThresholdPips: 80.0,
      scalpHybridLimitMode: "RETEST",
      scalpHybridPullbackPct: 50.0,
      scalpRr: 2.0,
      scalpSlBufferPips: 1.5,
      scalpMinRiskPips: 3.0,
      scalpMaxRiskPips: 150.0,
      scalpCooldownBars: 3,
      scalpTrendFilterEnable: false,
      scalpTrendEmaPeriod: 200,
      scalpLimitCancelBars: 30,
      scalpMaxWaitBars: 20,
      scalpCloseOnOpposite: false,
      scalpDynamicBeEnable: false,
    },
  },
  {
    id: "major_peaks_scalp",
    label: "👑 Sniper Đỉnh/Đáy Lớn (Major Swing)",
    desc: "Chỉ quét đỉnh cao nhất hoặc đáy thấp nhất trong 30 nến.",
    params: {
      scalpLookback: 5,
      scalpHighestPeakFilter: true,
      scalpPeakLookback: 30,
      scalpOpt: "Only Outbreaks & Retest",
      scalpExtend: true,
      scalpMaxBars: 300,
      scalpEntryMode: "SMART_HYBRID",
      scalpHybridThresholdPips: 80.0,
      scalpHybridLimitMode: "RETEST",
      scalpHybridPullbackPct: 50.0,
      scalpRr: 2.0,
      scalpSlBufferPips: 1.5,
      scalpMinRiskPips: 3.0,
      scalpMaxRiskPips: 150.0,
      scalpCooldownBars: 3,
      scalpTrendFilterEnable: false,
      scalpLimitCancelBars: 30,
      scalpMaxWaitBars: 20,
      scalpCloseOnOpposite: false,
      scalpDynamicBeEnable: false,
    },
  }
];


// Helper Component for TradingView-style Line Customization (Color + Width + Style)
function LineStyleRow({ label, visible, onToggleVisible, color, onChangeColor, width, onChangeWidth, style, onChangeStyle }) {
  return (
    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/60 border border-slate-800 hover:border-slate-700 transition">
      <label className="flex items-center gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={visible}
          onChange={(e) => onToggleVisible(e.target.checked)}
          className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
        />
        <span className={`text-xs font-semibold ${visible ? "text-slate-200" : "text-slate-500 line-through"}`}>
          {label}
        </span>
      </label>

      <div className="flex items-center gap-2">
        <div className="relative flex items-center">
          <input
            type="color"
            value={color}
            onChange={(e) => onChangeColor(e.target.value)}
            className="w-6 h-6 rounded-md border border-slate-700 bg-transparent cursor-pointer p-0 appearance-none overflow-hidden"
            title="Chọn màu sắc"
          />
        </div>

        <select
          value={width}
          onChange={(e) => onChangeWidth(parseInt(e.target.value) || 1)}
          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-slate-300 text-[11px] focus:outline-none focus:border-cyan-500 cursor-pointer"
          title="Độ dày nét vẽ"
        >
          <option value="1">─ 1px (Mảnh)</option>
          <option value="2">━ 2px (Vừa)</option>
          <option value="3">█ 3px (Dày)</option>
        </select>

        <select
          value={style}
          onChange={(e) => onChangeStyle(parseInt(e.target.value) || 0)}
          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-slate-300 text-[11px] focus:outline-none focus:border-cyan-500 cursor-pointer"
          title="Kiểu nét vẽ (Liền / Đứt / Chấm)"
        >
          <option value="0">── Nét Liền</option>
          <option value="2">-- Nét Đứt</option>
          <option value="1">·· Nét Chấm</option>
        </select>
      </div>
    </div>
  );
}

export default function IndicatorSettingsModal({
  isOpen,
  onClose,
  indicatorId,
  strategyParams,
  onApplyParams,
  indicatorStyles,
  onApplyStyles,
  publicConfig,
}) {
  const { isAdmin, isBasic, promptTierGate } = useAuth();
  const [activeTab, setActiveTab] = useState("inputs");
  const [activePreset, setActivePreset] = useState("daytrading");

  const getAdminConfig = () => {
    if (publicConfig && typeof publicConfig === "object" && Object.keys(publicConfig).length > 0) {
      return publicConfig;
    }
    try {
      const saved = localStorage.getItem("minopro_public_config");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {};
  };

  // Strategy Form State (Matrix Pro)
  const [matrixData, setMatrixData] = useState({
    emaFast: 9,
    emaSlow: 21,
    rsiPeriod: 14,
    tpMult: 1.8,
    slMult: 1.2,
    minConfidence: 35,
    slFilterEnable: true,
    slMin: 10.0,
    slMax: 100.0,
    cooldownBars: 12,
    timeoutExitEnable: false,
    maxHoldingBars: 30,
    sessionFilterEnable: false,
    sessionStart: "06:00",
    sessionEnd: "03:30",
    ema200FilterEnable: true,
    ema200DistFilterEnable: false,
    ema200OuterPips: 200.0,
    ema200InnerPips: 100.0,
    closeOnly: true,
    showEMA: false,
    showTPSL: true,
    showForecast: true,
    showRSI: false,
  });

  // SMC Dynamic State
  const [smcData, setSmcData] = useState({
    windowBars: 100,
    lenPH: 3,
    lenPL: 3,
    minGapBars: 2,
    r1: 1.5,
    r2: 2.0,
    entryMode: "Smart Hybrid",
    fibPct: 35.0,
    halfBreakBasis: "Range(H-L)",
    slFilterEnable: true,
    slMin: 10.0,
    slMax: 150.0,
    slExtraOn: true,
    slExtraVal: 5.0,
    pendingCancelOn: true,
    pendingCancelN: 40,
    revEnable: true,
    revR: 2.0,
    htfFilterEnable: false,
    htfTimeframe: "AUTO",
    htfBufferPips: 5.0,
    htfMaxAgeDays: 7,
    htfMode: "touch",
    breakevenEnable: false,
    breakevenPips: 100.0,
    fvgFilterEnable: false,
    smcRevertEnable: true,
    smcRevertEntryMode: "LIMIT_MID",
    smcRevertRr: 1.5,
    smcRevertSlBufferPips: 10.0,
    smcRevertCancelBars: 20,
    smcSingleOrderPerSide: true,
    smcMaxOrdersMode: "ONE_PER_SIDE",
    smcHybridThresholdPips: 80.0,
    smcHybridPullbackPct: 50.0,
    showMSBLines: true,
    showTP: true,
    showSL: true,
  });

  // Classical SMC State
  const [classicalSmcData, setClassicalSmcData] = useState({
    pivotLength: 5,
    obLookback: 20,
    mitigationMethod: "close",
    showOB: true,
    hideMitigatedOB: false,
    showFVG: true,
    hideMitigatedFVG: false,
    showBOS: true,
    showCHoCH: true,
  });

  // Classical SMC Style State
  const [classicalSmcStyle, setClassicalSmcStyle] = useState({
    bullishOBColor: "#10b981",
    bearishOBColor: "#ef4444",
    bullishFVGColor: "#06b6d4",
    bearishFVGColor: "#f43f5e",
    bosColor: "#38bdf8",
    chochColor: "#f59e0b",
    showLabels: true,
  });

  // SMC Style State
  const [smcStyle, setSmcStyle] = useState({
    anchor: { visible: false, color: "#ef4444", width: 2, style: 0 },
    mid: { visible: false, color: "#38bdf8", width: 1, style: 0 },
    same: { visible: false, color: "#c084fc", width: 1, style: 0 },
    entry: { visible: true, color: "#facc15", width: 1, style: 2 },
    tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
    tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
    sl: { visible: true, color: "#f97316", width: 1, style: 0 },
    showLabels: true,
  });

  // Ichimoku Form State
  const [ichimokuData, setIchimokuData] = useState({
    tenkan: 9,
    kijun: 26,
    senkouB: 52,
    displacement: 26,
  });

  const [ichimokuStyle, setIchimokuStyle] = useState({
    tenkan: { visible: true, color: "#06b6d4", width: 1, style: 0 },
    kijun: { visible: true, color: "#f59e0b", width: 2, style: 0 },
    chikou: { visible: true, color: "#10b981", width: 1, style: 1 },
    spanA: { visible: true, color: "#10b981", width: 1, style: 0 },
    spanB: { visible: true, color: "#ef4444", width: 1, style: 0 },
    cloudFill: true,
    cloudBullColor: "#10b981",
    cloudBearColor: "#ef4444",
  });

  // Multi EMA Styles State
  const [multiEmaData, setMultiEmaData] = useState({
    ema1: 34,
    ema2: 89,
    ema3: 200,
    ema4: 610,
  });

  const [multiEmaStyle, setMultiEmaStyle] = useState({
    ema1: { visible: true, color: "#38bdf8", width: 1, style: 0 },
    ema2: { visible: true, color: "#facc15", width: 2, style: 0 },
    ema3: { visible: true, color: "#c084fc", width: 2, style: 0 },
    ema4: { visible: true, color: "#fb7185", width: 2, style: 0 },
  });

  // Bollinger Bands Form State
  const [bbData, setBbData] = useState({
    period: 20,
    stdDev: 2.0,
    source: "close",
  });

  const [bbStyle, setBbStyle] = useState({
    upper: { visible: true, color: "#38bdf8", width: 1, style: 2 },
    lower: { visible: true, color: "#38bdf8", width: 1, style: 2 },
  });

  // MACD Form State
  const [macdData, setMacdData] = useState({
    fast: 12,
    slow: 26,
    signal: 9,
  });

  // HMA Indicator Form & Style State
  const [hmaData, setHmaData] = useState({
    period: 21,
  });

  const [hmaStyle, setHmaStyle] = useState({
    visible: true,
    bullColor: "#10b981",
    bearColor: "#ef4444",
    width: 2,
    style: 0,
  });

  // HMA Breakout System Form & Style State
  const [hmaSystemData, setHmaSystemData] = useState({
    period: 21,
    lookbackBars: 100,
    hmaSlopeFilter: true,
    maxHmaSlope: 3.0,
    minBodyPct: 40,
    maxBarsFromExtreme: 20,
    allowFloatingBreakout: false,
    candleStrengthFilter: false,
    minCandleBodyRatio: 35,
    maxRejectionWickRatio: 40,
    hmaCurlFilter: true,
    cascadeFilter: false,
    bearRatioThresh: 70,
    dynamicLimitEnable: false,
    limitThresholdPips: 80.0,
    limitPullbackPct: 50.0,
    limitCancelBars: 10,
    closeOnOpposite: true,
    dynamicBeEnable: false,
    beTriggerPct: 70.0,
    beLockPips: 5.0,
    r1: 2.0,
    r2: 2.5,
    slBufferMode: "FIXED",
    slBufferMinPips: 5.0,
    slBufferMaxPips: 30.0,
    slAtrMult: 1.0,
    slBufferPips: 15.0,
    minRiskPips: 3.0,
    maxRiskPips: 300.0,
    hmaCooldownBars: 15,
    sessionFilterEnable: false,
    sessionMode: "ALL",
    sessionStart: "06:00",
    sessionEnd: "19:30",
    hmaShowLine: true,
    hmaShowBaseBox: false,
    showTP: true,
    showSL: true,
  });

  const [hmaSystemStyle, setHmaSystemStyle] = useState({
    hmaLine: { visible: true, bullColor: "#10b981", bearColor: "#ef4444", width: 2, style: 0 },
    buyBoxColor: "#06b6d4",
    sellBoxColor: "#f59e0b",
    entry: { visible: true, color: "#facc15", width: 1, style: 2 },
    tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
    tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
    sl: { visible: true, color: "#ef4444", width: 1, style: 0 },
    showLabels: true,
  });

  // Bob Volman Form & Style State
  const [volmanData, setVolmanData] = useState({
    volmanEmaPeriod: 20,
    volmanR1: 1.5,
    volmanSlBufferPips: 5.0,
    volmanCooldownBars: 5,
    volmanEnableBB: true,
    volmanEnableTB: true,
    volmanEnableSB: false,
    volmanPivotLength: 3,
    volmanSlFilterEnable: true,
    volmanMinRiskPips: 3.0,
    volmanMaxRiskPips: 150.0,
    volmanBreakoutCandleFilter: true,
    volmanMinBodyRatio: 50,
    volmanMinBodyBeyondPct: 30,
    volmanMaxRejectionWick: 25,
    volmanDynamicLimitEnable: false,
    volmanLimitThresholdPips: 80,
    volmanLimitPullbackPct: 40,
    volmanLimitCancelBars: 20,
    volmanMaFilterEnable: false,
    volmanMaPeriod: 200,
    volmanMaType: "EMA",
    volmanMaxConsecutiveEnable: false,
    volmanMaxConsecutiveSameDir: 3,
  });

  const [volmanStyle, setVolmanStyle] = useState({
    emaLine: { visible: true, color: "#06b6d4", width: 2, style: 0 },
    barrierColor: "#f59e0b",
    trendlineColor: "#c084fc",
    buyBuildupColor: "#06b6d4",
    sellBuildupColor: "#f43f5e",
    entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
    tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
    sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
    showLabels: true,
  });

  // ORB Session Form & Style State
  const [orbSessionData, setOrbSessionData] = useState({
    orbAsiaEnable: true,
    orbLondonEnable: true,
    orbLondonEndHour: 19,
    orbNyEnable: false,
    orbEntryMode: "SMART_HYBRID",
    orbRr: 3.0,
    orbSlBufferMode: "FIXED", // "ATR" | "FIXED"
    orbSlBufferPips: 2.0,
    orbSlAtrMult: 1.0,
    orbSlBufferMinPips: 2.0,
    orbSlBufferMaxPips: 15.0,
    orbSlFilterEnable: false,
    orbMinSlPips: 3.0,
    orbMaxSlPips: 150.0,
    orbMaxBreakPips: 15.0,
    orbMaxLimitBars: 6,
    orbCancelOnTp: true,
    orbCancelOnSl: true,
    orbEma200Filter: false,
    orbEma200Mode: "OFF",
    orbMaxZonePips: 250.0,
    orbMinZonePips: 10.0,
    orbDynamicBeEnable: false,
    orbBeTriggerPct: 75,
    orbBeLockPips: 5.0,
    orbTimeoutExitEnable: true,
    orbRevertEnable: false,
    orbRevertEntryMode: "LIMIT_50_BOX",
    orbRevertRr: 1.5,
    orbRevertCancelBars: 6,
    orbRevertSlBufferPips: 2.0,
    orbDynamicLimitEnable: false,
    orbLimitThresholdPips: 100.0,
    orbLimitPullbackPct: 50.0,
    orbLimitCancelBars: 10,
  });

  const [orbSessionStyle, setOrbSessionStyle] = useState({
    asiaBoxColor: "#38bdf8",
    londonBoxColor: "#eab308",
    nyBoxColor: "#a855f7",
    entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
    tp: { visible: true, color: "#10b981", width: 1.5, style: 2 },
    sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
    showLabels: true,
  });

  // Liquidity Sweep Form & Style State
  const [scalpingProData, setScalpingProData] = useState({
    scalpLookback: 5,
    scalpOpt: "Only Outbreaks & Retest",
    scalpExtend: true,
    scalpMaxBars: 300,
    scalpEntryMode: "RETEST",
    scalpRr: 2.0,
    scalpSlBufferPips: 1.5,
    scalpMinRiskPips: 3.0,
    scalpMaxRiskPips: 150.0,
    scalpCooldownBars: 3,
    scalpMinSwingPips: 25.0,
    scalpLimitCancelBars: 30,
    scalpHighestPeakFilter: false,
    scalpPeakLookback: 30,
    scalpMaxWaitBars: 5,
    scalpTrendFilterEnable: false,
    scalpTrendEmaPeriod: 200,
    scalpSessionFilterEnable: false,
    scalpSessionStart: "00:00",
    scalpSessionEnd: "23:59",
    scalpCloseOnOpposite: false,
    scalpDynamicBeEnable: false,
    scalpBeTriggerMode: "PCT_TP",
    scalpBeTriggerPct: 50.0,
    scalpBeTriggerPips: 40.0,
    scalpBeLockPips: 2.0,
  });

  const [liquiditySweepData, setLiquiditySweepData] = useState(() => {
    const adminCfg = getAdminConfig();
    return {
      sweepM1AnchorM5: adminCfg.sweep_m1_anchor_m5 !== undefined ? Boolean(adminCfg.sweep_m1_anchor_m5) : true,
      sweepLookback: adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : 5,
      sweepOpt: adminCfg.sweep_opt || "Only Outbreaks & Retest",
      sweepExtend: adminCfg.sweep_extend !== undefined ? Boolean(adminCfg.sweep_extend) : true,
      sweepMaxBars: adminCfg.sweep_max_bars !== undefined ? Number(adminCfg.sweep_max_bars) : 300,
      sweepEntryMode: adminCfg.sweep_entry_mode || "SMART_HYBRID",
      sweepRr: adminCfg.sweep_rr !== undefined ? Number(adminCfg.sweep_rr) : 2.0,
      sweepSlBufferPips: adminCfg.sweep_sl_buffer_pips !== undefined ? Number(adminCfg.sweep_sl_buffer_pips) : 1.5,
      sweepMinRiskPips: adminCfg.sweep_min_risk_pips !== undefined ? Number(adminCfg.sweep_min_risk_pips) : 3.0,
      sweepMaxRiskPips: adminCfg.sweep_max_risk_pips !== undefined ? Number(adminCfg.sweep_max_risk_pips) : 150.0,
      sweepCooldownBars: adminCfg.sweep_cooldown_bars !== undefined ? Number(adminCfg.sweep_cooldown_bars) : 3,
      sweepMinSwingPips: adminCfg.sweep_min_swing_pips !== undefined ? Number(adminCfg.sweep_min_swing_pips) : 18.0,
      sweepCompareBars: adminCfg.sweep_compare_bars !== undefined ? Number(adminCfg.sweep_compare_bars) : 25,
      sweepHighestPeakFilter: adminCfg.sweep_highest_peak_filter !== undefined ? Boolean(adminCfg.sweep_highest_peak_filter) : false,
      sweepPeakLookback: adminCfg.sweep_peak_lookback !== undefined ? Number(adminCfg.sweep_peak_lookback) : 30,
      sweepTrendFilterEnable: adminCfg.sweep_trend_filter_enable !== undefined ? Boolean(adminCfg.sweep_trend_filter_enable) : false,
      sweepTrendEmaPeriod: adminCfg.sweep_trend_ema_period !== undefined ? Number(adminCfg.sweep_trend_ema_period) : 200,
      sweepSessionFilterEnable: adminCfg.sweep_session_filter_enable !== undefined ? Boolean(adminCfg.sweep_session_filter_enable) : false,
      sweepSessionStart: adminCfg.sweep_session_start || "00:00",
      sweepSessionEnd: adminCfg.sweep_session_end || "23:59",
      sweepLimitCancelBars: adminCfg.sweep_limit_cancel_bars !== undefined ? Number(adminCfg.sweep_limit_cancel_bars) : 30,
      sweepMaxWaitBars: adminCfg.sweep_max_wait_bars !== undefined ? Number(adminCfg.sweep_max_wait_bars) : 5,
      sweepCloseOnOpposite: adminCfg.sweep_close_on_opposite !== undefined ? Boolean(adminCfg.sweep_close_on_opposite) : false,
      sweepDynamicBeEnable: adminCfg.sweep_dynamic_be_enable !== undefined ? Boolean(adminCfg.sweep_dynamic_be_enable) : false,
      sweepBeTriggerMode: adminCfg.sweep_be_trigger_mode || "PCT_TP",
      sweepBeTriggerPct: adminCfg.sweep_be_trigger_pct !== undefined ? Number(adminCfg.sweep_be_trigger_pct) : 50.0,
      sweepBeTriggerPips: adminCfg.sweep_be_trigger_pips !== undefined ? Number(adminCfg.sweep_be_trigger_pips) : 40.0,
      sweepBeLockPips: adminCfg.sweep_be_lock_pips !== undefined ? Number(adminCfg.sweep_be_lock_pips) : 2.0,
      sweepHybridThresholdPips: adminCfg.sweep_hybrid_threshold_pips !== undefined ? Number(adminCfg.sweep_hybrid_threshold_pips) : 150.0,
      sweepHybridLimitMode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
      sweepHybridZonePct: adminCfg.sweep_hybrid_zone_pct !== undefined ? Number(adminCfg.sweep_hybrid_zone_pct) : 50.0,
      sweepHybridPullbackPct: adminCfg.sweep_hybrid_pullback_pct !== undefined ? Number(adminCfg.sweep_hybrid_pullback_pct) : 50.0,
    };
  });

  const [liquiditySweepStyle, setLiquiditySweepStyle] = useState({
    bslColor: "#f59e0b",
    sslColor: "#06b6d4",
    mssColor: "#a855f7",
    entry: { visible: true, color: "#8b5cf6", width: 1.5, style: 0 },
    tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
    tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
    sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
    showLabels: true,
    showZones: false,
  });

  // Load Initial Strategy Parameters
  useEffect(() => {
    if (strategyParams) {
      setMatrixData((prev) => ({
        ...prev,
        emaFast: strategyParams.matrixEmaFast !== undefined ? Number(strategyParams.matrixEmaFast) : (strategyParams.emaFast !== undefined ? Number(strategyParams.emaFast) : prev.emaFast),
        emaSlow: strategyParams.matrixEmaSlow !== undefined ? Number(strategyParams.matrixEmaSlow) : (strategyParams.emaSlow !== undefined ? Number(strategyParams.emaSlow) : prev.emaSlow),
        rsiPeriod: strategyParams.matrixRsiPeriod !== undefined ? Number(strategyParams.matrixRsiPeriod) : (strategyParams.rsiPeriod !== undefined ? Number(strategyParams.rsiPeriod) : prev.rsiPeriod),
        tpMult: strategyParams.matrixTpMult !== undefined ? Number(strategyParams.matrixTpMult) : (strategyParams.tpMult !== undefined ? Number(strategyParams.tpMult) : prev.tpMult),
        slMult: strategyParams.matrixSlMult !== undefined ? Number(strategyParams.matrixSlMult) : (strategyParams.slMult !== undefined ? Number(strategyParams.slMult) : prev.slMult),
        minConfidence: strategyParams.matrixMinConfidence !== undefined ? Number(strategyParams.matrixMinConfidence) : (strategyParams.minConfidence !== undefined ? Number(strategyParams.minConfidence) : prev.minConfidence),
        cooldownBars: strategyParams.matrixCooldownBars !== undefined ? Number(strategyParams.matrixCooldownBars) : (strategyParams.matrix_cooldown_bars !== undefined ? Number(strategyParams.matrix_cooldown_bars) : (strategyParams.cooldownBars !== undefined && strategyParams.hmaCooldownBars === undefined && strategyParams.volmanCooldownBars === undefined ? Number(strategyParams.cooldownBars) : prev.cooldownBars)),
        slFilterEnable: strategyParams.matrixSlFilterEnable !== undefined ? Boolean(strategyParams.matrixSlFilterEnable) : (strategyParams.slFilterEnable !== undefined && strategyParams.smcSlFilterEnable === undefined ? Boolean(strategyParams.slFilterEnable) : prev.slFilterEnable),
        slMin: strategyParams.matrixSlMin !== undefined ? Number(strategyParams.matrixSlMin) : (strategyParams.matrix_sl_min !== undefined ? Number(strategyParams.matrix_sl_min) : (strategyParams.slMin !== undefined && strategyParams.smcSlMin === undefined ? Number(strategyParams.slMin) : prev.slMin)),
        slMax: strategyParams.matrixSlMax !== undefined ? Number(strategyParams.matrixSlMax) : (strategyParams.matrix_sl_max !== undefined ? Number(strategyParams.matrix_sl_max) : (strategyParams.slMax !== undefined && strategyParams.smcSlMax === undefined ? Number(strategyParams.slMax) : prev.slMax)),
        timeoutExitEnable: strategyParams.matrixTimeoutExitEnable !== undefined ? Boolean(strategyParams.matrixTimeoutExitEnable) : (strategyParams.timeoutExitEnable !== undefined ? Boolean(strategyParams.timeoutExitEnable) : prev.timeoutExitEnable),
        maxHoldingBars: strategyParams.matrixMaxHoldingBars !== undefined ? Number(strategyParams.matrixMaxHoldingBars) : (strategyParams.maxHoldingBars !== undefined ? Number(strategyParams.maxHoldingBars) : prev.maxHoldingBars),
        sessionFilterEnable: strategyParams.matrixSessionFilterEnable !== undefined ? Boolean(strategyParams.matrixSessionFilterEnable) : (strategyParams.sessionFilterEnable !== undefined ? Boolean(strategyParams.sessionFilterEnable) : prev.sessionFilterEnable),
        sessionStart: strategyParams.matrixSessionStart || strategyParams.sessionStart || prev.sessionStart,
        sessionEnd: strategyParams.matrixSessionEnd || strategyParams.sessionEnd || prev.sessionEnd,
        ema200FilterEnable: strategyParams.matrixEma200FilterEnable !== undefined ? Boolean(strategyParams.matrixEma200FilterEnable) : (strategyParams.ema200FilterEnable !== undefined ? Boolean(strategyParams.ema200FilterEnable) : prev.ema200FilterEnable),
        ema200DistFilterEnable: strategyParams.matrixEma200DistFilterEnable !== undefined ? Boolean(strategyParams.matrixEma200DistFilterEnable) : (strategyParams.ema200DistFilterEnable !== undefined ? Boolean(strategyParams.ema200DistFilterEnable) : prev.ema200DistFilterEnable),
        ema200OuterPips: strategyParams.matrixEma200OuterPips !== undefined ? Number(strategyParams.matrixEma200OuterPips) : (strategyParams.ema200OuterPips !== undefined ? Number(strategyParams.ema200OuterPips) : prev.ema200OuterPips),
        ema200InnerPips: strategyParams.matrixEma200InnerPips !== undefined ? Number(strategyParams.matrixEma200InnerPips) : (strategyParams.ema200InnerPips !== undefined ? Number(strategyParams.ema200InnerPips) : prev.ema200InnerPips),
        showEMA: strategyParams.showEMA !== undefined ? Boolean(strategyParams.showEMA) : prev.showEMA,
        showTPSL: strategyParams.showTPSL !== undefined ? Boolean(strategyParams.showTPSL) : prev.showTPSL,
        showForecast: strategyParams.showForecast !== undefined ? Boolean(strategyParams.showForecast) : prev.showForecast,
        showRSI: strategyParams.showRSI !== undefined ? Boolean(strategyParams.showRSI) : prev.showRSI,
      }));

      setSmcData((prev) => ({
        ...prev,
        windowBars: strategyParams.smcWindowBars !== undefined ? Number(strategyParams.smcWindowBars) : (strategyParams.smc_window_bars !== undefined ? Number(strategyParams.smc_window_bars) : (strategyParams.windowBars !== undefined ? Number(strategyParams.windowBars) : prev.windowBars)),
        lenPH: strategyParams.smcLenPH !== undefined ? Number(strategyParams.smcLenPH) : (strategyParams.smc_len_ph !== undefined ? Number(strategyParams.smc_len_ph) : (strategyParams.lenPH !== undefined ? Number(strategyParams.lenPH) : prev.lenPH)),
        lenPL: strategyParams.smcLenPL !== undefined ? Number(strategyParams.smcLenPL) : (strategyParams.smc_len_pl !== undefined ? Number(strategyParams.smc_len_pl) : (strategyParams.lenPL !== undefined ? Number(strategyParams.lenPL) : prev.lenPL)),
        minGapBars: strategyParams.smcMinGapBars !== undefined ? Number(strategyParams.smcMinGapBars) : (strategyParams.smc_min_gap_bars !== undefined ? Number(strategyParams.smc_min_gap_bars) : (strategyParams.minGapBars !== undefined ? Number(strategyParams.minGapBars) : prev.minGapBars)),
        r1: strategyParams.smcR1 !== undefined ? Number(strategyParams.smcR1) : (strategyParams.smc_r1 !== undefined ? Number(strategyParams.smc_r1) : (strategyParams.r1 !== undefined && strategyParams.hmaR1 === undefined && strategyParams.volmanR1 === undefined ? Number(strategyParams.r1) : prev.r1)),
        r2: strategyParams.smcR2 !== undefined ? Number(strategyParams.smcR2) : (strategyParams.smc_r2 !== undefined ? Number(strategyParams.smc_r2) : (strategyParams.r2 !== undefined && strategyParams.hmaR2 === undefined ? Number(strategyParams.r2) : prev.r2)),
        entryMode: strategyParams.smcEntryMode || strategyParams.entryMode || prev.entryMode,
        fibPct: strategyParams.smcFibPct !== undefined ? Number(strategyParams.smcFibPct) : (strategyParams.fibPct !== undefined ? Number(strategyParams.fibPct) : prev.fibPct),
        halfBreakBasis: strategyParams.smcHalfBreakBasis || strategyParams.halfBreakBasis || prev.halfBreakBasis,
        slFilterEnable: strategyParams.smcSlFilterEnable !== undefined ? Boolean(strategyParams.smcSlFilterEnable) : (strategyParams.slFilterEnable !== undefined && strategyParams.matrixSlFilterEnable === undefined ? Boolean(strategyParams.slFilterEnable) : prev.slFilterEnable),
        slMin: strategyParams.smcSlMin !== undefined ? Number(strategyParams.smcSlMin) : (strategyParams.smc_sl_min !== undefined ? Number(strategyParams.smc_sl_min) : (strategyParams.slMin !== undefined && strategyParams.matrixSlMin === undefined ? Number(strategyParams.slMin) : prev.slMin)),
        slMax: strategyParams.smcSlMax !== undefined ? Number(strategyParams.smcSlMax) : (strategyParams.smc_sl_max !== undefined ? Number(strategyParams.smc_sl_max) : (strategyParams.slMax !== undefined && strategyParams.matrixSlMax === undefined ? Number(strategyParams.slMax) : prev.slMax)),
        slExtraOn: strategyParams.smcSlExtraOn !== undefined ? Boolean(strategyParams.smcSlExtraOn) : (strategyParams.slExtraOn !== undefined ? Boolean(strategyParams.slExtraOn) : prev.slExtraOn),
        slExtraVal: strategyParams.smcSlExtraVal !== undefined ? Number(strategyParams.smcSlExtraVal) : (strategyParams.slExtraPips !== undefined ? Number(strategyParams.slExtraPips) : prev.slExtraVal),
        pendingCancelOn: strategyParams.smcPendingCancelOn !== undefined ? Boolean(strategyParams.smcPendingCancelOn) : (strategyParams.pendingCancelOn !== undefined ? Boolean(strategyParams.pendingCancelOn) : prev.pendingCancelOn),
        pendingCancelN: strategyParams.smcPendingCancelN !== undefined ? Number(strategyParams.smcPendingCancelN) : (strategyParams.pendingCancelN !== undefined ? Number(strategyParams.pendingCancelN) : prev.pendingCancelN),
        revEnable: strategyParams.smcRevEnable !== undefined ? Boolean(strategyParams.smcRevEnable) : (strategyParams.revEnable !== undefined ? Boolean(strategyParams.revEnable) : prev.revEnable),
        revR: strategyParams.smcRevR !== undefined ? Number(strategyParams.smcRevR) : (strategyParams.revR !== undefined ? Number(strategyParams.revR) : prev.revR),
        htfFilterEnable: strategyParams.smcHtfFilterEnable !== undefined ? Boolean(strategyParams.smcHtfFilterEnable) : (strategyParams.htfFilterEnable !== undefined ? Boolean(strategyParams.htfFilterEnable) : prev.htfFilterEnable),
        htfTimeframe: strategyParams.smcHtfTimeframe || strategyParams.htfTimeframe || prev.htfTimeframe,
        htfBufferPips: strategyParams.smcHtfBufferPips !== undefined ? Number(strategyParams.smcHtfBufferPips) : (strategyParams.htfBufferPips !== undefined ? Number(strategyParams.htfBufferPips) : prev.htfBufferPips),
        htfMaxAgeDays: strategyParams.smcHtfMaxAgeDays !== undefined ? Number(strategyParams.smcHtfMaxAgeDays) : (strategyParams.htfMaxAgeDays !== undefined ? Number(strategyParams.htfMaxAgeDays) : prev.htfMaxAgeDays),
        htfMode: strategyParams.smcHtfMode || strategyParams.htfMode || prev.htfMode,
        breakevenEnable: strategyParams.smcBreakevenEnable !== undefined ? Boolean(strategyParams.smcBreakevenEnable) : (strategyParams.breakevenEnable !== undefined ? Boolean(strategyParams.breakevenEnable) : prev.breakevenEnable),
        breakevenPips: strategyParams.smcBreakevenPips !== undefined ? Number(strategyParams.smcBreakevenPips) : (strategyParams.breakevenPips !== undefined ? Number(strategyParams.breakevenPips) : prev.breakevenPips),
        fvgFilterEnable: strategyParams.smcFvgFilterEnable !== undefined ? Boolean(strategyParams.smcFvgFilterEnable) : (strategyParams.smc_fvg_filter_enable !== undefined ? Boolean(strategyParams.smc_fvg_filter_enable) : (strategyParams.fvgFilterEnable !== undefined ? Boolean(strategyParams.fvgFilterEnable) : (strategyParams.fvg_filter_enable !== undefined ? Boolean(strategyParams.fvg_filter_enable) : prev.fvgFilterEnable))),
        smcRevertEnable: strategyParams.smcRevertEnable !== undefined ? Boolean(strategyParams.smcRevertEnable) : (strategyParams.smc_revert_enable !== undefined ? Boolean(strategyParams.smc_revert_enable) : prev.smcRevertEnable),
        smcRevertEntryMode: strategyParams.smcRevertEntryMode || strategyParams.smc_revert_entry_mode || prev.smcRevertEntryMode,
        smcRevertRr: strategyParams.smcRevertRr !== undefined ? Number(strategyParams.smcRevertRr) : (strategyParams.smc_revert_rr !== undefined ? Number(strategyParams.smc_revert_rr) : prev.smcRevertRr),
        smcRevertSlBufferPips: strategyParams.smcRevertSlBufferPips !== undefined ? Number(strategyParams.smcRevertSlBufferPips) : (strategyParams.smc_revert_sl_buffer_pips !== undefined ? Number(strategyParams.smc_revert_sl_buffer_pips) : prev.smcRevertSlBufferPips),
        smcRevertCancelBars: strategyParams.smcRevertCancelBars !== undefined ? Number(strategyParams.smcRevertCancelBars) : (strategyParams.smc_revert_cancel_bars !== undefined ? Number(strategyParams.smc_revert_cancel_bars) : prev.smcRevertCancelBars),
        smcSingleOrderPerSide: strategyParams.smcSingleOrderPerSide !== undefined ? Boolean(strategyParams.smcSingleOrderPerSide) : (strategyParams.smc_single_order_per_side !== undefined ? Boolean(strategyParams.smc_single_order_per_side) : (prev.smcSingleOrderPerSide ?? true)),
        smcMaxOrdersMode: strategyParams.smcMaxOrdersMode || strategyParams.smc_max_orders_mode || prev.smcMaxOrdersMode || "ONE_PER_SIDE",
        smcHybridThresholdPips: strategyParams.smcHybridThresholdPips !== undefined ? Number(strategyParams.smcHybridThresholdPips) : (strategyParams.smc_hybrid_threshold_pips !== undefined ? Number(strategyParams.smc_hybrid_threshold_pips) : (strategyParams.hybrid_threshold_pips !== undefined ? Number(strategyParams.hybrid_threshold_pips) : (prev.smcHybridThresholdPips ?? 80.0))),
        smcHybridPullbackPct: strategyParams.smcHybridPullbackPct !== undefined ? Number(strategyParams.smcHybridPullbackPct) : (strategyParams.smc_hybrid_pullback_pct !== undefined ? Number(strategyParams.smc_hybrid_pullback_pct) : (strategyParams.hybrid_pullback_pct !== undefined ? Number(strategyParams.hybrid_pullback_pct) : (prev.smcHybridPullbackPct ?? 50.0))),
        showMSBLines: strategyParams.showMSBLines !== undefined ? Boolean(strategyParams.showMSBLines) : prev.showMSBLines,
        showTP: strategyParams.showTP !== undefined ? Boolean(strategyParams.showTP) : prev.showTP,
        showSL: strategyParams.showSL !== undefined ? Boolean(strategyParams.showSL) : prev.showSL,
      }));

      setClassicalSmcData((prev) => ({ ...prev, ...strategyParams }));

      setHmaSystemData((prev) => ({
        ...prev,
        period: strategyParams.period !== undefined ? Number(strategyParams.period) : (strategyParams.hmaPeriod !== undefined ? Number(strategyParams.hmaPeriod) : (strategyParams.hma_period !== undefined ? Number(strategyParams.hma_period) : prev.period)),
        lookbackBars: strategyParams.lookbackBars !== undefined ? Number(strategyParams.lookbackBars) : (strategyParams.hmaLookbackBars !== undefined ? Number(strategyParams.hmaLookbackBars) : (strategyParams.hma_lookback_bars !== undefined ? Number(strategyParams.hma_lookback_bars) : prev.lookbackBars)),
        maxBarsFromExtreme: strategyParams.maxBarsFromExtreme !== undefined ? Number(strategyParams.maxBarsFromExtreme) : (strategyParams.hmaMaxBarsFromExtreme !== undefined ? Number(strategyParams.hmaMaxBarsFromExtreme) : (strategyParams.hma_max_bars_from_extreme !== undefined ? Number(strategyParams.hma_max_bars_from_extreme) : prev.maxBarsFromExtreme)),
        minBodyPct: strategyParams.minBodyPct !== undefined ? Number(strategyParams.minBodyPct) : (strategyParams.hmaMinBodyPct !== undefined ? Number(strategyParams.hmaMinBodyPct) : (strategyParams.hma_min_body_pct !== undefined ? Number(strategyParams.hma_min_body_pct) : prev.minBodyPct)),
        hmaSlopeFilter: strategyParams.hmaSlopeFilter !== undefined ? Boolean(strategyParams.hmaSlopeFilter) : (strategyParams.hma_slope_filter !== undefined ? Boolean(strategyParams.hma_slope_filter) : (strategyParams.slopeFilter !== undefined ? Boolean(strategyParams.slopeFilter) : prev.hmaSlopeFilter)),
        maxHmaSlope: strategyParams.maxHmaSlope !== undefined ? Number(strategyParams.maxHmaSlope) : (strategyParams.hmaMaxSlope !== undefined ? Number(strategyParams.hmaMaxSlope) : (strategyParams.hma_max_slope !== undefined ? Number(strategyParams.hma_max_slope) : prev.maxHmaSlope)),
        r1: strategyParams.hmaR1 !== undefined ? Number(strategyParams.hmaR1) : (strategyParams.hma_r1 !== undefined ? Number(strategyParams.hma_r1) : (strategyParams.r1 !== undefined && strategyParams.smcR1 === undefined && strategyParams.volmanR1 === undefined ? Number(strategyParams.r1) : prev.r1)),
        r2: strategyParams.hmaR2 !== undefined ? Number(strategyParams.hmaR2) : (strategyParams.hma_r2 !== undefined ? Number(strategyParams.hma_r2) : (strategyParams.r2 !== undefined && strategyParams.smcR2 === undefined ? Number(strategyParams.r2) : prev.r2)),
        slBufferMode: strategyParams.hmaSlBufferMode ?? strategyParams.slBufferMode ?? strategyParams.hma_sl_buffer_mode ?? prev.slBufferMode ?? "ATR",
        slBufferMinPips: strategyParams.hmaSlBufferMinPips !== undefined ? Number(strategyParams.hmaSlBufferMinPips) : (strategyParams.hma_sl_buffer_min_pips !== undefined ? Number(strategyParams.hma_sl_buffer_min_pips) : (strategyParams.slBufferMinPips !== undefined ? Number(strategyParams.slBufferMinPips) : (prev.slBufferMinPips ?? 5.0))),
        slBufferMaxPips: strategyParams.hmaSlBufferMaxPips !== undefined ? Number(strategyParams.hmaSlBufferMaxPips) : (strategyParams.hma_sl_buffer_max_pips !== undefined ? Number(strategyParams.hma_sl_buffer_max_pips) : (strategyParams.slBufferMaxPips !== undefined ? Number(strategyParams.slBufferMaxPips) : (prev.slBufferMaxPips ?? 30.0))),
        slAtrMult: strategyParams.hmaSlAtrMult !== undefined ? Number(strategyParams.hmaSlAtrMult) : (strategyParams.hma_sl_atr_mult !== undefined ? Number(strategyParams.hma_sl_atr_mult) : (strategyParams.slAtrMult !== undefined ? Number(strategyParams.slAtrMult) : (prev.slAtrMult ?? 1.0))),
        slBufferPips: strategyParams.hmaSlBufferPips !== undefined ? Number(strategyParams.hmaSlBufferPips) : (strategyParams.hma_sl_buffer_pips !== undefined ? Number(strategyParams.hma_sl_buffer_pips) : (strategyParams.slBufferPips !== undefined && strategyParams.volmanSlBufferPips === undefined ? Number(strategyParams.slBufferPips) : prev.slBufferPips)),
        minRiskPips: strategyParams.hmaMinRiskPips !== undefined ? Number(strategyParams.hmaMinRiskPips) : (strategyParams.hma_min_risk_pips !== undefined ? Number(strategyParams.hma_min_risk_pips) : (strategyParams.minRiskPips !== undefined && strategyParams.volmanMinRiskPips === undefined ? Number(strategyParams.minRiskPips) : prev.minRiskPips)),
        maxRiskPips: strategyParams.hmaMaxRiskPips !== undefined ? Number(strategyParams.hmaMaxRiskPips) : (strategyParams.hma_max_risk_pips !== undefined ? Number(strategyParams.hma_max_risk_pips) : (strategyParams.maxRiskPips !== undefined && strategyParams.volmanMaxRiskPips === undefined ? Number(strategyParams.maxRiskPips) : prev.maxRiskPips)),
        hmaCooldownBars: strategyParams.hmaCooldownBars !== undefined ? Number(strategyParams.hmaCooldownBars) : (strategyParams.hma_cooldown_bars !== undefined ? Number(strategyParams.hma_cooldown_bars) : (strategyParams.cooldownBars !== undefined && strategyParams.matrixCooldownBars === undefined && strategyParams.volmanCooldownBars === undefined ? Number(strategyParams.cooldownBars) : (prev.hmaCooldownBars || 5))),
        dynamicBeEnable: strategyParams.dynamicBeEnable !== undefined
          ? Boolean(strategyParams.dynamicBeEnable)
          : (strategyParams.hma_dynamic_be_enable !== undefined ? Boolean(strategyParams.hma_dynamic_be_enable) : (strategyParams.hmaDynamicBeEnable !== undefined ? Boolean(strategyParams.hmaDynamicBeEnable) : prev.dynamicBeEnable)),
        beTriggerPct: strategyParams.beTriggerPct !== undefined
          ? Number(strategyParams.beTriggerPct)
          : (strategyParams.hma_be_trigger_pct !== undefined ? Number(strategyParams.hma_be_trigger_pct) : (strategyParams.hmaBeTriggerPct !== undefined ? Number(strategyParams.hmaBeTriggerPct) : prev.beTriggerPct)),
        beLockPips: strategyParams.beLockPips !== undefined
          ? Number(strategyParams.beLockPips)
          : (strategyParams.hma_be_lock_pips !== undefined ? Number(strategyParams.hma_be_lock_pips) : (strategyParams.hmaBeLockPips !== undefined ? Number(strategyParams.hmaBeLockPips) : prev.beLockPips)),
        closeOnOpposite: strategyParams.closeOnOpposite !== undefined
          ? Boolean(strategyParams.closeOnOpposite)
          : (strategyParams.hma_close_on_opposite !== undefined ? Boolean(strategyParams.hma_close_on_opposite) : (strategyParams.close_on_opposite !== undefined ? Boolean(strategyParams.close_on_opposite) : (strategyParams.hmaCloseOnOpposite !== undefined ? Boolean(strategyParams.hmaCloseOnOpposite) : (prev.closeOnOpposite ?? true)))),
        dynamicLimitEnable: strategyParams.dynamicLimitEnable !== undefined
          ? Boolean(strategyParams.dynamicLimitEnable)
          : (strategyParams.hma_dynamic_limit_enable !== undefined ? Boolean(strategyParams.hma_dynamic_limit_enable) : (strategyParams.hmaDynamicLimitEnable !== undefined ? Boolean(strategyParams.hmaDynamicLimitEnable) : prev.dynamicLimitEnable)),
        limitThresholdPips: strategyParams.limitThresholdPips !== undefined
          ? Number(strategyParams.limitThresholdPips)
          : (strategyParams.hma_limit_threshold_pips !== undefined ? Number(strategyParams.hma_limit_threshold_pips) : (strategyParams.hmaLimitThresholdPips !== undefined ? Number(strategyParams.hmaLimitThresholdPips) : prev.limitThresholdPips)),
        limitPullbackPct: strategyParams.limitPullbackPct !== undefined
          ? Number(strategyParams.limitPullbackPct)
          : (strategyParams.hma_limit_pullback_pct !== undefined ? Number(strategyParams.hma_limit_pullback_pct) : (strategyParams.hmaLimitPullbackPct !== undefined ? Number(strategyParams.hmaLimitPullbackPct) : prev.limitPullbackPct)),
        limitCancelBars: strategyParams.limitCancelBars !== undefined
          ? Number(strategyParams.limitCancelBars)
          : (strategyParams.hma_limit_cancel_bars !== undefined ? Number(strategyParams.hma_limit_cancel_bars) : (strategyParams.hmaLimitCancelBars !== undefined ? Number(strategyParams.hmaLimitCancelBars) : prev.limitCancelBars)),
        candleStrengthFilter: strategyParams.candleStrengthFilter !== undefined
          ? Boolean(strategyParams.candleStrengthFilter)
          : (strategyParams.hma_candle_strength_filter !== undefined ? Boolean(strategyParams.hma_candle_strength_filter) : (strategyParams.hmaCandleStrengthFilter !== undefined ? Boolean(strategyParams.hmaCandleStrengthFilter) : prev.candleStrengthFilter)),
        minCandleBodyRatio: strategyParams.minCandleBodyRatio !== undefined
          ? Number(strategyParams.minCandleBodyRatio)
          : (strategyParams.hma_min_candle_body_ratio !== undefined ? Number(strategyParams.hma_min_candle_body_ratio) : (strategyParams.hmaMinCandleBodyRatio !== undefined ? Number(strategyParams.hmaMinCandleBodyRatio) : prev.minCandleBodyRatio)),
        maxRejectionWickRatio: strategyParams.maxRejectionWickRatio !== undefined
          ? Number(strategyParams.maxRejectionWickRatio)
          : (strategyParams.hma_max_rejection_wick_ratio !== undefined ? Number(strategyParams.hma_max_rejection_wick_ratio) : (strategyParams.hmaMaxRejectionWickRatio !== undefined ? Number(strategyParams.hmaMaxRejectionWickRatio) : prev.maxRejectionWickRatio)),
        allowFloatingBreakout: strategyParams.allowFloatingBreakout !== undefined
          ? Boolean(strategyParams.allowFloatingBreakout)
          : (strategyParams.hma_allow_floating_breakout !== undefined ? Boolean(strategyParams.hma_allow_floating_breakout) : (strategyParams.hmaAllowFloatingBreakout !== undefined ? Boolean(strategyParams.hmaAllowFloatingBreakout) : prev.allowFloatingBreakout)),
        hmaCurlFilter: strategyParams.hmaCurlFilter !== undefined
          ? Boolean(strategyParams.hmaCurlFilter)
          : (strategyParams.hma_curl_filter !== undefined ? Boolean(strategyParams.hma_curl_filter) : (strategyParams.hmaCurlEnable !== undefined ? Boolean(strategyParams.hmaCurlEnable) : (prev.hmaCurlFilter ?? true))),
        cascadeFilter: strategyParams.cascadeFilter !== undefined
          ? Boolean(strategyParams.cascadeFilter)
          : (strategyParams.cascade_filter !== undefined ? Boolean(strategyParams.cascade_filter) : (strategyParams.hmaCascadeFilter !== undefined ? Boolean(strategyParams.hmaCascadeFilter) : (prev.cascadeFilter ?? false))),
        bearRatioThresh: strategyParams.bearRatioThresh !== undefined
          ? Number(strategyParams.bearRatioThresh)
          : (strategyParams.bear_ratio_thresh !== undefined ? Number(strategyParams.bear_ratio_thresh) : (strategyParams.hmaBearRatioThresh !== undefined ? Number(strategyParams.hmaBearRatioThresh) : (prev.bearRatioThresh ?? 70))),
        sessionFilterEnable: strategyParams.sessionFilterEnable !== undefined
          ? Boolean(strategyParams.sessionFilterEnable)
          : (strategyParams.hma_session_filter_enable !== undefined ? Boolean(strategyParams.hma_session_filter_enable) : (strategyParams.hmaSessionFilterEnable !== undefined ? Boolean(strategyParams.hmaSessionFilterEnable) : (prev.sessionFilterEnable ?? false))),
        sessionMode: strategyParams.sessionMode || strategyParams.hma_session_mode || strategyParams.hmaSessionMode || prev.sessionMode || "ALL",
        sessionStart: strategyParams.sessionStart || strategyParams.hma_session_start || strategyParams.hmaSessionStart || prev.sessionStart || "06:00",
        sessionEnd: strategyParams.sessionEnd || strategyParams.hma_session_end || strategyParams.hmaSessionEnd || prev.sessionEnd || "19:30",
        hmaShowLine: strategyParams.hmaShowLine !== undefined
          ? Boolean(strategyParams.hmaShowLine)
          : (strategyParams.hma_show_line !== undefined ? Boolean(strategyParams.hma_show_line) : prev.hmaShowLine),
        hmaShowBaseBox: strategyParams.hmaShowBaseBox !== undefined
          ? Boolean(strategyParams.hmaShowBaseBox)
          : (strategyParams.hma_show_base_box !== undefined ? Boolean(strategyParams.hma_show_base_box) : prev.hmaShowBaseBox),
      }));

      if (strategyParams.volman) {
        setVolmanData((prev) => ({ ...prev, ...strategyParams.volman }));
      } else {
        setVolmanData((prev) => ({
          ...prev,
          volmanEmaPeriod: strategyParams.volmanEmaPeriod !== undefined ? Number(strategyParams.volmanEmaPeriod) : (strategyParams.volman_ema_period !== undefined ? Number(strategyParams.volman_ema_period) : prev.volmanEmaPeriod),
          volmanR1: strategyParams.volmanR1 !== undefined ? Number(strategyParams.volmanR1) : (strategyParams.volman_r1 !== undefined ? Number(strategyParams.volman_r1) : prev.volmanR1),
          volmanSlBufferPips: strategyParams.volmanSlBufferPips !== undefined ? Number(strategyParams.volmanSlBufferPips) : (strategyParams.volman_sl_buffer_pips !== undefined ? Number(strategyParams.volman_sl_buffer_pips) : prev.volmanSlBufferPips),
          volmanCooldownBars: strategyParams.volmanCooldownBars !== undefined ? Number(strategyParams.volmanCooldownBars) : (strategyParams.volman_cooldown_bars !== undefined ? Number(strategyParams.volman_cooldown_bars) : prev.volmanCooldownBars),
          volmanEnableBB: strategyParams.volmanEnableBB !== undefined ? Boolean(strategyParams.volmanEnableBB) : (strategyParams.volman_enable_bb !== undefined ? Boolean(strategyParams.volman_enable_bb) : prev.volmanEnableBB),
          volmanEnableTB: strategyParams.volmanEnableTB !== undefined ? Boolean(strategyParams.volmanEnableTB) : (strategyParams.volman_enable_tb !== undefined ? Boolean(strategyParams.volman_enable_tb) : prev.volmanEnableTB),
          volmanEnableSB: strategyParams.volmanEnableSB !== undefined ? Boolean(strategyParams.volmanEnableSB) : (strategyParams.volman_enable_sb !== undefined ? Boolean(strategyParams.volman_enable_sb) : prev.volmanEnableSB),
          volmanPivotLength: strategyParams.volmanPivotLength !== undefined ? Number(strategyParams.volmanPivotLength) : (strategyParams.volman_pivot_length !== undefined ? Number(strategyParams.volman_pivot_length) : prev.volmanPivotLength),
          volmanSlFilterEnable: strategyParams.volmanSlFilterEnable !== undefined ? Boolean(strategyParams.volmanSlFilterEnable) : (strategyParams.volman_sl_filter_enable !== undefined ? Boolean(strategyParams.volman_sl_filter_enable) : prev.volmanSlFilterEnable),
          volmanMinRiskPips: strategyParams.volmanMinRiskPips !== undefined ? Number(strategyParams.volmanMinRiskPips) : (strategyParams.volman_min_risk_pips !== undefined ? Number(strategyParams.volman_min_risk_pips) : prev.volmanMinRiskPips),
          volmanMaxRiskPips: strategyParams.volmanMaxRiskPips !== undefined ? Number(strategyParams.volmanMaxRiskPips) : (strategyParams.volman_max_risk_pips !== undefined ? Number(strategyParams.volman_max_risk_pips) : prev.volmanMaxRiskPips),
          volmanBreakoutCandleFilter: strategyParams.volmanBreakoutCandleFilter !== undefined ? Boolean(strategyParams.volmanBreakoutCandleFilter) : (strategyParams.volman_breakout_candle_filter !== undefined ? Boolean(strategyParams.volman_breakout_candle_filter) : prev.volmanBreakoutCandleFilter),
          volmanMinBodyRatio: strategyParams.volmanMinBodyRatio !== undefined ? Number(strategyParams.volmanMinBodyRatio) : (strategyParams.volman_min_body_ratio !== undefined ? Number(strategyParams.volman_min_body_ratio) : prev.volmanMinBodyRatio),
          volmanMinBodyBeyondPct: strategyParams.volmanMinBodyBeyondPct !== undefined ? Number(strategyParams.volmanMinBodyBeyondPct) : (strategyParams.volman_min_body_beyond_pct !== undefined ? Number(strategyParams.volman_min_body_beyond_pct) : prev.volmanMinBodyBeyondPct),
          volmanMaxRejectionWick: strategyParams.volmanMaxRejectionWick !== undefined ? Number(strategyParams.volmanMaxRejectionWick) : (strategyParams.volman_max_rejection_wick !== undefined ? Number(strategyParams.volman_max_rejection_wick) : prev.volmanMaxRejectionWick),
          volmanDynamicLimitEnable: strategyParams.volmanDynamicLimitEnable !== undefined ? Boolean(strategyParams.volmanDynamicLimitEnable) : (strategyParams.volman_dynamic_limit_enable !== undefined ? Boolean(strategyParams.volman_dynamic_limit_enable) : prev.volmanDynamicLimitEnable),
          volmanLimitThresholdPips: strategyParams.volmanLimitThresholdPips !== undefined ? Number(strategyParams.volmanLimitThresholdPips) : (strategyParams.volman_limit_threshold_pips !== undefined ? Number(strategyParams.volman_limit_threshold_pips) : prev.volmanLimitThresholdPips),
          volmanLimitPullbackPct: strategyParams.volmanLimitPullbackPct !== undefined ? Number(strategyParams.volmanLimitPullbackPct) : (strategyParams.volman_limit_pullback_pct !== undefined ? Number(strategyParams.volman_limit_pullback_pct) : prev.volmanLimitPullbackPct),
          volmanLimitCancelBars: strategyParams.volmanLimitCancelBars !== undefined ? Number(strategyParams.volmanLimitCancelBars) : (strategyParams.volman_limit_cancel_bars !== undefined ? Number(strategyParams.volman_limit_cancel_bars) : prev.volmanLimitCancelBars),
          volmanMaFilterEnable: strategyParams.volmanMaFilterEnable !== undefined ? Boolean(strategyParams.volmanMaFilterEnable) : (strategyParams.volman_ma_filter_enable !== undefined ? Boolean(strategyParams.volman_ma_filter_enable) : (strategyParams.volman_ema200_filter !== undefined ? Boolean(strategyParams.volman_ema200_filter) : prev.volmanMaFilterEnable)),
          volmanMaPeriod: strategyParams.volmanMaPeriod !== undefined ? Number(strategyParams.volmanMaPeriod) : (strategyParams.volman_ma_period !== undefined ? Number(strategyParams.volman_ma_period) : (strategyParams.volman_ema200_period !== undefined ? Number(strategyParams.volman_ema200_period) : prev.volmanMaPeriod)),
          volmanMaType: strategyParams.volmanMaType !== undefined ? String(strategyParams.volmanMaType).toUpperCase() : (strategyParams.volman_ma_type !== undefined ? String(strategyParams.volman_ma_type).toUpperCase() : prev.volmanMaType),
          volmanMaxConsecutiveEnable: strategyParams.volmanMaxConsecutiveEnable !== undefined ? Boolean(strategyParams.volmanMaxConsecutiveEnable) : (strategyParams.volman_max_consecutive_enable !== undefined ? Boolean(strategyParams.volman_max_consecutive_enable) : (prev.volmanMaxConsecutiveEnable ?? false)),
          volmanMaxConsecutiveSameDir: strategyParams.volmanMaxConsecutiveSameDir !== undefined ? Number(strategyParams.volmanMaxConsecutiveSameDir) : (strategyParams.volman_max_consecutive_same_dir !== undefined ? Number(strategyParams.volman_max_consecutive_same_dir) : (prev.volmanMaxConsecutiveSameDir ?? 3)),
        }));
      }

      setOrbSessionData((prev) => ({
        ...prev,
        orbAsiaEnable: strategyParams.orbAsiaEnable !== undefined ? Boolean(strategyParams.orbAsiaEnable) : (strategyParams.orb_asia_enable !== undefined ? Boolean(strategyParams.orb_asia_enable) : prev.orbAsiaEnable),
        orbLondonEnable: strategyParams.orbLondonEnable !== undefined ? Boolean(strategyParams.orbLondonEnable) : (strategyParams.orb_london_enable !== undefined ? Boolean(strategyParams.orb_london_enable) : prev.orbLondonEnable),
        orbLondonEndHour: strategyParams.orbLondonEndHour !== undefined ? Number(strategyParams.orbLondonEndHour) : (strategyParams.orb_london_end_hour !== undefined ? Number(strategyParams.orb_london_end_hour) : (prev.orbLondonEndHour ?? 19)),
        orbNyEnable: strategyParams.orbNyEnable !== undefined ? Boolean(strategyParams.orbNyEnable) : (strategyParams.orb_ny_enable !== undefined ? Boolean(strategyParams.orb_ny_enable) : prev.orbNyEnable),
        orbEntryMode: strategyParams.orbEntryMode || strategyParams.orb_entry_mode || prev.orbEntryMode,
        orbRr: strategyParams.orbRr !== undefined ? Number(strategyParams.orbRr) : (strategyParams.orb_rr !== undefined ? Number(strategyParams.orb_rr) : prev.orbRr),
        orbSlBufferMode: strategyParams.orbSlBufferMode || strategyParams.orb_sl_buffer_mode || prev.orbSlBufferMode || "FIXED",
        orbSlBufferPips: strategyParams.orbSlBufferPips !== undefined ? Number(strategyParams.orbSlBufferPips) : (strategyParams.orb_sl_buffer_pips !== undefined ? Number(strategyParams.orb_sl_buffer_pips) : prev.orbSlBufferPips),
        orbSlAtrMult: strategyParams.orbSlAtrMult !== undefined ? Number(strategyParams.orbSlAtrMult) : (strategyParams.orb_sl_atr_mult !== undefined ? Number(strategyParams.orb_sl_atr_mult) : (prev.orbSlAtrMult ?? 1.0)),
        orbSlBufferMinPips: strategyParams.orbSlBufferMinPips !== undefined ? Number(strategyParams.orbSlBufferMinPips) : (strategyParams.orb_sl_buffer_min_pips !== undefined ? Number(strategyParams.orb_sl_buffer_min_pips) : (prev.orbSlBufferMinPips ?? 2.0)),
        orbSlBufferMaxPips: strategyParams.orbSlBufferMaxPips !== undefined ? Number(strategyParams.orbSlBufferMaxPips) : (strategyParams.orb_sl_buffer_max_pips !== undefined ? Number(strategyParams.orb_sl_buffer_max_pips) : (prev.orbSlBufferMaxPips ?? 15.0)),
        orbSlFilterEnable: strategyParams.orbSlFilterEnable !== undefined ? Boolean(strategyParams.orbSlFilterEnable) : (strategyParams.orb_sl_filter_enable !== undefined ? Boolean(strategyParams.orb_sl_filter_enable) : (prev.orbSlFilterEnable ?? false)),
        orbMinSlPips: strategyParams.orbMinSlPips !== undefined ? Number(strategyParams.orbMinSlPips) : (strategyParams.orb_min_sl_pips !== undefined ? Number(strategyParams.orb_min_sl_pips) : (prev.orbMinSlPips ?? 3.0)),
        orbMaxSlPips: strategyParams.orbMaxSlPips !== undefined ? Number(strategyParams.orbMaxSlPips) : (strategyParams.orb_max_sl_pips !== undefined ? Number(strategyParams.orb_max_sl_pips) : (prev.orbMaxSlPips ?? 150.0)),
        orbMaxBreakPips: strategyParams.orbMaxBreakPips !== undefined ? Number(strategyParams.orbMaxBreakPips) : (strategyParams.orb_max_break_pips !== undefined ? Number(strategyParams.orb_max_break_pips) : prev.orbMaxBreakPips),
        orbMaxLimitBars: strategyParams.orbMaxLimitBars !== undefined ? Number(strategyParams.orbMaxLimitBars) : (strategyParams.orb_max_limit_bars !== undefined ? Number(strategyParams.orb_max_limit_bars) : (prev.orbMaxLimitBars ?? 6)),
        orbCancelOnTp: strategyParams.orbCancelOnTp !== undefined ? Boolean(strategyParams.orbCancelOnTp) : (strategyParams.orb_cancel_on_tp !== undefined ? Boolean(strategyParams.orb_cancel_on_tp) : (prev.orbCancelOnTp ?? true)),
        orbCancelOnSl: strategyParams.orbCancelOnSl !== undefined ? Boolean(strategyParams.orbCancelOnSl) : (strategyParams.orb_cancel_on_sl !== undefined ? Boolean(strategyParams.orb_cancel_on_sl) : (prev.orbCancelOnSl ?? true)),
        orbEma200Filter: strategyParams.orbEma200Filter !== undefined ? Boolean(strategyParams.orbEma200Filter) : (strategyParams.orb_ema200_filter !== undefined ? Boolean(strategyParams.orb_ema200_filter) : prev.orbEma200Filter),
        orbEma200Mode: strategyParams.orbEma200Mode || strategyParams.orb_ema200_mode || (strategyParams.orbEma200Filter || strategyParams.orb_ema200_filter ? "STRICT" : (prev.orbEma200Mode || "OFF")),
        orbMaxZonePips: strategyParams.orbMaxZonePips !== undefined ? Number(strategyParams.orbMaxZonePips) : (strategyParams.orb_max_zone_pips !== undefined ? Number(strategyParams.orb_max_zone_pips) : prev.orbMaxZonePips),
        orbMinZonePips: strategyParams.orbMinZonePips !== undefined ? Number(strategyParams.orbMinZonePips) : (strategyParams.orb_min_zone_pips !== undefined ? Number(strategyParams.orb_min_zone_pips) : prev.orbMinZonePips),
        orbDynamicBeEnable: strategyParams.orbDynamicBeEnable !== undefined ? Boolean(strategyParams.orbDynamicBeEnable) : (strategyParams.orb_dynamic_be_enable !== undefined ? Boolean(strategyParams.orb_dynamic_be_enable) : (prev.orbDynamicBeEnable ?? false)),
        orbBeTriggerPct: strategyParams.orbBeTriggerPct !== undefined ? Number(strategyParams.orbBeTriggerPct) : (strategyParams.orb_be_trigger_pct !== undefined ? Number(strategyParams.orb_be_trigger_pct) : (prev.orbBeTriggerPct ?? 75)),
        orbBeLockPips: strategyParams.orbBeLockPips !== undefined ? Number(strategyParams.orbBeLockPips) : (strategyParams.orb_be_lock_pips !== undefined ? Number(strategyParams.orb_be_lock_pips) : (prev.orbBeLockPips ?? 5.0)),
        orbTimeoutExitEnable: strategyParams.orbTimeoutExitEnable !== undefined ? Boolean(strategyParams.orbTimeoutExitEnable) : (strategyParams.orb_timeout_exit_enable !== undefined ? Boolean(strategyParams.orb_timeout_exit_enable) : (prev.orbTimeoutExitEnable ?? true)),
        orbRevertEnable: strategyParams.orbRevertEnable !== undefined ? Boolean(strategyParams.orbRevertEnable) : (strategyParams.orb_revert_enable !== undefined ? Boolean(strategyParams.orb_revert_enable) : (prev.orbRevertEnable ?? false)),
        orbRevertEntryMode: strategyParams.orbRevertEntryMode || strategyParams.orb_revert_entry_mode || prev.orbRevertEntryMode || "LIMIT_50_BOX",
        orbRevertRr: strategyParams.orbRevertRr !== undefined ? Number(strategyParams.orbRevertRr) : (strategyParams.orb_revert_rr !== undefined ? Number(strategyParams.orb_revert_rr) : (prev.orbRevertRr ?? 1.5)),
        orbRevertCancelBars: strategyParams.orbRevertCancelBars !== undefined ? Number(strategyParams.orbRevertCancelBars) : (strategyParams.orb_revert_cancel_bars !== undefined ? Number(strategyParams.orb_revert_cancel_bars) : (prev.orbRevertCancelBars ?? 6)),
        orbRevertSlBufferPips: strategyParams.orbRevertSlBufferPips !== undefined ? Number(strategyParams.orbRevertSlBufferPips) : (strategyParams.orb_revert_sl_buffer_pips !== undefined ? Number(strategyParams.orb_revert_sl_buffer_pips) : (prev.orbRevertSlBufferPips ?? 2.0)),
        orbDynamicLimitEnable: strategyParams.orbDynamicLimitEnable !== undefined ? Boolean(strategyParams.orbDynamicLimitEnable) : (strategyParams.orb_dynamic_limit_enable !== undefined ? Boolean(strategyParams.orb_dynamic_limit_enable) : Boolean(prev.orbDynamicLimitEnable)),
        orbLimitThresholdPips: strategyParams.orbLimitThresholdPips !== undefined ? Number(strategyParams.orbLimitThresholdPips) : (strategyParams.orb_limit_threshold_pips !== undefined ? Number(strategyParams.orb_limit_threshold_pips) : (prev.orbLimitThresholdPips ?? 100.0)),
        orbLimitPullbackPct: strategyParams.orbLimitPullbackPct !== undefined ? Number(strategyParams.orbLimitPullbackPct) : (strategyParams.orb_limit_pullback_pct !== undefined ? Number(strategyParams.orb_limit_pullback_pct) : (prev.orbLimitPullbackPct ?? 50.0)),
        orbLimitCancelBars: strategyParams.orbLimitCancelBars !== undefined ? Number(strategyParams.orbLimitCancelBars) : (strategyParams.orb_limit_cancel_bars !== undefined ? Number(strategyParams.orb_limit_cancel_bars) : (prev.orbLimitCancelBars ?? 10)),
      }));

      setScalpingProData((prev) => ({
        ...prev,
        scalpLookback: strategyParams.scalpLookback !== undefined ? Number(strategyParams.scalpLookback) : (strategyParams.scalp_lookback !== undefined ? Number(strategyParams.scalp_lookback) : prev.scalpLookback),
        scalpOpt: strategyParams.scalpOpt || strategyParams.scalp_opt || prev.scalpOpt || "Only Outbreaks & Retest",
        scalpExtend: strategyParams.scalpExtend !== undefined ? Boolean(strategyParams.scalpExtend) : (strategyParams.scalp_extend !== undefined ? Boolean(strategyParams.scalp_extend) : prev.scalpExtend),
        scalpMaxBars: strategyParams.scalpMaxBars !== undefined ? Number(strategyParams.scalpMaxBars) : (strategyParams.scalp_max_bars !== undefined ? Number(strategyParams.scalp_max_bars) : prev.scalpMaxBars),
        scalpEntryMode: strategyParams.scalpEntryMode || strategyParams.scalp_entry_mode || prev.scalpEntryMode || "SMART_HYBRID",
        scalpHybridThresholdPips: strategyParams.scalpHybridThresholdPips !== undefined ? Number(strategyParams.scalpHybridThresholdPips) : (strategyParams.scalp_hybrid_threshold_pips !== undefined ? Number(strategyParams.scalp_hybrid_threshold_pips) : (prev.scalpHybridThresholdPips ?? 80.0)),
        scalpHybridLimitMode: strategyParams.scalpHybridLimitMode || strategyParams.scalp_hybrid_limit_mode || prev.scalpHybridLimitMode || "RETEST",
        scalpHybridZonePct: strategyParams.scalpHybridZonePct !== undefined ? Number(strategyParams.scalpHybridZonePct) : (strategyParams.scalp_hybrid_zone_pct !== undefined ? Number(strategyParams.scalp_hybrid_zone_pct) : (prev.scalpHybridZonePct ?? 50.0)),
        scalpHybridPullbackPct: strategyParams.scalpHybridPullbackPct !== undefined ? Number(strategyParams.scalpHybridPullbackPct) : (strategyParams.scalp_hybrid_pullback_pct !== undefined ? Number(strategyParams.scalp_hybrid_pullback_pct) : (prev.scalpHybridPullbackPct ?? 50.0)),
        scalpRr: strategyParams.scalpRr !== undefined ? Number(strategyParams.scalpRr) : (strategyParams.scalp_rr !== undefined ? Number(strategyParams.scalp_rr) : prev.scalpRr),
        scalpSlBufferPips: strategyParams.scalpSlBufferPips !== undefined ? Number(strategyParams.scalpSlBufferPips) : (strategyParams.scalp_sl_buffer_pips !== undefined ? Number(strategyParams.scalp_sl_buffer_pips) : prev.scalpSlBufferPips),
        scalpMinRiskPips: strategyParams.scalpMinRiskPips !== undefined ? Number(strategyParams.scalpMinRiskPips) : (strategyParams.scalp_min_risk_pips !== undefined ? Number(strategyParams.scalp_min_risk_pips) : prev.scalpMinRiskPips),
        scalpMaxRiskPips: strategyParams.scalpMaxRiskPips !== undefined ? Number(strategyParams.scalpMaxRiskPips) : (strategyParams.scalp_max_risk_pips !== undefined ? Number(strategyParams.scalp_max_risk_pips) : prev.scalpMaxRiskPips),
        scalpCooldownBars: strategyParams.scalpCooldownBars !== undefined ? Number(strategyParams.scalpCooldownBars) : (strategyParams.scalp_cooldown_bars !== undefined ? Number(strategyParams.scalp_cooldown_bars) : prev.scalpCooldownBars),
        scalpHighestPeakFilter: strategyParams.scalpHighestPeakFilter !== undefined ? Boolean(strategyParams.scalpHighestPeakFilter) : (strategyParams.scalp_highest_peak_filter !== undefined ? Boolean(strategyParams.scalp_highest_peak_filter) : prev.scalpHighestPeakFilter),
        scalpPeakLookback: strategyParams.scalpPeakLookback !== undefined ? Number(strategyParams.scalpPeakLookback) : (strategyParams.scalp_peak_lookback !== undefined ? Number(strategyParams.scalp_peak_lookback) : (prev.scalpPeakLookback ?? 30)),
        scalpLimitCancelBars: strategyParams.scalpLimitCancelBars !== undefined ? Number(strategyParams.scalpLimitCancelBars) : (strategyParams.scalp_limit_cancel_bars !== undefined ? Number(strategyParams.scalp_limit_cancel_bars) : prev.scalpLimitCancelBars),
        scalpMaxWaitBars: strategyParams.scalpMaxWaitBars !== undefined ? Number(strategyParams.scalpMaxWaitBars) : (strategyParams.scalp_max_wait_bars !== undefined ? Number(strategyParams.scalp_max_wait_bars) : (prev.scalpMaxWaitBars ?? 20)),
        scalpCloseOnOpposite: strategyParams.scalpCloseOnOpposite !== undefined ? Boolean(strategyParams.scalpCloseOnOpposite) : (strategyParams.scalp_close_on_opposite !== undefined ? Boolean(strategyParams.scalp_close_on_opposite) : (prev.scalpCloseOnOpposite ?? false)),
        scalpDynamicBeEnable: strategyParams.scalpDynamicBeEnable !== undefined ? Boolean(strategyParams.scalpDynamicBeEnable) : (strategyParams.scalp_dynamic_be_enable !== undefined ? Boolean(strategyParams.scalp_dynamic_be_enable) : (prev.scalpDynamicBeEnable ?? false)),
        scalpBeTriggerMode: strategyParams.scalpBeTriggerMode || strategyParams.scalp_be_trigger_mode || prev.scalpBeTriggerMode || "PCT_TP",
        scalpBeTriggerPct: strategyParams.scalpBeTriggerPct !== undefined ? Number(strategyParams.scalpBeTriggerPct) : (strategyParams.scalp_be_trigger_pct !== undefined ? Number(strategyParams.scalp_be_trigger_pct) : (prev.scalpBeTriggerPct ?? 50.0)),
        scalpBeTriggerPips: strategyParams.scalpBeTriggerPips !== undefined ? Number(strategyParams.scalpBeTriggerPips) : (strategyParams.scalp_be_trigger_pips !== undefined ? Number(strategyParams.scalp_be_trigger_pips) : (prev.scalpBeTriggerPips ?? 40.0)),
        scalpBeLockPips: strategyParams.scalpBeLockPips !== undefined ? Number(strategyParams.scalpBeLockPips) : (strategyParams.scalp_be_lock_pips !== undefined ? Number(strategyParams.scalp_be_lock_pips) : (prev.scalpBeLockPips ?? 2.0)),
      }));

      const adminCfg = getAdminConfig();
      setLiquiditySweepData((prev) => ({
        ...prev,
        sweepM1AnchorM5: strategyParams.sweepM1AnchorM5 !== undefined ? Boolean(strategyParams.sweepM1AnchorM5) : (strategyParams.sweep_m1_anchor_m5 !== undefined ? Boolean(strategyParams.sweep_m1_anchor_m5) : (adminCfg.sweep_m1_anchor_m5 !== undefined ? Boolean(adminCfg.sweep_m1_anchor_m5) : (prev.sweepM1AnchorM5 ?? true))),
        sweepLookback: strategyParams.sweepLookback !== undefined ? Number(strategyParams.sweepLookback) : (strategyParams.sweep_lookback !== undefined ? Number(strategyParams.sweep_lookback) : (strategyParams.len !== undefined ? Number(strategyParams.len) : (adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : (prev.sweepLookback ?? 5)))),
        sweepOpt: strategyParams.sweepOpt || strategyParams.sweep_opt || strategyParams.opt || adminCfg.sweep_opt || prev.sweepOpt || "Only Outbreaks & Retest",
        sweepExtend: strategyParams.sweepExtend !== undefined ? Boolean(strategyParams.sweepExtend) : (strategyParams.sweep_extend !== undefined ? Boolean(strategyParams.sweep_extend) : (strategyParams.extend !== undefined ? Boolean(strategyParams.extend) : (adminCfg.sweep_extend !== undefined ? Boolean(adminCfg.sweep_extend) : (prev.sweepExtend ?? true)))),
        sweepMaxBars: strategyParams.sweepMaxBars !== undefined ? Number(strategyParams.sweepMaxBars) : (strategyParams.sweep_max_bars !== undefined ? Number(strategyParams.sweep_max_bars) : (strategyParams.maxB !== undefined ? Number(strategyParams.maxB) : (adminCfg.sweep_max_bars !== undefined ? Number(adminCfg.sweep_max_bars) : (prev.sweepMaxBars ?? 300)))),
        sweepEntryMode: strategyParams.sweepEntryMode || strategyParams.sweep_entry_mode || adminCfg.sweep_entry_mode || prev.sweepEntryMode || "SMART_HYBRID",
        sweepRr: strategyParams.sweepRr !== undefined ? Number(strategyParams.sweepRr) : (strategyParams.sweep_rr !== undefined ? Number(strategyParams.sweep_rr) : (adminCfg.sweep_rr !== undefined ? Number(adminCfg.sweep_rr) : (prev.sweepRr ?? 2.0))),
        sweepSlBufferPips: strategyParams.sweepSlBufferPips !== undefined ? Number(strategyParams.sweepSlBufferPips) : (strategyParams.sweep_sl_buffer_pips !== undefined ? Number(strategyParams.sweep_sl_buffer_pips) : (adminCfg.sweep_sl_buffer_pips !== undefined ? Number(adminCfg.sweep_sl_buffer_pips) : (prev.sweepSlBufferPips ?? 1.5))),
        sweepMinRiskPips: strategyParams.sweepMinRiskPips !== undefined ? Number(strategyParams.sweepMinRiskPips) : (strategyParams.sweep_min_risk_pips !== undefined ? Number(strategyParams.sweep_min_risk_pips) : (adminCfg.sweep_min_risk_pips !== undefined ? Number(adminCfg.sweep_min_risk_pips) : (prev.sweepMinRiskPips ?? 3.0))),
        sweepMaxRiskPips: strategyParams.sweepMaxRiskPips !== undefined ? Number(strategyParams.sweepMaxRiskPips) : (strategyParams.sweep_max_risk_pips !== undefined ? Number(strategyParams.sweep_max_risk_pips) : (adminCfg.sweep_max_risk_pips !== undefined ? Number(adminCfg.sweep_max_risk_pips) : (prev.sweepMaxRiskPips ?? 150.0))),
        sweepCooldownBars: strategyParams.sweepCooldownBars !== undefined ? Number(strategyParams.sweepCooldownBars) : (strategyParams.sweep_cooldown_bars !== undefined ? Number(strategyParams.sweep_cooldown_bars) : (adminCfg.sweep_cooldown_bars !== undefined ? Number(adminCfg.sweep_cooldown_bars) : (prev.sweepCooldownBars ?? 3))),
        sweepMinSwingPips: strategyParams.sweepMinSwingPips !== undefined ? Number(strategyParams.sweepMinSwingPips) : (strategyParams.sweep_min_swing_pips !== undefined ? Number(strategyParams.sweep_min_swing_pips) : (adminCfg.sweep_min_swing_pips !== undefined ? Number(adminCfg.sweep_min_swing_pips) : (prev.sweepMinSwingPips ?? 18.0))),
        sweepCompareBars: strategyParams.sweepCompareBars !== undefined ? Number(strategyParams.sweepCompareBars) : (strategyParams.sweep_compare_bars !== undefined ? Number(strategyParams.sweep_compare_bars) : (adminCfg.sweep_compare_bars !== undefined ? Number(adminCfg.sweep_compare_bars) : (prev.sweepCompareBars ?? 25))),
        sweepHighestPeakFilter: strategyParams.sweepHighestPeakFilter !== undefined ? Boolean(strategyParams.sweepHighestPeakFilter) : (strategyParams.sweep_highest_peak_filter !== undefined ? Boolean(strategyParams.sweep_highest_peak_filter) : (adminCfg.sweep_highest_peak_filter !== undefined ? Boolean(adminCfg.sweep_highest_peak_filter) : (prev.sweepHighestPeakFilter ?? false))),
        sweepPeakLookback: strategyParams.sweepPeakLookback !== undefined ? Number(strategyParams.sweepPeakLookback) : (strategyParams.sweep_peak_lookback !== undefined ? Number(strategyParams.sweep_peak_lookback) : (adminCfg.sweep_peak_lookback !== undefined ? Number(adminCfg.sweep_peak_lookback) : (prev.sweepPeakLookback ?? 30))),
        sweepTrendFilterEnable: strategyParams.sweepTrendFilterEnable !== undefined ? Boolean(strategyParams.sweepTrendFilterEnable) : (strategyParams.sweep_trend_filter_enable !== undefined ? Boolean(strategyParams.sweep_trend_filter_enable) : (adminCfg.sweep_trend_filter_enable !== undefined ? Boolean(adminCfg.sweep_trend_filter_enable) : (prev.sweepTrendFilterEnable ?? false))),
        sweepTrendEmaPeriod: strategyParams.sweepTrendEmaPeriod !== undefined ? Number(strategyParams.sweepTrendEmaPeriod) : (strategyParams.sweep_trend_ema_period !== undefined ? Number(strategyParams.sweep_trend_ema_period) : (adminCfg.sweep_trend_ema_period !== undefined ? Number(adminCfg.sweep_trend_ema_period) : (prev.sweepTrendEmaPeriod ?? 200))),
        sweepSessionFilterEnable: strategyParams.sweepSessionFilterEnable !== undefined ? Boolean(strategyParams.sweepSessionFilterEnable) : (strategyParams.sweep_session_filter_enable !== undefined ? Boolean(strategyParams.sweep_session_filter_enable) : (adminCfg.sweep_session_filter_enable !== undefined ? Boolean(adminCfg.sweep_session_filter_enable) : (prev.sweepSessionFilterEnable ?? false))),
        sweepSessionStart: strategyParams.sweepSessionStart || strategyParams.sweep_session_start || adminCfg.sweep_session_start || prev.sweepSessionStart || "00:00",
        sweepSessionEnd: strategyParams.sweepSessionEnd || strategyParams.sweep_session_end || adminCfg.sweep_session_end || prev.sweepSessionEnd || "23:59",
        sweepLimitCancelBars: strategyParams.sweepLimitCancelBars !== undefined ? Number(strategyParams.sweepLimitCancelBars) : (strategyParams.sweep_limit_cancel_bars !== undefined ? Number(strategyParams.sweep_limit_cancel_bars) : (strategyParams.cancel_bars !== undefined ? Number(strategyParams.cancel_bars) : (adminCfg.sweep_limit_cancel_bars !== undefined ? Number(adminCfg.sweep_limit_cancel_bars) : (prev.sweepLimitCancelBars ?? 30)))),
        sweepMaxWaitBars: strategyParams.sweepMaxWaitBars !== undefined ? Number(strategyParams.sweepMaxWaitBars) : (strategyParams.sweep_max_wait_bars !== undefined ? Number(strategyParams.sweep_max_wait_bars) : (adminCfg.sweep_max_wait_bars !== undefined ? Number(adminCfg.sweep_max_wait_bars) : (prev.sweepMaxWaitBars ?? 5))),
        sweepCloseOnOpposite: strategyParams.sweepCloseOnOpposite !== undefined ? Boolean(strategyParams.sweepCloseOnOpposite) : (strategyParams.sweep_close_on_opposite !== undefined ? Boolean(strategyParams.sweep_close_on_opposite) : (adminCfg.sweep_close_on_opposite !== undefined ? Boolean(adminCfg.sweep_close_on_opposite) : (prev.sweepCloseOnOpposite ?? false))),
        sweepDynamicBeEnable: strategyParams.sweepDynamicBeEnable !== undefined ? Boolean(strategyParams.sweepDynamicBeEnable) : (strategyParams.sweep_dynamic_be_enable !== undefined ? Boolean(strategyParams.sweep_dynamic_be_enable) : (adminCfg.sweep_dynamic_be_enable !== undefined ? Boolean(adminCfg.sweep_dynamic_be_enable) : (prev.sweepDynamicBeEnable ?? false))),
        sweepBeTriggerMode: strategyParams.sweepBeTriggerMode || strategyParams.sweep_be_trigger_mode || adminCfg.sweep_be_trigger_mode || prev.sweepBeTriggerMode || "PCT_TP",
        sweepBeTriggerPct: strategyParams.sweepBeTriggerPct !== undefined ? Number(strategyParams.sweepBeTriggerPct) : (strategyParams.sweep_be_trigger_pct !== undefined ? Number(strategyParams.sweep_be_trigger_pct) : (adminCfg.sweep_be_trigger_pct !== undefined ? Number(adminCfg.sweep_be_trigger_pct) : (prev.sweepBeTriggerPct ?? 50.0))),
        sweepBeTriggerPips: strategyParams.sweepBeTriggerPips !== undefined ? Number(strategyParams.sweepBeTriggerPips) : (strategyParams.sweep_be_trigger_pips !== undefined ? Number(strategyParams.sweep_be_trigger_pips) : (adminCfg.sweep_be_trigger_pips !== undefined ? Number(adminCfg.sweep_be_trigger_pips) : (prev.sweepBeTriggerPips ?? 40.0))),
        sweepBeLockPips: strategyParams.sweepBeLockPips !== undefined ? Number(strategyParams.sweepBeLockPips) : (strategyParams.sweep_be_lock_pips !== undefined ? Number(strategyParams.sweep_be_lock_pips) : (adminCfg.sweep_be_lock_pips !== undefined ? Number(adminCfg.sweep_be_lock_pips) : (prev.sweepBeLockPips ?? 2.0))),
        sweepHybridThresholdPips: strategyParams.sweepHybridThresholdPips !== undefined ? Number(strategyParams.sweepHybridThresholdPips) : (strategyParams.sweep_hybrid_threshold_pips !== undefined ? Number(strategyParams.sweep_hybrid_threshold_pips) : (adminCfg.sweep_hybrid_threshold_pips !== undefined ? Number(adminCfg.sweep_hybrid_threshold_pips) : (prev.sweepHybridThresholdPips ?? 150.0))),
        sweepHybridLimitMode: strategyParams.sweepHybridLimitMode || strategyParams.sweep_hybrid_limit_mode || adminCfg.sweep_hybrid_limit_mode || prev.sweepHybridLimitMode || "ZONE_50",
        sweepHybridZonePct: strategyParams.sweepHybridZonePct !== undefined ? Number(strategyParams.sweepHybridZonePct) : (strategyParams.sweep_hybrid_zone_pct !== undefined ? Number(strategyParams.sweep_hybrid_zone_pct) : (adminCfg.sweep_hybrid_zone_pct !== undefined ? Number(adminCfg.sweep_hybrid_zone_pct) : (prev.sweepHybridZonePct ?? 50.0))),
        sweepHybridPullbackPct: strategyParams.sweepHybridPullbackPct !== undefined ? Number(strategyParams.sweepHybridPullbackPct) : (strategyParams.sweep_hybrid_pullback_pct !== undefined ? Number(strategyParams.sweep_hybrid_pullback_pct) : (adminCfg.sweep_hybrid_pullback_pct !== undefined ? Number(adminCfg.sweep_hybrid_pullback_pct) : (prev.sweepHybridPullbackPct ?? 50.0))),
      }));
    }
  }, [strategyParams]);

  useEffect(() => {
    if (indicatorStyles) {
      if (indicatorStyles.ichimoku) setIchimokuStyle((prev) => ({ ...prev, ...indicatorStyles.ichimoku }));
      if (indicatorStyles.multiEma) setMultiEmaStyle((prev) => ({ ...prev, ...indicatorStyles.multiEma }));
      if (indicatorStyles.bb) setBbStyle((prev) => ({ ...prev, ...indicatorStyles.bb }));
      if (indicatorStyles.smc) setSmcStyle((prev) => ({ ...prev, ...indicatorStyles.smc }));
      if (indicatorStyles.classicalSmc) setClassicalSmcStyle((prev) => ({ ...prev, ...indicatorStyles.classicalSmc }));
      if (indicatorStyles.hma) setHmaStyle((prev) => ({ ...prev, ...indicatorStyles.hma }));
      if (indicatorStyles.hmaSystem) setHmaSystemStyle((prev) => ({ ...prev, ...indicatorStyles.hmaSystem }));
      if (indicatorStyles.volman) setVolmanStyle((prev) => ({ ...prev, ...indicatorStyles.volman }));
      if (indicatorStyles.orbSession) setOrbSessionStyle((prev) => ({ ...prev, ...indicatorStyles.orbSession }));
      if (indicatorStyles.liquiditySweep) setLiquiditySweepStyle((prev) => ({ ...prev, ...indicatorStyles.liquiditySweep }));
    }
  }, [indicatorStyles]);


  if (!isOpen) return null;

  if (indicatorId === "orb_session_system" && !isAdmin) {
    onClose();
    return null;
  }

  const currentDef = AVAILABLE_INDICATORS.find((i) => i.id === indicatorId) || {
    name: "Cài Đặt Chỉ Báo",
  };

  if (currentDef?.isSystem && !isBasic) {
    promptTierGate(currentDef.name, "BASIC");
    onClose();
    return null;
  }

  const handleResetDefaults = () => {
    if (indicatorId === "matrix_pro") {
      setMatrixData({
        emaFast: 9,
        emaSlow: 21,
        rsiPeriod: 14,
        tpMult: 1.8,
        slMult: 1.2,
        minConfidence: 35,
        slFilterEnable: true,
        slMin: 10.0,
        slMax: 100.0,
        cooldownBars: 12,
        timeoutExitEnable: false,
        maxHoldingBars: 30,
        sessionFilterEnable: false,
        sessionStart: "06:00",
        sessionEnd: "03:30",
        ema200FilterEnable: true,
        ema200DistFilterEnable: false,
        ema200OuterPips: 200.0,
        ema200InnerPips: 100.0,
        closeOnly: true,
        showEMA: false,
        showTPSL: true,
        showForecast: true,
        showRSI: false,
      });
      setActivePreset("daytrading");
    } else if (indicatorId === "smc_structure") {
      setClassicalSmcData({
        pivotLength: 5,
        obLookback: 20,
        mitigationMethod: "close",
        showOB: true,
        hideMitigatedOB: false,
        showFVG: true,
        hideMitigatedFVG: false,
        showBOS: true,
        showCHoCH: true,
      });
      setClassicalSmcStyle({
        bullishOBColor: "#10b981",
        bearishOBColor: "#ef4444",
        bullishFVGColor: "#06b6d4",
        bearishFVGColor: "#f43f5e",
        bosColor: "#38bdf8",
        chochColor: "#f59e0b",
        showLabels: true,
      });
    } else if (indicatorId === "msb_dynamic") {
      setSmcData({
        windowBars: 100,
        lenPH: 3,
        lenPL: 3,
        minGapBars: 2,
        r1: 1.5,
        r2: 2.0,
        entryMode: "Smart Hybrid",
        fibPct: 35.0,
        halfBreakBasis: "Range(H-L)",
        slFilterEnable: true,
        slMin: 10.0,
        slMax: 150.0,
        slExtraOn: true,
        slExtraVal: 5.0,
        pendingCancelOn: true,
        pendingCancelN: 40,
        revEnable: true,
        revR: 2.0,
        htfFilterEnable: false,
        htfTimeframe: "AUTO",
        htfBufferPips: 5.0,
        htfMaxAgeDays: 7,
        htfMode: "touch",
        breakevenEnable: false,
        breakevenPips: 100.0,
        fvgFilterEnable: false,
        smcRevertEnable: true,
        smcRevertEntryMode: "LIMIT_MID",
        smcRevertRr: 1.5,
        smcRevertSlBufferPips: 10.0,
        smcRevertCancelBars: 20,
        smcSingleOrderPerSide: true,
        smcMaxOrdersMode: "ONE_PER_SIDE",
        smcHybridThresholdPips: 80.0,
        smcHybridPullbackPct: 50.0,
        showMSBLines: true,
        showTP: true,
        showSL: true,
      });
      setSmcStyle({
        anchor: { visible: false, color: "#ef4444", width: 2, style: 0 },
        mid: { visible: false, color: "#38bdf8", width: 1, style: 0 },
        same: { visible: false, color: "#c084fc", width: 1, style: 0 },
        entry: { visible: true, color: "#facc15", width: 1, style: 2 },
        tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
        tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
        sl: { visible: true, color: "#f97316", width: 1, style: 0 },
        showLabels: true,
      });
      setActivePreset("m1_scalp_sniper");
    } else if (indicatorId === "ichimoku_cloud") {
      setIchimokuData({ tenkan: 9, kijun: 26, senkouB: 52, displacement: 26 });
      setIchimokuStyle({
        tenkan: { visible: true, color: "#06b6d4", width: 1, style: 0 },
        kijun: { visible: true, color: "#f59e0b", width: 2, style: 0 },
        chikou: { visible: true, color: "#10b981", width: 1, style: 1 },
        spanA: { visible: true, color: "#10b981", width: 1, style: 0 },
        spanB: { visible: true, color: "#ef4444", width: 1, style: 0 },
        cloudFill: true,
        cloudBullColor: "#10b981",
        cloudBearColor: "#ef4444",
      });
    } else if (indicatorId === "multi_ema_ribbon") {
      setMultiEmaData({ ema1: 34, ema2: 89, ema3: 200, ema4: 610 });
      setMultiEmaStyle({
        ema1: { visible: true, color: "#38bdf8", width: 1, style: 0 },
        ema2: { visible: true, color: "#facc15", width: 2, style: 0 },
        ema3: { visible: true, color: "#c084fc", width: 2, style: 0 },
        ema4: { visible: true, color: "#fb7185", width: 2, style: 0 },
      });
    } else if (indicatorId === "bollinger_bands") {
      setBbData({ period: 20, stdDev: 2.0, source: "close" });
      setBbStyle({
        upper: { visible: true, color: "#38bdf8", width: 1, style: 2 },
        lower: { visible: true, color: "#38bdf8", width: 1, style: 2 },
      });
    } else if (indicatorId === "macd_oscillator") {
      setMacdData({ fast: 12, slow: 26, signal: 9 });
    } else if (indicatorId === "hma_indicator") {
      setHmaData({ period: 21 });
      setHmaStyle({ visible: true, bullColor: "#10b981", bearColor: "#ef4444", width: 2, style: 0 });
    } else if (indicatorId === "hma_breakout_system") {
      setHmaSystemData({
        period: 21,
        lookbackBars: 100,
        hmaSlopeFilter: true,
        maxHmaSlope: 3.0,
        minBodyPct: 40,
        maxBarsFromExtreme: 35,
        allowFloatingBreakout: false,
        candleStrengthFilter: false,
        minCandleBodyRatio: 35,
        maxRejectionWickRatio: 40,
        dynamicLimitEnable: false,
        limitThresholdPips: 80.0,
        limitPullbackPct: 50.0,
        limitCancelBars: 10,
        dynamicBeEnable: false,
        beTriggerPct: 75.0,
        beLockPips: 5.0,
        r1: 2.0,
        r2: 2.5,
        slBufferMode: "FIXED",
        slBufferMinPips: 5.0,
        slBufferMaxPips: 30.0,
        slAtrMult: 1.0,
        slBufferPips: 20.0,
        minRiskPips: 3.0,
        maxRiskPips: 200.0,
        hmaCooldownBars: 15,
        hmaCurlFilter: false,
        cascadeFilter: false,
        bearRatioThresh: 70,
        sessionFilterEnable: false,
        sessionMode: "ALL",
        sessionStart: "06:00",
        sessionEnd: "19:30",
        hmaShowLine: true,
        hmaShowBaseBox: false,
        showTP: true,
        showSL: true,
      });
      setHmaSystemStyle({
        hmaLine: { visible: true, bullColor: "#10b981", bearColor: "#ef4444", width: 2, style: 0 },
        buyBoxColor: "#06b6d4",
        sellBoxColor: "#f59e0b",
        entry: { visible: true, color: "#facc15", width: 1, style: 2 },
        tp1: { visible: true, color: "#84cc16", width: 1, style: 0 },
        tp2: { visible: true, color: "#22c55e", width: 1, style: 0 },
        sl: { visible: true, color: "#ef4444", width: 1, style: 0 },
        showLabels: true,
      });
      setActivePreset("m1_scalp_21_2r");
    } else if (indicatorId === "bob_volman_system") {
      setVolmanData({
        volmanEmaPeriod: 20,
        volmanR1: 1.5,
        volmanSlBufferPips: 5.0,
        volmanCooldownBars: 5,
        volmanEnableBB: true,
        volmanEnableTB: true,
        volmanMaFilterEnable: false,
        volmanMaPeriod: 200,
        volmanMaType: "EMA",
      });
      setVolmanStyle({
        emaLine: { visible: true, color: "#06b6d4", width: 2, style: 0 },
        barrierColor: "#f59e0b",
        trendlineColor: "#c084fc",
        buyBuildupColor: "#06b6d4",
        sellBuildupColor: "#f43f5e",
        entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
        tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
        sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
        showLabels: true,
      });
      setActivePreset("scalping_20ema");
    } else if (indicatorId === "orb_session_system") {
      setOrbSessionData({
        orbAsiaEnable: true,
        orbLondonEnable: true,
        orbLondonEndHour: 19,
        orbNyEnable: false,
        orbEntryMode: "SMART_HYBRID",
        orbRr: 3.0,
        orbSlBufferPips: 2.0,
        orbMaxBreakPips: 15.0,
        orbMaxLimitBars: 6,
        orbCancelOnTp: true,
        orbCancelOnSl: true,
        orbEma200Filter: false,
        orbMaxZonePips: 250.0,
        orbMinZonePips: 10.0,
        orbRevertEnable: false,
        orbRevertEntryMode: "LIMIT_50_BOX",
        orbRevertRr: 1.5,
        orbRevertCancelBars: 6,
        orbRevertSlBufferPips: 2.0,
      });
      setOrbSessionStyle({
        asiaBoxColor: "#38bdf8",
        londonBoxColor: "#eab308",
        nyBoxColor: "#a855f7",
        entry: { visible: true, color: "#38bdf8", width: 1.5, style: 0 },
        tp: { visible: true, color: "#10b981", width: 1.5, style: 2 },
        sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
        showLabels: true,
      });
      setActivePreset("smart_hybrid_3r");
    } else if (indicatorId === "liquidity_sweep_pro") {
      const adminCfg = getAdminConfig();
      setLiquiditySweepData({
        sweepM1AnchorM5: adminCfg.sweep_m1_anchor_m5 !== undefined ? Boolean(adminCfg.sweep_m1_anchor_m5) : true,
        sweepLookback: adminCfg.sweep_lookback !== undefined ? Number(adminCfg.sweep_lookback) : 5,
        sweepOpt: adminCfg.sweep_opt || "Only Outbreaks & Retest",
        sweepExtend: adminCfg.sweep_extend !== undefined ? Boolean(adminCfg.sweep_extend) : true,
        sweepMaxBars: adminCfg.sweep_max_bars !== undefined ? Number(adminCfg.sweep_max_bars) : 300,
        sweepEntryMode: adminCfg.sweep_entry_mode || "SMART_HYBRID",
        sweepRr: adminCfg.sweep_rr !== undefined ? Number(adminCfg.sweep_rr) : 2.0,
        sweepSlBufferPips: adminCfg.sweep_sl_buffer_pips !== undefined ? Number(adminCfg.sweep_sl_buffer_pips) : 1.5,
        sweepMinRiskPips: adminCfg.sweep_min_risk_pips !== undefined ? Number(adminCfg.sweep_min_risk_pips) : 3.0,
        sweepMaxRiskPips: adminCfg.sweep_max_risk_pips !== undefined ? Number(adminCfg.sweep_max_risk_pips) : 150.0,
        sweepCooldownBars: adminCfg.sweep_cooldown_bars !== undefined ? Number(adminCfg.sweep_cooldown_bars) : 3,
        sweepMinSwingPips: adminCfg.sweep_min_swing_pips !== undefined ? Number(adminCfg.sweep_min_swing_pips) : 18.0,
        sweepCompareBars: adminCfg.sweep_compare_bars !== undefined ? Number(adminCfg.sweep_compare_bars) : 25,
        sweepHighestPeakFilter: adminCfg.sweep_highest_peak_filter !== undefined ? Boolean(adminCfg.sweep_highest_peak_filter) : false,
        sweepPeakLookback: adminCfg.sweep_peak_lookback !== undefined ? Number(adminCfg.sweep_peak_lookback) : 30,
        sweepMaxWaitBars: adminCfg.sweep_max_wait_bars !== undefined ? Number(adminCfg.sweep_max_wait_bars) : 5,
        sweepTrendFilterEnable: adminCfg.sweep_trend_filter_enable !== undefined ? Boolean(adminCfg.sweep_trend_filter_enable) : false,
        sweepTrendEmaPeriod: adminCfg.sweep_trend_ema_period !== undefined ? Number(adminCfg.sweep_trend_ema_period) : 200,
        sweepSessionFilterEnable: adminCfg.sweep_session_filter_enable !== undefined ? Boolean(adminCfg.sweep_session_filter_enable) : false,
        sweepSessionStart: adminCfg.sweep_session_start || "00:00",
        sweepSessionEnd: adminCfg.sweep_session_end || "23:59",
        sweepLimitCancelBars: adminCfg.sweep_limit_cancel_bars !== undefined ? Number(adminCfg.sweep_limit_cancel_bars) : 30,
        sweepCloseOnOpposite: adminCfg.sweep_close_on_opposite !== undefined ? Boolean(adminCfg.sweep_close_on_opposite) : false,
        sweepDynamicBeEnable: adminCfg.sweep_dynamic_be_enable !== undefined ? Boolean(adminCfg.sweep_dynamic_be_enable) : false,
        sweepBeTriggerMode: adminCfg.sweep_be_trigger_mode || "PCT_TP",
        sweepBeTriggerPct: adminCfg.sweep_be_trigger_pct !== undefined ? Number(adminCfg.sweep_be_trigger_pct) : 50.0,
        sweepBeTriggerPips: adminCfg.sweep_be_trigger_pips !== undefined ? Number(adminCfg.sweep_be_trigger_pips) : 40.0,
        sweepBeLockPips: adminCfg.sweep_be_lock_pips !== undefined ? Number(adminCfg.sweep_be_lock_pips) : 2.0,
        sweepHybridThresholdPips: adminCfg.sweep_hybrid_threshold_pips !== undefined ? Number(adminCfg.sweep_hybrid_threshold_pips) : 150.0,
        sweepHybridLimitMode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
        sweepHybridZonePct: adminCfg.sweep_hybrid_zone_pct !== undefined ? Number(adminCfg.sweep_hybrid_zone_pct) : 50.0,
        sweepHybridPullbackPct: adminCfg.sweep_hybrid_pullback_pct !== undefined ? Number(adminCfg.sweep_hybrid_pullback_pct) : 50.0,
      });
      setLiquiditySweepStyle({
        bslColor: "#f59e0b",
        sslColor: "#06b6d4",
        mssColor: "#a855f7",
        entry: { visible: true, color: "#8b5cf6", width: 1.5, style: 0 },
        tp1: { visible: true, color: "#10b981", width: 1.5, style: 2 },
        tp2: { visible: true, color: "#059669", width: 1.5, style: 2 },
        sl: { visible: true, color: "#ef4444", width: 1.5, style: 2 },
        showLabels: true,
      });
      setActivePreset("scalp_m1_quick");
    }
  };

  const handleSave = () => {
    if (indicatorId === "matrix_pro") {
      onApplyParams({
        matrixEmaFast: Number(matrixData.emaFast || 9),
        matrixEmaSlow: Number(matrixData.emaSlow || 21),
        matrixRsiPeriod: Number(matrixData.rsiPeriod || 14),
        matrixTpMult: Number(matrixData.tpMult || 1.8),
        matrixSlMult: Number(matrixData.slMult || 1.2),
        matrixMinConfidence: Number(matrixData.minConfidence || 35),
        matrixSlFilterEnable: Boolean(matrixData.slFilterEnable),
        matrixSlMin: Number(matrixData.slMin ?? 10.0),
        matrixSlMax: Number(matrixData.slMax ?? 150.0),
        matrixCooldownBars: Number(matrixData.cooldownBars || 25),
        matrixTimeoutExitEnable: Boolean(matrixData.timeoutExitEnable),
        matrixMaxHoldingBars: Number(matrixData.maxHoldingBars || 30),
        matrixSessionFilterEnable: Boolean(matrixData.sessionFilterEnable),
        matrixSessionStart: matrixData.sessionStart || "06:00",
        matrixSessionEnd: matrixData.sessionEnd || "03:30",
        matrixEma200FilterEnable: Boolean(matrixData.ema200FilterEnable),
        matrixEma200DistFilterEnable: Boolean(matrixData.ema200DistFilterEnable),
        matrixEma200OuterPips: Number(matrixData.ema200OuterPips || 200.0),
        matrixEma200InnerPips: Number(matrixData.ema200InnerPips || 100.0),
        // Legacy keys for direct compatibility
        emaFast: Number(matrixData.emaFast || 9),
        emaSlow: Number(matrixData.emaSlow || 21),
        rsiPeriod: Number(matrixData.rsiPeriod || 14),
        tpMult: Number(matrixData.tpMult || 1.8),
        slMult: Number(matrixData.slMult || 1.2),
        minConfidence: Number(matrixData.minConfidence || 35),
        slFilterEnable: Boolean(matrixData.slFilterEnable),
        slMin: Number(matrixData.slMin ?? 10.0),
        slMax: Number(matrixData.slMax ?? 150.0),
        cooldownBars: Number(matrixData.cooldownBars || 25),
        timeoutExitEnable: Boolean(matrixData.timeoutExitEnable),
        maxHoldingBars: Number(matrixData.maxHoldingBars || 30),
        sessionFilterEnable: Boolean(matrixData.sessionFilterEnable),
        sessionStart: matrixData.sessionStart || "06:00",
        sessionEnd: matrixData.sessionEnd || "03:30",
        ema200FilterEnable: Boolean(matrixData.ema200FilterEnable),
        ema200DistFilterEnable: Boolean(matrixData.ema200DistFilterEnable),
        ema200OuterPips: Number(matrixData.ema200OuterPips || 200.0),
        ema200InnerPips: Number(matrixData.ema200InnerPips || 100.0),
        showEMA: matrixData.showEMA,
        showTPSL: matrixData.showTPSL,
        showForecast: matrixData.showForecast,
        showRSI: matrixData.showRSI,
      });
    } else if (indicatorId === "msb_dynamic") {
      onApplyParams({
        smcWindowBars: Number(smcData.windowBars || 100),
        smcLenPH: Number(smcData.lenPH || 3),
        smcLenPL: Number(smcData.lenPL || 3),
        smcMinGapBars: Number(smcData.minGapBars || 2),
        smcR1: Number(smcData.r1 || 1.5),
        smc_r1: Number(smcData.r1 || 1.5),
        smcR2: Number(smcData.r2 || 2.0),
        smc_r2: Number(smcData.r2 || 2.0),
        smcEntryMode: smcData.entryMode || "Limit 1",
        smcFibPct: Number(smcData.fibPct || 35.0),
        smcHalfBreakBasis: smcData.halfBreakBasis || "Range(H-L)",
        smcSlFilterEnable: Boolean(smcData.slFilterEnable),
        smcSlMin: Number(smcData.slMin ?? 30.0),
        smcSlMinPips: Number(smcData.slMin ?? 30.0),
        smc_sl_min: Number(smcData.slMin ?? 30.0),
        smcSlMax: Number(smcData.slMax ?? 100.0),
        smcSlMaxPips: Number(smcData.slMax ?? 100.0),
        smc_sl_max: Number(smcData.slMax ?? 100.0),
        smcSlExtraOn: Boolean(smcData.slExtraOn),
        smcSlExtraVal: Number(smcData.slExtraVal ?? 15.0),
        smcSlExtraPips: Number(smcData.slExtraVal ?? 15.0),
        smcPendingCancelOn: Boolean(smcData.pendingCancelOn),
        smcPendingCancelN: Number(smcData.pendingCancelN || 40),
        smcRevEnable: Boolean(smcData.revEnable),
        smcRevR: Number(smcData.revR || 2.0),
        smcHtfFilterEnable: Boolean(smcData.htfFilterEnable),
        smcHtfTimeframe: smcData.htfTimeframe || "AUTO",
        smcHtfBufferPips: Number(smcData.htfBufferPips ?? 5.0),
        smcHtfMaxAgeDays: Number(smcData.htfMaxAgeDays || 7),
        smcHtfMode: smcData.htfMode || "touch",
        smcBreakevenEnable: Boolean(smcData.breakevenEnable),
        smcBreakevenPips: Number(smcData.breakevenPips ?? 100.0),
        smcFvgFilterEnable: Boolean(smcData.fvgFilterEnable),
        fvgFilterEnable: Boolean(smcData.fvgFilterEnable),
        fvg_filter_enable: Boolean(smcData.fvgFilterEnable),
        smcRevertEnable: Boolean(smcData.smcRevertEnable),
        smc_revert_enable: Boolean(smcData.smcRevertEnable),
        smcRevertEntryMode: smcData.smcRevertEntryMode || "MARKET",
        smc_revert_entry_mode: smcData.smcRevertEntryMode || "MARKET",
        smcRevertRr: Number(smcData.smcRevertRr ?? 1.5),
        smc_revert_rr: Number(smcData.smcRevertRr ?? 1.5),
        smcRevertSlBufferPips: Number(smcData.smcRevertSlBufferPips ?? 10.0),
        smc_revert_sl_buffer_pips: Number(smcData.smcRevertSlBufferPips ?? 10.0),
        smcRevertCancelBars: Number(smcData.smcRevertCancelBars ?? 20),
        smc_revert_cancel_bars: Number(smcData.smcRevertCancelBars ?? 20),
        smcSingleOrderPerSide: smcData.smcSingleOrderPerSide !== false,
        smc_single_order_per_side: smcData.smcSingleOrderPerSide !== false,
        smcMaxOrdersMode: smcData.smcMaxOrdersMode || "ONE_PER_SIDE",
        smc_max_orders_mode: smcData.smcMaxOrdersMode || "ONE_PER_SIDE",
        smcHybridThresholdPips: Number(smcData.smcHybridThresholdPips ?? 80.0),
        smc_hybrid_threshold_pips: Number(smcData.smcHybridThresholdPips ?? 80.0),
        smcHybridPullbackPct: Number(smcData.smcHybridPullbackPct ?? 50.0),
        smc_hybrid_pullback_pct: Number(smcData.smcHybridPullbackPct ?? 50.0),
        // Legacy keys
        windowBars: Number(smcData.windowBars || 100),
        lenPH: Number(smcData.lenPH || 3),
        lenPL: Number(smcData.lenPL || 3),
        minGapBars: Number(smcData.minGapBars || 2),
        r1: Number(smcData.r1 || 1.5),
        r2: Number(smcData.r2 || 2.0),
        entryMode: smcData.entryMode || "Limit 1",
        fibPct: Number(smcData.fibPct || 35.0),
        halfBreakBasis: smcData.halfBreakBasis || "Range(H-L)",
        slFilterEnable: Boolean(smcData.slFilterEnable),
        slMin: Number(smcData.slMin ?? 30.0),
        slMax: Number(smcData.slMax ?? 100.0),
        slExtraOn: Boolean(smcData.slExtraOn),
        slExtraVal: Number(smcData.slExtraVal ?? 15.0),
        pendingCancelOn: Boolean(smcData.pendingCancelOn),
        pendingCancelN: Number(smcData.pendingCancelN || 40),
        revEnable: Boolean(smcData.revEnable),
        revR: Number(smcData.revR || 2.0),
        htfFilterEnable: Boolean(smcData.htfFilterEnable),
        htfTimeframe: smcData.htfTimeframe || "AUTO",
        htfBufferPips: Number(smcData.htfBufferPips ?? 5.0),
        htfMaxAgeDays: Number(smcData.htfMaxAgeDays || 7),
        htfMode: smcData.htfMode || "touch",
        breakevenEnable: Boolean(smcData.breakevenEnable),
        breakevenPips: Number(smcData.breakevenPips ?? 100.0),
        showMSBLines: smcData.showMSBLines,
        showTP: smcData.showTP,
        showSL: smcData.showSL,
      });
    } else if (indicatorId === "smc_structure") {
      onApplyParams(classicalSmcData);
    } else if (indicatorId === "bob_volman_system") {
      onApplyParams({
        volmanEmaPeriod: Number(volmanData.volmanEmaPeriod || 20),
        volman_ema_period: Number(volmanData.volmanEmaPeriod || 20),
        volmanR1: Number(volmanData.volmanR1 || 1.5),
        volman_r1: Number(volmanData.volmanR1 || 1.5),
        volmanSlBufferPips: Number(volmanData.volmanSlBufferPips ?? 5.0),
        volman_sl_buffer_pips: Number(volmanData.volmanSlBufferPips ?? 5.0),
        volmanCooldownBars: Number(volmanData.volmanCooldownBars ?? 5),
        volman_cooldown_bars: Number(volmanData.volmanCooldownBars ?? 5),
        volmanEnableBB: Boolean(volmanData.volmanEnableBB),
        volman_enable_bb: Boolean(volmanData.volmanEnableBB),
        volmanEnableTB: Boolean(volmanData.volmanEnableTB),
        volman_enable_tb: Boolean(volmanData.volmanEnableTB),
        volmanEnableSB: Boolean(volmanData.volmanEnableSB),
        volman_enable_sb: Boolean(volmanData.volmanEnableSB),
        volmanPivotLength: Number(volmanData.volmanPivotLength ?? 3),
        volman_pivot_length: Number(volmanData.volmanPivotLength ?? 3),
        volmanSlFilterEnable: Boolean(volmanData.volmanSlFilterEnable),
        volman_sl_filter_enable: Boolean(volmanData.volmanSlFilterEnable),
        volmanMinRiskPips: Number(volmanData.volmanMinRiskPips ?? 3.0),
        volman_min_risk_pips: Number(volmanData.volmanMinRiskPips ?? 3.0),
        volmanMaxRiskPips: Number(volmanData.volmanMaxRiskPips ?? 150.0),
        volman_max_risk_pips: Number(volmanData.volmanMaxRiskPips ?? 150.0),
        volmanBreakoutCandleFilter: Boolean(volmanData.volmanBreakoutCandleFilter),
        volman_breakout_candle_filter: Boolean(volmanData.volmanBreakoutCandleFilter),
        volmanMinBodyRatio: Number(volmanData.volmanMinBodyRatio ?? 50),
        volman_min_body_ratio: Number(volmanData.volmanMinBodyRatio ?? 50),
        volmanMinBodyBeyondPct: Number(volmanData.volmanMinBodyBeyondPct ?? 30),
        volman_min_body_beyond_pct: Number(volmanData.volmanMinBodyBeyondPct ?? 30),
        volmanMaxRejectionWick: Number(volmanData.volmanMaxRejectionWick ?? 25),
        volman_max_rejection_wick: Number(volmanData.volmanMaxRejectionWick ?? 25),
        volmanDynamicLimitEnable: Boolean(volmanData.volmanDynamicLimitEnable),
        volman_dynamic_limit_enable: Boolean(volmanData.volmanDynamicLimitEnable),
        volmanLimitThresholdPips: Number(volmanData.volmanLimitThresholdPips ?? 80),
        volman_limit_threshold_pips: Number(volmanData.volmanLimitThresholdPips ?? 80),
        volmanLimitPullbackPct: Number(volmanData.volmanLimitPullbackPct ?? 40),
        volman_limit_pullback_pct: Number(volmanData.volmanLimitPullbackPct ?? 40),
        volmanLimitCancelBars: Number(volmanData.volmanLimitCancelBars ?? 20),
        volman_limit_cancel_bars: Number(volmanData.volmanLimitCancelBars ?? 20),
        volmanMaFilterEnable: Boolean(volmanData.volmanMaFilterEnable),
        volman_ma_filter_enable: Boolean(volmanData.volmanMaFilterEnable),
        volmanMaPeriod: Number(volmanData.volmanMaPeriod || 200),
        volman_ma_period: Number(volmanData.volmanMaPeriod || 200),
        volmanMaType: volmanData.volmanMaType || "EMA",
        volman_ma_type: volmanData.volmanMaType || "EMA",
        volmanMaxConsecutiveEnable: Boolean(volmanData.volmanMaxConsecutiveEnable),
        volman_max_consecutive_enable: Boolean(volmanData.volmanMaxConsecutiveEnable),
        volmanMaxConsecutiveSameDir: Number(volmanData.volmanMaxConsecutiveSameDir ?? 3),
        volman_max_consecutive_same_dir: Number(volmanData.volmanMaxConsecutiveSameDir ?? 3),
      });
    } else if (indicatorId === "orb_session_system") {
      onApplyParams({
        orbAsiaEnable: orbSessionData.orbAsiaEnable,
        orb_asia_enable: orbSessionData.orbAsiaEnable,
        orbLondonEnable: orbSessionData.orbLondonEnable,
        orb_london_enable: orbSessionData.orbLondonEnable,
        orbLondonEndHour: Number(orbSessionData.orbLondonEndHour || 19),
        orb_london_end_hour: Number(orbSessionData.orbLondonEndHour || 19),
        orbNyEnable: orbSessionData.orbNyEnable,
        orb_ny_enable: orbSessionData.orbNyEnable,
        orbEntryMode: orbSessionData.orbEntryMode,
        orb_entry_mode: orbSessionData.orbEntryMode,
        orbRr: Number(orbSessionData.orbRr || 3.0),
        orb_rr: Number(orbSessionData.orbRr || 3.0),
        orbSlBufferMode: orbSessionData.orbSlBufferMode || "FIXED",
        orb_sl_buffer_mode: orbSessionData.orbSlBufferMode || "FIXED",
        orbSlBufferPips: Number(orbSessionData.orbSlBufferPips ?? 2.0),
        orb_sl_buffer_pips: Number(orbSessionData.orbSlBufferPips ?? 2.0),
        orbSlAtrMult: Number(orbSessionData.orbSlAtrMult ?? 1.0),
        orb_sl_atr_mult: Number(orbSessionData.orbSlAtrMult ?? 1.0),
        orbSlBufferMinPips: Number(orbSessionData.orbSlBufferMinPips ?? 2.0),
        orb_sl_buffer_min_pips: Number(orbSessionData.orbSlBufferMinPips ?? 2.0),
        orbSlBufferMaxPips: Number(orbSessionData.orbSlBufferMaxPips ?? 15.0),
        orb_sl_buffer_max_pips: Number(orbSessionData.orbSlBufferMaxPips ?? 15.0),
        orbSlFilterEnable: Boolean(orbSessionData.orbSlFilterEnable),
        orb_sl_filter_enable: Boolean(orbSessionData.orbSlFilterEnable),
        orbMinSlPips: Number(orbSessionData.orbMinSlPips ?? 3.0),
        orb_min_sl_pips: Number(orbSessionData.orbMinSlPips ?? 3.0),
        orbMaxSlPips: Number(orbSessionData.orbMaxSlPips ?? 150.0),
        orb_max_sl_pips: Number(orbSessionData.orbMaxSlPips ?? 150.0),
        orbMaxBreakPips: Number(orbSessionData.orbMaxBreakPips ?? 15.0),
        orb_max_break_pips: Number(orbSessionData.orbMaxBreakPips ?? 15.0),
        orbMaxLimitBars: Number(orbSessionData.orbMaxLimitBars ?? 6),
        orb_max_limit_bars: Number(orbSessionData.orbMaxLimitBars ?? 6),
        orbCancelOnTp: Boolean(orbSessionData.orbCancelOnTp !== false),
        orb_cancel_on_tp: Boolean(orbSessionData.orbCancelOnTp !== false),
        orbCancelOnSl: Boolean(orbSessionData.orbCancelOnSl !== false),
        orb_cancel_on_sl: Boolean(orbSessionData.orbCancelOnSl !== false),
        orbEma200Filter: Boolean(orbSessionData.orbEma200Mode ? orbSessionData.orbEma200Mode !== "OFF" : orbSessionData.orbEma200Filter),
        orb_ema200_filter: Boolean(orbSessionData.orbEma200Mode ? orbSessionData.orbEma200Mode !== "OFF" : orbSessionData.orbEma200Filter),
        orbEma200Mode: orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF"),
        orb_ema200_mode: orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF"),
        orbMaxZonePips: Number(orbSessionData.orbMaxZonePips ?? 250.0),
        orb_max_zone_pips: Number(orbSessionData.orbMaxZonePips ?? 250.0),
        orbMinZonePips: Number(orbSessionData.orbMinZonePips ?? 10.0),
        orb_min_zone_pips: Number(orbSessionData.orbMinZonePips ?? 10.0),
        orbDynamicBeEnable: Boolean(orbSessionData.orbDynamicBeEnable),
        orb_dynamic_be_enable: Boolean(orbSessionData.orbDynamicBeEnable),
        orbBeTriggerPct: Number(orbSessionData.orbBeTriggerPct ?? 75),
        orb_be_trigger_pct: Number(orbSessionData.orbBeTriggerPct ?? 75),
        orbBeLockPips: Number(orbSessionData.orbBeLockPips ?? 5.0),
        orb_be_lock_pips: Number(orbSessionData.orbBeLockPips ?? 5.0),
        orbTimeoutExitEnable: Boolean(orbSessionData.orbTimeoutExitEnable !== false),
        orb_timeout_exit_enable: Boolean(orbSessionData.orbTimeoutExitEnable !== false),
        orbRevertEnable: Boolean(orbSessionData.orbRevertEnable),
        orb_revert_enable: Boolean(orbSessionData.orbRevertEnable),
        orbRevertEntryMode: orbSessionData.orbRevertEntryMode || "LIMIT_50_BOX",
        orb_revert_entry_mode: orbSessionData.orbRevertEntryMode || "LIMIT_50_BOX",
        orbRevertRr: Number(orbSessionData.orbRevertRr ?? 1.5),
        orb_revert_rr: Number(orbSessionData.orbRevertRr ?? 1.5),
        orbRevertCancelBars: Number(orbSessionData.orbRevertCancelBars ?? 6),
        orb_revert_cancel_bars: Number(orbSessionData.orbRevertCancelBars ?? 6),
        orbRevertSlBufferPips: Number(orbSessionData.orbRevertSlBufferPips ?? 2.0),
        orb_revert_sl_buffer_pips: Number(orbSessionData.orbRevertSlBufferPips ?? 2.0),
        orbDynamicLimitEnable: Boolean(orbSessionData.orbDynamicLimitEnable),
        orb_dynamic_limit_enable: Boolean(orbSessionData.orbDynamicLimitEnable),
        orbLimitThresholdPips: Number(orbSessionData.orbLimitThresholdPips ?? 100.0),
        orb_limit_threshold_pips: Number(orbSessionData.orbLimitThresholdPips ?? 100.0),
        orbLimitPullbackPct: Number(orbSessionData.orbLimitPullbackPct ?? 50.0),
        orb_limit_pullback_pct: Number(orbSessionData.orbLimitPullbackPct ?? 50.0),
        orbLimitCancelBars: Number(orbSessionData.orbLimitCancelBars ?? 10),
        orb_limit_cancel_bars: Number(orbSessionData.orbLimitCancelBars ?? 10),
      });
    } else if (indicatorId === "hma_breakout_system") {
      const hmaCooldown = hmaSystemData.hmaCooldownBars !== undefined ? Number(hmaSystemData.hmaCooldownBars) : (hmaSystemData.cooldownBars !== undefined ? Number(hmaSystemData.cooldownBars) : 5);
      const buffer = Number(hmaSystemData.slBufferPips ?? hmaSystemData.hmaSlBufferPips ?? 5.0);
      const r1Val = Number(hmaSystemData.r1 ?? hmaSystemData.hmaR1 ?? 1.5);
      const r2Val = Number(hmaSystemData.r2 ?? hmaSystemData.hmaR2 ?? 2.0);
      const maxRisk = Number(hmaSystemData.maxRiskPips ?? hmaSystemData.hmaMaxRiskPips ?? 80.0);
      const minRisk = Number(hmaSystemData.minRiskPips ?? hmaSystemData.hmaMinRiskPips ?? 3.0);
      const periodVal = Number(hmaSystemData.period ?? hmaSystemData.hmaPeriod ?? 24);
      const lookbackVal = Number(hmaSystemData.lookbackBars ?? hmaSystemData.hmaLookbackBars ?? 100);
      const maxBarsVal = Number(hmaSystemData.maxBarsFromExtreme ?? hmaSystemData.hmaMaxBarsFromExtreme ?? 35);
      const minBodyVal = Number(hmaSystemData.minBodyPct ?? hmaSystemData.hmaMinBodyPct ?? 40);

      onApplyParams({
        period: periodVal,
        hmaPeriod: periodVal,
        hma_period: periodVal,
        lookbackBars: lookbackVal,
        hmaLookbackBars: lookbackVal,
        hma_lookback_bars: lookbackVal,
        maxBarsFromExtreme: maxBarsVal,
        hmaMaxBarsFromExtreme: maxBarsVal,
        hma_max_bars_from_extreme: maxBarsVal,
        minBodyPct: minBodyVal,
        hmaMinBodyPct: minBodyVal,
        hma_min_body_pct: minBodyVal,
        hmaSlopeFilter: Boolean(hmaSystemData.hmaSlopeFilter),
        hma_slope_filter: Boolean(hmaSystemData.hmaSlopeFilter),
        maxHmaSlope: Number(hmaSystemData.maxHmaSlope || 3.0),
        hmaMaxSlope: Number(hmaSystemData.maxHmaSlope || 3.0),
        hma_max_slope: Number(hmaSystemData.maxHmaSlope || 3.0),
        allowFloatingBreakout: Boolean(hmaSystemData.allowFloatingBreakout),
        hma_allow_floating_breakout: Boolean(hmaSystemData.allowFloatingBreakout),
        candleStrengthFilter: Boolean(hmaSystemData.candleStrengthFilter),
        hma_candle_strength_filter: Boolean(hmaSystemData.candleStrengthFilter),
        minCandleBodyRatio: Number(hmaSystemData.minCandleBodyRatio || 35),
        hma_min_candle_body_ratio: Number(hmaSystemData.minCandleBodyRatio || 35),
        maxRejectionWickRatio: Number(hmaSystemData.maxRejectionWickRatio || 40),
        hma_max_rejection_wick_ratio: Number(hmaSystemData.maxRejectionWickRatio || 40),
        dynamicLimitEnable: Boolean(hmaSystemData.dynamicLimitEnable),
        hmaDynamicLimitEnable: Boolean(hmaSystemData.dynamicLimitEnable),
        hma_dynamic_limit_enable: Boolean(hmaSystemData.dynamicLimitEnable),
        limitThresholdPips: Number(hmaSystemData.limitThresholdPips || 80.0),
        hmaLimitThresholdPips: Number(hmaSystemData.limitThresholdPips || 80.0),
        hma_limit_threshold_pips: Number(hmaSystemData.limitThresholdPips || 80.0),
        limitPullbackPct: Number(hmaSystemData.limitPullbackPct || 50.0),
        hmaLimitPullbackPct: Number(hmaSystemData.limitPullbackPct || 50.0),
        hma_limit_pullback_pct: Number(hmaSystemData.limitPullbackPct || 50.0),
        limitCancelBars: Number(hmaSystemData.limitCancelBars || 10),
        hmaLimitCancelBars: Number(hmaSystemData.limitCancelBars || 10),
        hma_limit_cancel_bars: Number(hmaSystemData.limitCancelBars || 10),
        dynamicBeEnable: Boolean(hmaSystemData.dynamicBeEnable),
        hmaDynamicBeEnable: Boolean(hmaSystemData.dynamicBeEnable),
        hma_dynamic_be_enable: Boolean(hmaSystemData.dynamicBeEnable),
        beTriggerPct: Number(hmaSystemData.beTriggerPct || 75.0),
        hmaBeTriggerPct: Number(hmaSystemData.beTriggerPct || 75.0),
        hma_be_trigger_pct: Number(hmaSystemData.beTriggerPct || 75.0),
        beLockPips: Number(hmaSystemData.beLockPips ?? 5.0),
        hmaBeLockPips: Number(hmaSystemData.beLockPips ?? 5.0),
        hma_be_lock_pips: Number(hmaSystemData.beLockPips ?? 5.0),
        r1: r1Val,
        hmaR1: r1Val,
        hma_r1: r1Val,
        r2: r2Val,
        hmaR2: r2Val,
        hma_r2: r2Val,
        slBufferMode: hmaSystemData.slBufferMode || "ATR",
        hmaSlBufferMode: hmaSystemData.slBufferMode || "ATR",
        hma_sl_buffer_mode: hmaSystemData.slBufferMode || "ATR",
        slBufferMinPips: Number(hmaSystemData.slBufferMinPips ?? 5.0),
        hmaSlBufferMinPips: Number(hmaSystemData.slBufferMinPips ?? 5.0),
        hma_sl_buffer_min_pips: Number(hmaSystemData.slBufferMinPips ?? 5.0),
        slBufferMaxPips: Number(hmaSystemData.slBufferMaxPips ?? 30.0),
        hmaSlBufferMaxPips: Number(hmaSystemData.slBufferMaxPips ?? 30.0),
        hma_sl_buffer_max_pips: Number(hmaSystemData.slBufferMaxPips ?? 30.0),
        slAtrMult: Number(hmaSystemData.slAtrMult ?? 1.0),
        hmaSlAtrMult: Number(hmaSystemData.slAtrMult ?? 1.0),
        hma_sl_atr_mult: Number(hmaSystemData.slAtrMult ?? 1.0),
        slBufferPips: buffer,
        hmaSlBufferPips: buffer,
        hma_sl_buffer_pips: buffer,
        minRiskPips: minRisk,
        hmaMinRiskPips: minRisk,
        hma_min_risk_pips: minRisk,
        maxRiskPips: maxRisk,
        hmaMaxRiskPips: maxRisk,
        hma_max_risk_pips: maxRisk,
        cooldownBars: hmaCooldown,
        hmaCooldownBars: hmaCooldown,
        hma_cooldown_bars: hmaCooldown,
        hmaCurlFilter: Boolean(hmaSystemData.hmaCurlFilter),
        hma_curl_filter: Boolean(hmaSystemData.hmaCurlFilter),
        cascadeFilter: Boolean(hmaSystemData.cascadeFilter),
        cascade_filter: Boolean(hmaSystemData.cascadeFilter),
        bearRatioThresh: Number(hmaSystemData.bearRatioThresh ?? 70),
        bear_ratio_thresh: Number(hmaSystemData.bearRatioThresh ?? 70),
        sessionFilterEnable: Boolean(hmaSystemData.sessionFilterEnable),
        hmaSessionFilterEnable: Boolean(hmaSystemData.sessionFilterEnable),
        hma_session_filter_enable: Boolean(hmaSystemData.sessionFilterEnable),
        sessionMode: hmaSystemData.sessionMode || "ALL",
        hmaSessionMode: hmaSystemData.sessionMode || "ALL",
        hma_session_mode: hmaSystemData.sessionMode || "ALL",
        sessionStart: hmaSystemData.sessionStart || "06:00",
        hmaSessionStart: hmaSystemData.sessionStart || "06:00",
        hma_session_start: hmaSystemData.sessionStart || "06:00",
        sessionEnd: hmaSystemData.sessionEnd || "19:30",
        hmaSessionEnd: hmaSystemData.sessionEnd || "19:30",
        hma_session_end: hmaSystemData.sessionEnd || "19:30",
        closeOnOpposite: Boolean(hmaSystemData.closeOnOpposite !== false),
        close_on_opposite: Boolean(hmaSystemData.closeOnOpposite !== false),
        hmaCloseOnOpposite: Boolean(hmaSystemData.closeOnOpposite !== false),
        hma_close_on_opposite: Boolean(hmaSystemData.closeOnOpposite !== false),
        hmaShowLine: hmaSystemData.hmaShowLine,
        hmaShowBaseBox: hmaSystemData.hmaShowBaseBox,
      });
    } else if (indicatorId === "scalping_pro_system") {
      onApplyParams({
        scalpLookback: Number(scalpingProData.scalpLookback || 5),
        scalp_lookback: Number(scalpingProData.scalpLookback || 5),
        scalpOpt: scalpingProData.scalpOpt || "Only Outbreaks & Retest",
        scalp_opt: scalpingProData.scalpOpt || "Only Outbreaks & Retest",
        scalpExtend: Boolean(scalpingProData.scalpExtend !== false),
        scalp_extend: Boolean(scalpingProData.scalpExtend !== false),
        scalpMaxBars: Number(scalpingProData.scalpMaxBars || 300),
        scalp_max_bars: Number(scalpingProData.scalpMaxBars || 300),
        scalpEntryMode: scalpingProData.scalpEntryMode || "SMART_HYBRID",
        scalp_entry_mode: scalpingProData.scalpEntryMode || "SMART_HYBRID",
        scalpHybridThresholdPips: Number(scalpingProData.scalpHybridThresholdPips ?? 80.0),
        scalp_hybrid_threshold_pips: Number(scalpingProData.scalpHybridThresholdPips ?? 80.0),
        scalpHybridLimitMode: scalpingProData.scalpHybridLimitMode || "RETEST",
        scalp_hybrid_limit_mode: scalpingProData.scalpHybridLimitMode || "RETEST",
        scalpHybridZonePct: Number(scalpingProData.scalpHybridZonePct ?? 50.0),
        scalp_hybrid_zone_pct: Number(scalpingProData.scalpHybridZonePct ?? 50.0),
        scalpHybridPullbackPct: Number(scalpingProData.scalpHybridPullbackPct ?? 50.0),
        scalp_hybrid_pullback_pct: Number(scalpingProData.scalpHybridPullbackPct ?? 50.0),
        scalpRr: Number(scalpingProData.scalpRr || 2.0),
        scalp_rr: Number(scalpingProData.scalpRr || 2.0),
        scalpSlBufferPips: Number(scalpingProData.scalpSlBufferPips ?? 1.5),
        scalp_sl_buffer_pips: Number(scalpingProData.scalpSlBufferPips ?? 1.5),
        scalpMinRiskPips: Number(scalpingProData.scalpMinRiskPips ?? 3.0),
        scalp_min_risk_pips: Number(scalpingProData.scalpMinRiskPips ?? 3.0),
        scalpMaxRiskPips: Number(scalpingProData.scalpMaxRiskPips ?? 150.0),
        scalp_max_risk_pips: Number(scalpingProData.scalpMaxRiskPips ?? 150.0),
        scalpCooldownBars: Number(scalpingProData.scalpCooldownBars || 3),
        scalp_cooldown_bars: Number(scalpingProData.scalpCooldownBars || 3),
        scalpHighestPeakFilter: Boolean(scalpingProData.scalpHighestPeakFilter),
        scalp_highest_peak_filter: Boolean(scalpingProData.scalpHighestPeakFilter),
        scalpPeakLookback: Number(scalpingProData.scalpPeakLookback || 30),
        scalp_peak_lookback: Number(scalpingProData.scalpPeakLookback || 30),
        scalpMaxWaitBars: Number(scalpingProData.scalpMaxWaitBars || 20),
        scalp_max_wait_bars: Number(scalpingProData.scalpMaxWaitBars || 20),
        scalpLimitCancelBars: Number(scalpingProData.scalpLimitCancelBars || 30),
        scalp_limit_cancel_bars: Number(scalpingProData.scalpLimitCancelBars || 30),
        scalpCloseOnOpposite: Boolean(scalpingProData.scalpCloseOnOpposite),
        scalp_close_on_opposite: Boolean(scalpingProData.scalpCloseOnOpposite),
        scalpDynamicBeEnable: Boolean(scalpingProData.scalpDynamicBeEnable),
        scalp_dynamic_be_enable: Boolean(scalpingProData.scalpDynamicBeEnable),
        scalpBeTriggerMode: scalpingProData.scalpBeTriggerMode || "PCT_TP",
        scalp_be_trigger_mode: scalpingProData.scalpBeTriggerMode || "PCT_TP",
        scalpBeTriggerPct: Number(scalpingProData.scalpBeTriggerPct ?? 50.0),
        scalp_be_trigger_pct: Number(scalpingProData.scalpBeTriggerPct ?? 50.0),
        scalpBeTriggerPips: Number(scalpingProData.scalpBeTriggerPips ?? 40.0),
        scalp_be_trigger_pips: Number(scalpingProData.scalpBeTriggerPips ?? 40.0),
        scalpBeLockPips: Number(scalpingProData.scalpBeLockPips ?? 2.0),
        scalp_be_lock_pips: Number(scalpingProData.scalpBeLockPips ?? 2.0),
      });
    } else if (indicatorId === "liquidity_sweep_pro") {
      const adminCfg = getAdminConfig();
      onApplyParams({
        userCustomizedSweep: true,
        sweepM1AnchorM5: Boolean(liquiditySweepData.sweepM1AnchorM5 !== false),
        sweep_m1_anchor_m5: Boolean(liquiditySweepData.sweepM1AnchorM5 !== false),
        sweepLookback: Number(liquiditySweepData.sweepLookback || 5),
        sweep_lookback: Number(liquiditySweepData.sweepLookback || 5),
        len: Number(liquiditySweepData.sweepLookback || 5),
        sweepOpt: "Only Outbreaks & Retest",
        sweep_opt: "Only Outbreaks & Retest",
        opt: "Only Outbreaks & Retest",
        sweepExtend: Boolean(liquiditySweepData.sweepExtend !== false),
        sweep_extend: Boolean(liquiditySweepData.sweepExtend !== false),
        extend: Boolean(liquiditySweepData.sweepExtend !== false),
        sweepMaxBars: Number(liquiditySweepData.sweepMaxBars || 300),
        sweep_max_bars: Number(liquiditySweepData.sweepMaxBars || 300),
        maxB: Number(liquiditySweepData.sweepMaxBars || 300),
        sweepEntryMode: liquiditySweepData.sweepEntryMode || "SMART_HYBRID",
        sweep_entry_mode: liquiditySweepData.sweepEntryMode || "SMART_HYBRID",
        entry_mode: liquiditySweepData.sweepEntryMode || "SMART_HYBRID",
        sweepRr: Number(liquiditySweepData.sweepRr || 2.0),
        sweep_rr: Number(liquiditySweepData.sweepRr || 2.0),
        rr_ratio: Number(liquiditySweepData.sweepRr || 2.0),
        sweepSlBufferPips: Number(liquiditySweepData.sweepSlBufferPips ?? 1.5),
        sweep_sl_buffer_pips: Number(liquiditySweepData.sweepSlBufferPips ?? 1.5),
        sl_buffer_pips: Number(liquiditySweepData.sweepSlBufferPips ?? 1.5),
        sweepMinRiskPips: Number(strategyParams?.sweepMinRiskPips ?? strategyParams?.sweep_min_risk_pips ?? liquiditySweepData.sweepMinRiskPips ?? 3.0),
        sweep_min_risk_pips: Number(strategyParams?.sweep_min_risk_pips ?? strategyParams?.sweepMinRiskPips ?? liquiditySweepData.sweep_min_risk_pips ?? 3.0),
        sweepMaxRiskPips: Number(liquiditySweepData.sweepMaxRiskPips ?? 150.0),
        sweep_max_risk_pips: Number(liquiditySweepData.sweepMaxRiskPips ?? 150.0),
        sweepCooldownBars: Number(liquiditySweepData.sweepCooldownBars || 3),
        sweep_cooldown_bars: Number(liquiditySweepData.sweepCooldownBars || 3),
        sweepMinSwingPips: Number(liquiditySweepData.sweepMinSwingPips ?? 18.0),
        sweep_min_swing_pips: Number(liquiditySweepData.sweepMinSwingPips ?? 18.0),
        sweepCompareBars: Number(liquiditySweepData.sweepCompareBars ?? 25),
        sweep_compare_bars: Number(liquiditySweepData.sweepCompareBars ?? 25),
        sweepHighestPeakFilter: Boolean(liquiditySweepData.sweepHighestPeakFilter),
        sweep_highest_peak_filter: Boolean(liquiditySweepData.sweepHighestPeakFilter),
        sweepPeakLookback: Number(liquiditySweepData.sweepPeakLookback ?? 30),
        sweep_peak_lookback: Number(liquiditySweepData.sweepPeakLookback ?? 30),
        sweepTrendFilterEnable: Boolean(liquiditySweepData.sweepTrendFilterEnable),
        sweep_trend_filter_enable: Boolean(liquiditySweepData.sweepTrendFilterEnable),
        trend_filter_enable: Boolean(liquiditySweepData.sweepTrendFilterEnable),
        sweepTrendEmaPeriod: Number(liquiditySweepData.sweepTrendEmaPeriod || 200),
        sweep_trend_ema_period: Number(liquiditySweepData.sweepTrendEmaPeriod || 200),
        sweepSessionFilterEnable: Boolean(liquiditySweepData.sweepSessionFilterEnable),
        sweep_session_filter_enable: Boolean(liquiditySweepData.sweepSessionFilterEnable),
        sweepSessionStart: liquiditySweepData.sweepSessionStart || "00:00",
        sweep_session_start: liquiditySweepData.sweepSessionStart || "00:00",
        sweepSessionEnd: liquiditySweepData.sweepSessionEnd || "23:59",
        sweep_session_end: liquiditySweepData.sweepSessionEnd || "23:59",
        sweepLimitCancelBars: Number(liquiditySweepData.sweepLimitCancelBars || 30),
        sweep_limit_cancel_bars: Number(liquiditySweepData.sweepLimitCancelBars || 30),
        sweepMaxWaitBars: Math.max(2, Math.min(15, Number(liquiditySweepData.sweepMaxWaitBars || 5))),
        sweep_max_wait_bars: Math.max(2, Math.min(15, Number(liquiditySweepData.sweepMaxWaitBars || 5))),
        sweepCloseOnOpposite: Boolean(liquiditySweepData.sweepCloseOnOpposite),
        sweep_close_on_opposite: Boolean(liquiditySweepData.sweepCloseOnOpposite),
        sweepDynamicBeEnable: Boolean(liquiditySweepData.sweepDynamicBeEnable),
        sweep_dynamic_be_enable: Boolean(liquiditySweepData.sweepDynamicBeEnable),
        sweepBeTriggerMode: liquiditySweepData.sweepBeTriggerMode || "PCT_TP",
        sweep_be_trigger_mode: liquiditySweepData.sweepBeTriggerMode || "PCT_TP",
        sweepBeTriggerPct: Number(liquiditySweepData.sweepBeTriggerPct ?? 50.0),
        sweep_be_trigger_pct: Number(liquiditySweepData.sweepBeTriggerPct ?? 50.0),
        sweepBeTriggerPips: Number(liquiditySweepData.sweepBeTriggerPips ?? 40.0),
        sweep_be_trigger_pips: Number(liquiditySweepData.sweepBeTriggerPips ?? 40.0),
        sweepBeLockPips: Number(liquiditySweepData.sweepBeLockPips ?? 2.0),
        sweep_be_lock_pips: Number(liquiditySweepData.sweepBeLockPips ?? 2.0),
        sweepZoneLimitEnable: adminCfg.sweep_zone_limit_enable !== undefined ? (adminCfg.sweep_zone_limit_enable === true || adminCfg.sweep_zone_limit_enable === 'true') : true,
        sweep_zone_limit_enable: adminCfg.sweep_zone_limit_enable !== undefined ? (adminCfg.sweep_zone_limit_enable === true || adminCfg.sweep_zone_limit_enable === 'true') : true,
        sweepHybridThresholdPips: Number(adminCfg.sweep_hybrid_threshold_pips ?? 80.0),
        sweep_hybrid_threshold_pips: Number(adminCfg.sweep_hybrid_threshold_pips ?? 80.0),
        sweepHybridLimitMode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
        sweep_hybrid_limit_mode: adminCfg.sweep_hybrid_limit_mode || "ZONE_50",
        sweepHybridZonePct: Number(adminCfg.sweep_hybrid_zone_pct ?? 50.0),
        sweep_hybrid_zone_pct: Number(adminCfg.sweep_hybrid_zone_pct ?? 50.0),
        sweepHybridPullbackPct: Number(adminCfg.sweep_hybrid_pullback_pct ?? 50.0),
        sweep_hybrid_pullback_pct: Number(adminCfg.sweep_hybrid_pullback_pct ?? 50.0),
      });
    }
    if (onApplyStyles) {
      onApplyStyles({
        ichimoku: ichimokuStyle,
        multiEma: multiEmaStyle,
        bb: bbStyle,
        smc: smcStyle,
        classicalSmc: classicalSmcStyle,
        hma: hmaStyle,
        hmaSystem: hmaSystemStyle,
        volman: volmanStyle,
        orbSession: orbSessionStyle,
        liquiditySweep: liquiditySweepStyle,
      });
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl flex flex-col font-sans">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-800 bg-slate-950/50">
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-slate-100 truncate max-w-xs">
              {currentDef.name}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 px-6 border-b border-slate-800 text-xs font-semibold text-slate-400 bg-slate-950/20">
          <button
            onClick={() => setActiveTab("inputs")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "inputs"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent hover:text-slate-200"
            }`}
          >
            Các đầu vào (Inputs)
          </button>
          <button
            onClick={() => setActiveTab("style")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "style"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent hover:text-slate-200"
            }`}
          >
            Định dạng (Style / Màu sắc)
          </button>
          <button
            onClick={() => setActiveTab("visibility")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "visibility"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent hover:text-slate-200"
            }`}
          >
            Hiển thị (Visibility)
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 max-h-[62vh] overflow-y-auto space-y-4 text-xs">
          {/* ================= INPUTS TAB ================= */}
          {activeTab === "inputs" && (
            <div className="space-y-4">
              {/* MATRIX PRO INPUTS */}
              {indicatorId === "matrix_pro" && (
                <>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2 block">
                      CẤU HÌNH MẪU (PRESETS CHIẾN LƯỢC)
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      {STRATEGY_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActivePreset(preset.id);
                            setMatrixData((prev) => ({ ...prev, ...preset.params }));
                          }}
                          className={`p-2.5 rounded-xl text-left border transition cursor-pointer ${
                            activePreset === preset.id
                              ? "bg-cyan-500/15 border-cyan-400 text-cyan-300 font-bold shadow-sm"
                              : "bg-slate-950/50 border-slate-800 hover:bg-slate-950 hover:border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="text-xs font-bold block">{preset.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5 line-clamp-1">
                            {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">EMA Fast (Giao cắt nhanh):</span>
                      <input
                        type="number"
                        value={matrixData.emaFast}
                        onChange={(e) => setMatrixData({ ...matrixData, emaFast: parseFloat(e.target.value) || 9 })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">EMA Slow (Giao cắt chậm):</span>
                      <input
                        type="number"
                        value={matrixData.emaSlow}
                        onChange={(e) => setMatrixData({ ...matrixData, emaSlow: parseFloat(e.target.value) || 21 })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Chu kỳ RSI:</span>
                      <input
                        type="number"
                        value={matrixData.rsiPeriod}
                        onChange={(e) => setMatrixData({ ...matrixData, rsiPeriod: parseFloat(e.target.value) || 14 })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Hệ số Chốt Lời TP (ATR):</span>
                      <input
                        type="number"
                        step="0.1"
                        value={matrixData.tpMult}
                        onChange={(e) => setMatrixData({ ...matrixData, tpMult: parseFloat(e.target.value) || 1.8 })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300">Hệ số Cắt Lỗ SL (ATR):</span>
                      <input
                        type="number"
                        step="0.1"
                        value={matrixData.slMult}
                        onChange={(e) => setMatrixData({ ...matrixData, slMult: parseFloat(e.target.value) || 1.2 })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>

                    {/* Min Confidence Threshold Setting */}
                    <div className="p-3 bg-cyan-950/20 border border-cyan-500/30 rounded-xl space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-300 font-bold flex items-center gap-1.5">
                          <span>🎯</span> Độ Tin Cậy Tối Thiểu Vào Lệnh:
                        </span>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="35"
                            max="95"
                            step="5"
                            value={matrixData.minConfidence ?? 35}
                            onChange={(e) => setMatrixData({ ...matrixData, minConfidence: parseInt(e.target.value) || 35 })}
                            className="w-16 bg-slate-950 border border-cyan-500/50 rounded-lg px-2 py-1 text-right text-cyan-400 font-mono font-bold text-xs focus:outline-none focus:border-cyan-400"
                          />
                          <span className="text-cyan-400 font-bold text-xs">%</span>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="35"
                        max="90"
                        step="5"
                        value={matrixData.minConfidence ?? 35}
                        onChange={(e) => setMatrixData({ ...matrixData, minConfidence: parseInt(e.target.value) || 35 })}
                        className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-slate-800 rounded-lg"
                      />
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                        <span>35% (Mặc định / Nhạy)</span>
                        <span className="text-cyan-300 font-semibold">60% (Cân bằng)</span>
                        <span>90% (Siêu chọn lọc)</span>
                      </div>
                      <p className="text-[10.5px] text-slate-400 italic">
                        💡 Chỉ khi các tín hiệu (EMA, RSI, MACD, Nến đảo chiều) đồng thuận đạt từ <strong className="text-cyan-300">{matrixData.minConfidence ?? 35}%</strong> trở lên mới kích hoạt mở lệnh và lưu vào lịch sử Backtest.
                      </p>
                    </div>

                    {/* SL Distance Filter */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={matrixData.slFilterEnable ?? true}
                            onChange={(e) => setMatrixData({ ...matrixData, slFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs">
                            🛡️ Lọc khoảng cách Cắt Lỗ (SL Filter)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${matrixData.slFilterEnable ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>
                          {matrixData.slFilterEnable ? "ĐANG BẬT" : "TẮT"}
                        </span>
                      </div>

                      {matrixData.slFilterEnable && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <span className="text-slate-400 text-[11px] block mb-1">SL Tối thiểu (Pips):</span>
                            <input
                              type="number"
                              min="5"
                              max="50"
                              step="5"
                              value={matrixData.slMin ?? 30.0}
                              onChange={(e) => setMatrixData({ ...matrixData, slMin: parseFloat(e.target.value) || 30.0 })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                          <div>
                            <span className="text-slate-400 text-[11px] block mb-1">SL Tối đa (Pips):</span>
                            <input
                              type="number"
                              min="50"
                              max="500"
                              step="10"
                              value={matrixData.slMax ?? 120.0}
                              onChange={(e) => setMatrixData({ ...matrixData, slMax: parseFloat(e.target.value) || 120.0 })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                        </div>
                      )}
                      <p className="text-[10.5px] text-slate-400 italic">
                        💡 Loại bỏ các nến biến động bất thường (bung nến tin tức / râu nến quá dài). Chỉ vào lệnh khi SL nằm trong khoảng <strong className="text-cyan-300">{matrixData.slMin ?? 10} - {matrixData.slMax ?? 150} pips</strong>.
                      </p>
                    </div>

                    {/* Cooldown Bars Filter (Giãn cách nến vào lệnh) */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-200 font-bold text-xs flex items-center gap-1.5">
                          <span>⏳</span> Giãn cách nến tối thiểu giữa 2 lệnh (Cooldown):
                        </span>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            min="1"
                            max="200"
                            step="1"
                            value={matrixData.cooldownBars ?? 25}
                            onChange={(e) => setMatrixData({ ...matrixData, cooldownBars: parseInt(e.target.value) || 25 })}
                            className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                          />
                          <span className="text-slate-400 text-xs">nến</span>
                        </div>
                      </div>
                      <p className="text-[10.5px] text-slate-400 italic">
                        💡 Khoảng cách tối thiểu <strong className="text-cyan-300">{matrixData.cooldownBars ?? 25} cây nến</strong> giữa 2 lệnh liên tiếp để chống mở lệnh dồn dập trong vùng tích lũy / nến nhiễu (Mặc định chuẩn M1: <strong className="text-amber-300">25 nến</strong>).
                      </p>
                    </div>

                    {/* Timeout Exit */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={matrixData.timeoutExitEnable !== false}
                            onChange={(e) => setMatrixData({ ...matrixData, timeoutExitEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs">
                            ⏱️ Đóng lệnh khi hết số nến tối đa (Timeout Exit)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${matrixData.timeoutExitEnable !== false ? "bg-amber-500/20 text-amber-300" : "bg-cyan-500/20 text-cyan-400"}`}>
                          {matrixData.timeoutExitEnable !== false ? "ĐANG BẬT" : "TẮT (CHẠY ĐẾN TP/SL)"}
                        </span>
                      </div>

                      {matrixData.timeoutExitEnable !== false ? (
                        <div className="pt-1">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-400 text-[11px]">Số cây nến giữ tối đa:</span>
                            <div className="flex items-center gap-1.5">
                              <input
                                type="number"
                                min="5"
                                max="300"
                                step="5"
                                value={matrixData.maxHoldingBars ?? 30}
                                onChange={(e) => setMatrixData({ ...matrixData, maxHoldingBars: parseInt(e.target.value) || 30 })}
                                className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                              />
                              <span className="text-slate-400 text-xs">nến</span>
                            </div>
                          </div>
                          <p className="text-[10.5px] text-amber-400/90 italic mt-1.5">
                            ⚡ <strong>Đang BẬT:</strong> Nếu sau <strong className="text-amber-300">{matrixData.maxHoldingBars ?? 30} nến</strong> lệnh chưa chạm TP hoặc SL, hệ thống sẽ tự động đóng lệnh tại giá thị trường để thu hồi vốn / chốt kết quả tại thời điểm đó.
                          </p>
                        </div>
                      ) : (
                        <p className="text-[10.5px] text-cyan-400/90 italic pt-1">
                          🎯 <strong>Đang TẮT (Chế độ thuần TP/SL):</strong> Lệnh sẽ tiếp tục chạy qua tất cả các cây nến tiếp theo cho đến khi chạm hẳn mốc <strong>Chốt Lời (TP)</strong> hoặc dính hẳn mốc <strong>Cắt Lỗ (SL)</strong> mới kết thúc.
                        </p>
                      )}
                    </div>

                    {/* Session Trading Time Filter */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={matrixData.sessionFilterEnable !== false}
                            onChange={(e) => setMatrixData({ ...matrixData, sessionFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs">
                            ⏰ Khung giờ mở lệnh an toàn (Tránh Gap / Giãn Spread)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${matrixData.sessionFilterEnable !== false ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>
                          {matrixData.sessionFilterEnable !== false ? "ĐANG BẬT" : "TẮT (CẢ NGÀY)"}
                        </span>
                      </div>

                      {matrixData.sessionFilterEnable !== false && (
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <span className="text-slate-400 text-[11px] block mb-1">Giờ bắt đầu phiên (ICT UTC+7):</span>
                            <input
                              type="text"
                              value={matrixData.sessionStart || "06:00"}
                              onChange={(e) => setMatrixData({ ...matrixData, sessionStart: e.target.value })}
                              placeholder="06:00"
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-center text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                          <div>
                            <span className="text-slate-400 text-[11px] block mb-1">Giờ kết thúc phiên (ICT UTC+7):</span>
                            <input
                              type="text"
                              value={matrixData.sessionEnd || "03:30"}
                              onChange={(e) => setMatrixData({ ...matrixData, sessionEnd: e.target.value })}
                              placeholder="03:30"
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-center text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                        </div>
                      )}
                      <p className="text-[10.5px] text-slate-400 italic">
                        💡 Chỉ mở lệnh trong khung giờ từ <strong className="text-cyan-300">{matrixData.sessionStart || "06:00"}</strong> đến <strong className="text-cyan-300">{matrixData.sessionEnd || "03:30"} sáng hôm sau</strong>. Tự động tránh khung giờ giao phiên (03:30 - 06:00) khi thị trường giãn spread mạnh và nhảy gap.
                      </p>
                    </div>

                    {/* Long-Term Dynamic EMA 200 Distance Hysteresis Filter (Mean Reversion) */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={matrixData.ema200DistFilterEnable !== false}
                            onChange={(e) => setMatrixData({ ...matrixData, ema200DistFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs">
                            🎯 Lọc Sóng Hồi Động EMA 200 (Mean Reversion)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${matrixData.ema200DistFilterEnable !== false ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>
                          {matrixData.ema200DistFilterEnable !== false ? "ĐANG BẬT" : "TẮT"}
                        </span>
                      </div>

                      {matrixData.ema200DistFilterEnable !== false ? (
                        <div className="space-y-2.5 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-300 font-medium">Khoảng cách Kích hoạt Sóng Hồi (Xa EMA 200):</span>
                            <div className="flex items-center gap-1.5 font-mono">
                              <input
                                type="number"
                                min="50"
                                max="1000"
                                step="10"
                                value={matrixData.ema200OuterPips ?? 200}
                                onChange={(e) => setMatrixData({ ...matrixData, ema200OuterPips: parseFloat(e.target.value) || 200 })}
                                className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-cyan-300 font-bold text-xs focus:outline-none focus:border-cyan-500"
                              />
                              <span className="text-slate-400 text-[11px]">pips</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-300 font-medium">Khoảng cách Hồi về Mở 2 chiều (Gần EMA 200):</span>
                            <div className="flex items-center gap-1.5 font-mono">
                              <input
                                type="number"
                                min="10"
                                max="500"
                                step="10"
                                value={matrixData.ema200InnerPips ?? 100}
                                onChange={(e) => setMatrixData({ ...matrixData, ema200InnerPips: parseFloat(e.target.value) || 100 })}
                                className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-300 font-bold text-xs focus:outline-none focus:border-emerald-500"
                              />
                              <span className="text-slate-400 text-[11px]">pips</span>
                            </div>
                          </div>

                          <p className="text-[10.5px] text-slate-400 italic leading-relaxed">
                            💡 <span className="text-slate-300 font-semibold">Gần EMA 200 (≤ {matrixData.ema200OuterPips ?? 200} pips):</span> Đánh 2 chiều BUY & SELL linh hoạt trong vùng trung tính.
                            <br />
                            🌊 <span className="text-rose-400 font-semibold">Vọt cao (≥ +{matrixData.ema200OuterPips ?? 200} pips / 20 giá):</span> CẤM BUY đu đỉnh, tự động khóa <strong>CHỈ SELL</strong> để bắt sóng hồi về EMA 200.
                            <br />
                            🌊 <span className="text-emerald-400 font-semibold">Rớt sâu (≤ -{matrixData.ema200OuterPips ?? 200} pips / 20 giá):</span> CẤM SELL bán tháo, tự động khóa <strong>CHỈ BUY</strong> để bắt sóng hồi lên EMA 200.
                            <br />
                            🔄 <span className="text-cyan-300 font-semibold">Hồi về (≤ {matrixData.ema200InnerPips ?? 100} pips):</span> Tự động giải phóng khóa, quay về đánh 2 chiều bình thường.
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between pt-1">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={matrixData.ema200FilterEnable === true}
                              onChange={(e) => setMatrixData({ ...matrixData, ema200FilterEnable: e.target.checked })}
                              className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                            />
                            <span className="text-slate-300 text-xs">Lọc cứng: Chỉ BUY trên EMA200 / Chỉ SELL dưới EMA200</span>
                          </label>
                          <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${matrixData.ema200FilterEnable ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-500"}`}>
                            {matrixData.ema200FilterEnable ? "BẬT" : "TẮT"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* CLASSICAL SMC PRO INDICATOR INPUTS (smc_structure) */}
              {indicatorId === "smc_structure" && (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-cyan-400 tracking-wider mb-2 block">
                      CẤU HÌNH CHỈ BÁO SMC PRO (ORDER BLOCKS, FVG & CẤU TRÚC)
                    </span>
                    <p className="text-[11px] text-slate-400 mb-3">
                      Tự động quét cấu trúc thị trường, nhận diện các vùng Khối Lệnh (Order Blocks), Vùng Mất Cân Bằng (FVG) và điểm Phá Vỡ Cấu Trúc (BOS / CHoCH).
                    </p>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-200 font-medium block">Độ dài Pivot Swing (Dò Đỉnh/Đáy):</span>
                        <span className="text-[10.5px] text-slate-400">Số nến hai bên để xác định Swing High/Low</span>
                      </div>
                      <input
                        type="number"
                        min="2"
                        max="15"
                        value={classicalSmcData.pivotLength || 5}
                        onChange={(e) => setClassicalSmcData({ ...classicalSmcData, pivotLength: parseInt(e.target.value) || 5 })}
                        className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-200 font-medium block">Số nến tìm nguồn OB (Lookback):</span>
                        <span className="text-[10.5px] text-slate-400">Quét ngược về trước điểm BOS để tìm nến OB</span>
                      </div>
                      <input
                        type="number"
                        min="5"
                        max="50"
                        value={classicalSmcData.obLookback || 20}
                        onChange={(e) => setClassicalSmcData({ ...classicalSmcData, obLookback: parseInt(e.target.value) || 20 })}
                        className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-slate-200 font-medium block">Điều kiện Lấp / Hóa giải (Mitigation):</span>
                        <span className="text-[10.5px] text-slate-400">Tiêu chuẩn xác định vùng OB / FVG bị hóa giải</span>
                      </div>
                      <select
                        value={classicalSmcData.mitigationMethod || "close"}
                        onChange={(e) => setClassicalSmcData({ ...classicalSmcData, mitigationMethod: e.target.value })}
                        className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-200 text-xs focus:outline-none focus:border-cyan-500 cursor-pointer"
                      >
                        <option value="close">🎯 Giá Đóng Cửa (Candle Close) — Chuẩn SMC</option>
                        <option value="wick">⚡ Râu Nến (Wick Touch) — Chạm râu</option>
                      </select>
                    </div>

                    <p className="text-[10.5px] text-cyan-300/90 italic bg-cyan-950/30 p-2.5 rounded-lg border border-cyan-800/40">
                      {classicalSmcData.mitigationMethod === "wick"
                        ? "⚡ Chế độ Râu Nến: Chỉ cần râu nến chạm vào mép OB / FVG là tính vùng đó đã bị hóa giải (Mitigated)."
                        : "🎯 Chế độ Giá Đóng Cửa (Chuẩn SMC): Vùng OB / FVG chỉ bị hóa giải khi có thân nến đóng cửa vượt qua hoàn toàn vùng (Rút râu quét thanh khoản vẫn giữ nguyên vùng)."}
                    </p>

                    {/* Feature Toggles for SMC Components */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5 mt-3">
                      <span className="text-xs font-bold text-slate-200 block">Các thành phần hiển thị trên biểu đồ:</span>
                      
                      <div className="space-y-2">
                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-300 font-medium">Khối Lệnh Order Blocks (+OB & -OB)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.showOB !== false}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, showOB: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>

                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-400 pl-4">└ Ẩn Order Blocks đã bị hóa giải (Mitigated OB)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.hideMitigatedOB === true}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, hideMitigatedOB: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>

                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-300 font-medium">Vùng Mất Cân Bằng Giá (Fair Value Gaps - FVG)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.showFVG !== false}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, showFVG: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>

                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-400 pl-4">└ Ẩn FVG đã được lấp kín (Mitigated FVG)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.hideMitigatedFVG === true}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, hideMitigatedFVG: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>

                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-300 font-medium">Đường Phá Vỡ Cấu Trúc (Break of Structure - BOS)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.showBOS !== false}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, showBOS: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>

                        <label className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <span className="text-xs text-slate-300 font-medium">Đường Thay Đổi Tính Chất (Change of Character - CHoCH)</span>
                          <input
                            type="checkbox"
                            checked={classicalSmcData.showCHoCH !== false}
                            onChange={(e) => setClassicalSmcData({ ...classicalSmcData, showCHoCH: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* MSB DYNAMIC TRADING SYSTEM INPUTS (msb_dynamic) */}
              {indicatorId === "msb_dynamic" && (
                <>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-amber-400 tracking-wider mb-2 block">
                      CẤU HÌNH HỆ THỐNG MSB DYNAMIC + 2TP (PINE SCRIPT V5)
                    </span>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {SMC_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActivePreset(preset.id);
                            setSmcData((prev) => ({ ...prev, ...preset.params }));
                          }}
                          className={`p-2.5 rounded-xl text-left border transition cursor-pointer ${
                            activePreset === preset.id
                              ? "bg-amber-500/15 border-amber-400 text-amber-300 font-bold shadow-sm"
                              : "bg-slate-950/50 border-slate-800 hover:bg-slate-950 hover:border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="text-xs font-bold block">{preset.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5 line-clamp-1">
                            {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    {/* Only Admin can see and change Anchor (windowBars) and Pivot (lenPH, lenPL) */}
                    {isAdmin && (
                      <div className="p-3 bg-slate-900/60 border border-slate-800 rounded-xl space-y-3">
                        <div className="flex items-center justify-between pb-1 border-b border-slate-800/60">
                          <span className="text-[11px] font-bold text-amber-400">⚙️ Cấu Hình Thuật Toán Cực Trị (Admin Only)</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono font-bold">ADMIN</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-300 text-xs">Cửa sổ cực trị dò Anchor (windowBars):</span>
                          <input
                            type="number"
                            min="50"
                            max="400"
                            value={smcData.windowBars || 100}
                            onChange={(e) => setSmcData({ ...smcData, windowBars: parseInt(e.target.value) || 100 })}
                            className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-slate-300 text-xs">Pivot High / Low Dò MID & SAME:</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-slate-400">PH:</span>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={smcData.lenPH || 3}
                              onChange={(e) => setSmcData({ ...smcData, lenPH: parseInt(e.target.value) || 3 })}
                              className="w-14 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500"
                            />
                            <span className="text-[10px] text-slate-400">PL:</span>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              value={smcData.lenPL || 3}
                              onChange={(e) => setSmcData({ ...smcData, lenPL: parseInt(e.target.value) || 3 })}
                              className="w-14 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-amber-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 2 TP Levels */}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-xs">2 Mức Chốt Lời (TP Ratios R1, R2):</span>
                      <div className="flex items-center gap-2 font-mono text-xs">
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-400">TP1:</span>
                          <input
                            type="number"
                            step="0.1"
                            min="0.5"
                            max="5.0"
                            value={smcData.r1 || 1.5}
                            onChange={(e) => setSmcData({ ...smcData, r1: parseFloat(e.target.value) || 1.5 })}
                            className="w-14 bg-slate-950 border border-slate-700 rounded-lg px-1.5 py-1 text-right text-emerald-400 font-bold"
                            title="TP1 Ratio (Chốt chuẩn)"
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-slate-400">TP2:</span>
                          <input
                            type="number"
                            step="0.1"
                            min="1.0"
                            max="10.0"
                            value={smcData.r2 || 2.0}
                            onChange={(e) => setSmcData({ ...smcData, r2: parseFloat(e.target.value) || 2.0 })}
                            className="w-14 bg-slate-950 border border-slate-700 rounded-lg px-1.5 py-1 text-right text-emerald-300 font-bold"
                            title="TP2 Ratio (Mục tiêu mở rộng)"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Entry Mode */}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-xs font-medium">Cách vào lệnh (Entry Mode):</span>
                      <select
                        value={
                          smcData.entryMode === "Smart Hybrid" || smcData.entryMode === "SMART_HYBRID"
                            ? "Smart Hybrid"
                            : smcData.entryMode === "Limit 2" || smcData.entryMode === "Half Mid-Same" || smcData.entryMode === "Mid-Same"
                            ? "Limit 2"
                            : "Limit 1"
                        }
                        onChange={(e) => setSmcData({ ...smcData, entryMode: e.target.value })}
                        className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 text-xs focus:outline-none focus:border-amber-500 font-medium cursor-pointer"
                      >
                        <option value="Smart Hybrid">🤖 Smart Hybrid (Market nếu gần SL, Limit 50% nếu xa)</option>
                        <option value="Limit 1">🎯 Limit 1 (Phiên Mỹ FVG + Mid-Same 50%)</option>
                        <option value="Limit 2">📊 Limit 2 (Mid-Same 50%)</option>
                      </select>
                    </div>

                    {/* Smart Hybrid Threshold & Pullback Config */}
                    {(smcData.entryMode === "Smart Hybrid" || smcData.entryMode === "SMART_HYBRID") && (
                      <div className="p-2.5 bg-amber-950/20 border border-amber-500/30 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-amber-300 text-xs font-semibold block">Ngưỡng SL vào Market:</span>
                            <span className="text-[10px] text-slate-400">Nếu SL &le; ngưỡng thì vào thẳng Market</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="20"
                              max="300"
                              step="5"
                              value={smcData.smcHybridThresholdPips ?? 80.0}
                              onChange={(e) => setSmcData({ ...smcData, smcHybridThresholdPips: parseFloat(e.target.value) || 80.0 })}
                              className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-right text-amber-300 focus:outline-none focus:border-amber-500"
                            />
                            <span className="text-[10px] text-slate-400">pips</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-slate-300 text-xs font-semibold block">Pullback Limit khi SL &gt; ngưỡng:</span>
                            <span className="text-[10px] text-slate-400">Kê Limit hồi % vùng Breakout để an toàn</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="10"
                              max="90"
                              step="5"
                              value={smcData.smcHybridPullbackPct ?? 50.0}
                              onChange={(e) => setSmcData({ ...smcData, smcHybridPullbackPct: parseFloat(e.target.value) || 50.0 })}
                              className="w-16 bg-slate-900 border border-slate-700 rounded px-2 py-0.5 text-right text-amber-300 focus:outline-none focus:border-amber-500"
                            />
                            <span className="text-[10px] text-slate-400">%</span>
                          </div>
                        </div>

                        <p className="text-[10.5px] text-amber-200/90 italic pt-0.5 border-t border-amber-900/40">
                          💡 <strong>Hiệu quả thực tế:</strong> Khắc phục lỗi hụt TP oan của SMC truyền thống khi giá bứt phá mạnh, giúp hệ thống đạt hiệu suất tối ưu trên Vàng M1 (+2,100+ pips/tháng).
                        </p>
                      </div>
                    )}

                    {/* SL Range Filter in Pips */}
                    <div className="flex items-center justify-between">
                      <span className="text-slate-300 text-xs">Khoảng cách SL cho phép (Min - Max Pips):</span>
                      <div className="flex items-center gap-1.5 font-mono text-xs">
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="500"
                          value={smcData.slMin || 30.0}
                          onChange={(e) => setSmcData({ ...smcData, slMin: parseFloat(e.target.value) || 30.0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-1.5 py-1 text-right text-slate-100"
                          title="SL Min (Pips)"
                        />
                        <span className="text-slate-500">-</span>
                        <input
                          type="number"
                          step="1"
                          min="5"
                          max="1000"
                          value={smcData.slMax || 100.0}
                          onChange={(e) => setSmcData({ ...smcData, slMax: parseFloat(e.target.value) || 100.0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-1.5 py-1 text-right text-slate-100"
                          title="SL Max (Pips)"
                        />
                        <span className="text-[10px] text-slate-400 font-sans">pips</span>
                      </div>
                    </div>

                    {/* SL Extra Buffer & Timeout */}
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div className="p-2 rounded-lg bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-[11px] text-slate-300">Buffer SL từ SAME:</span>
                        <div className="flex items-center gap-1 font-mono text-xs">
                          <input
                            type="number"
                            min="0"
                            max="50"
                            step="0.5"
                            value={smcData.slExtraVal || 15}
                            onChange={(e) => setSmcData({ ...smcData, slExtraVal: parseFloat(e.target.value) || 15 })}
                            className="w-12 bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-right text-slate-100"
                          />
                          <span className="text-[10px] text-slate-400 font-sans">pips</span>
                        </div>
                      </div>
                      <div className="p-2 rounded-lg bg-slate-950/70 border border-slate-800 flex items-center justify-between">
                        <span className="text-[11px] text-slate-300">Timeout hủy Limit (Nến):</span>
                        <input
                          type="number"
                          min="5"
                          max="200"
                          value={smcData.pendingCancelN || 40}
                          onChange={(e) => setSmcData({ ...smcData, pendingCancelN: parseInt(e.target.value) || 40 })}
                          className="w-14 bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-right text-slate-100 font-mono text-xs"
                        />
                      </div>
                    </div>

                    {/* Fakeout Revert on Stop Loss (Đảo Chiều Khi Dính SL) */}
                    <div className="p-3 bg-gradient-to-r from-orange-950/30 to-slate-950/70 border border-orange-500/30 rounded-xl space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-orange-300 font-bold text-xs flex items-center gap-1.5">
                            <span>🔄</span> Đảo Chiều Fakeout Khi Dính SL (Fakeout Revert Strategy)
                          </span>
                          <span className="text-[11px] text-slate-400 block mt-0.5">
                            Tự động kích hoạt lệnh đảo chiều ngược lại khi cấu trúc SMC bị phá vỡ quét SL
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSmcData({ ...smcData, smcRevertEnable: !smcData.smcRevertEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            smcData.smcRevertEnable
                              ? "bg-orange-500/25 text-orange-300 border border-orange-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {smcData.smcRevertEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {smcData.smcRevertEnable && (
                        <div className="space-y-3 pt-2 border-t border-slate-800">
                          <div>
                            <span className="text-slate-300 text-xs font-semibold block mb-1">Kiểu Vào Lệnh Revert (Entry Mode):</span>
                            <select
                              value={smcData.smcRevertEntryMode || "MARKET"}
                              onChange={(e) => setSmcData({ ...smcData, smcRevertEntryMode: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                            >
                              <option value="MARKET">⚡ MARKET: Vào Lệnh Market Ngay Khi Quét SL</option>
                              <option value="LIMIT_MID">🛡️ LIMIT_MID: Limit Hồi 50% Vùng Entry - SL (Retest Invalidation)</option>
                              <option value="LIMIT_ENTRY">🎯 LIMIT_ENTRY: Limit Tại Entry Cấu Trúc Gốc (Retest Entry)</option>
                              <option value="SMART_HYBRID">🤖 SMART_HYBRID: Thông minh (Market nếu gần SL, Limit 50% nếu xa)</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-3 gap-2.5">
                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">Target RR Revert:</span>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-orange-400 font-bold">1:</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  max="50"
                                  value={smcData.smcRevertRr ?? 1.5}
                                  onChange={(e) => setSmcData({ ...smcData, smcRevertRr: parseFloat(e.target.value) || 1.5 })}
                                  className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                                />
                              </div>
                            </div>

                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">Số Nến Hủy Limit:</span>
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="200"
                                value={smcData.smcRevertCancelBars ?? 20}
                                onChange={(e) => setSmcData({ ...smcData, smcRevertCancelBars: parseInt(e.target.value) || 20 })}
                                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                              />
                            </div>

                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">SL Buffer (pips):</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                max="500"
                                value={smcData.smcRevertSlBufferPips ?? 10.0}
                                onChange={(e) => setSmcData({ ...smcData, smcRevertSlBufferPips: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Single Order / Chống Nhồi Lệnh Liên Tục */}
                    <div className="p-3 bg-gradient-to-r from-cyan-950/30 to-slate-950/70 border border-cyan-500/30 rounded-xl space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-cyan-300 font-bold text-xs flex items-center gap-1.5">
                            <span>🛡️</span> Quản Lý Lệnh Đồng Thời (Chống Nhồi Lệnh Liên Tục)
                          </span>
                          <span className="text-[11px] text-slate-400 block mt-0.5">
                            Khi lệnh trước đó chưa chạm TP hoặc SL, chỉ gồng lệnh đó, cấm vào thêm lệnh cùng chiều
                          </span>
                        </div>
                        <span className="px-2 py-0.5 text-xs font-bold rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          {smcData.smcMaxOrdersMode === "UNLIMITED"
                            ? "NHỒI LỆNH TỰ DO"
                            : smcData.smcMaxOrdersMode === "ONE_TOTAL"
                            ? "1 LỆNH DUY NHẤT"
                            : "1 LỆNH / CHIỀU"}
                        </span>
                      </div>

                      <div className="space-y-1.5 pt-1 border-t border-slate-800">
                        <span className="text-slate-300 text-xs font-semibold block">Chế độ giới hạn lệnh:</span>
                        <select
                          value={smcData.smcMaxOrdersMode || "ONE_PER_SIDE"}
                          onChange={(e) => setSmcData({ 
                            ...smcData, 
                            smcMaxOrdersMode: e.target.value,
                            smcSingleOrderPerSide: e.target.value !== "UNLIMITED"
                          })}
                          className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-medium focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        >
                          <option value="ONE_PER_SIDE">🛡️ 1 Lệnh / Chiều (Mặc định: Chưa TP chỉ gồng lệnh, cấm nhồi thêm cùng chiều)</option>
                          <option value="ONE_TOTAL">🔒 1 Lệnh Duy Nhất Toàn Bộ (Đang có BUY hoặc SELL thì không vào bất kỳ lệnh nào khác)</option>
                          <option value="UNLIMITED">⚡ Nhồi Lệnh Tự Do (Mở lệnh liên tục mỗi khi cấu trúc bị phá vỡ)</option>
                        </select>
                      </div>
                    </div>

                    {/* Breakeven Protection (Dời SL về hòa vốn khi có lãi) */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={smcData.breakevenEnable === true}
                            onChange={(e) => setSmcData({ ...smcData, breakevenEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs flex items-center gap-1.5">
                            <span>🎯</span> Tự Động Dời SL Về Hòa Vốn (Breakeven Protection)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${smcData.breakevenEnable === true ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-800 text-slate-500"}`}>
                          {smcData.breakevenEnable === true ? "ĐANG BẬT" : "TẮT"}
                        </span>
                      </div>

                      {smcData.breakevenEnable === true && (
                        <div className="flex items-center justify-between pt-1 border-t border-slate-800/60">
                          <div>
                            <span className="text-slate-300 text-xs block font-medium">Khoảng lãi kích hoạt dời SL về Entry:</span>
                            <span className="text-[10.5px] text-slate-400">Khi giá đạt mức lãi này, SL tự động dời về Entry để bảo toàn vốn</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="10"
                              max="500"
                              step="10"
                              value={smcData.breakevenPips ?? 100.0}
                              onChange={(e) => setSmcData({ ...smcData, breakevenPips: parseFloat(e.target.value) || 100.0 })}
                              className="w-16 bg-slate-900 border border-slate-700 rounded-lg px-2 py-0.5 text-right text-cyan-300 focus:outline-none focus:border-cyan-500"
                            />
                            <span className="text-slate-400 text-xs">pips</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Multi-Timeframe HTF Order Block Confirmation Filter */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={smcData.htfFilterEnable === true}
                            onChange={(e) => setSmcData({ ...smcData, htfFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs">
                            🛡️ Lọc theo Vùng Khối Lệnh Khung Lớn (HTF Order Block)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${smcData.htfFilterEnable ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-500"}`}>
                          {smcData.htfFilterEnable ? "ĐANG BẬT" : "TẮT (CHẠY ĐƠN ĐỘC)"}
                        </span>
                      </div>

                      {smcData.htfFilterEnable && (
                        <div className="space-y-2.5 pt-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-slate-300 text-xs block font-medium">Khung thời gian bảo hộ (HTF):</span>
                              <span className="text-[10.5px] text-slate-400">Chọn khung quét Order Block cản lớn</span>
                            </div>
                            <select
                              value={smcData.htfTimeframe || "AUTO"}
                              onChange={(e) => setSmcData({ ...smcData, htfTimeframe: e.target.value })}
                              className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 text-xs focus:outline-none focus:border-amber-500 cursor-pointer"
                            >
                              <option value="AUTO">🎯 Tự động theo khung hiện tại (Auto)</option>
                              <option value="M15">⚡ Khung M15 OB (Khuyên dùng cho M1)</option>
                              <option value="H1">📊 Khung H1 OB (Khuyên dùng cho M5)</option>
                              <option value="H4">🌊 Khung H4 OB (Khuyên dùng cho M5/M15)</option>
                              <option value="D1">🏛️ Khung D1 OB (Khuyên dùng cho H1)</option>
                            </select>
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-slate-300 text-xs">Vùng đệm tiếp xúc HTF OB (Buffer):</span>
                            <div className="flex items-center gap-1 font-mono text-xs">
                              <input
                                type="number"
                                min="0"
                                max="50"
                                step="1"
                                value={smcData.htfBufferPips ?? 10.0}
                                onChange={(e) => setSmcData({ ...smcData, htfBufferPips: parseFloat(e.target.value) || 10.0 })}
                                className="w-16 bg-slate-900 border border-slate-700 rounded-lg px-2 py-0.5 text-right text-amber-300 focus:outline-none focus:border-amber-500"
                              />
                              <span className="text-slate-400 text-xs">pips</span>
                            </div>
                          </div>

                          {/* HTF Mode Selector */}
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-slate-300 text-xs block font-medium">Chế độ xác nhận HTF OB:</span>
                              <span className="text-[10.5px] text-slate-400">
                                {(smcData.htfMode || "touch") === "touch"
                                  ? "Giá chỉ cần từng chạm OB trong 24 nến trước MSB"
                                  : "Entry price phải đang NẰM TRONG vùng OB khi MSB xảy ra"}
                              </span>
                            </div>
                            <select
                              value={smcData.htfMode || "touch"}
                              onChange={(e) => setSmcData({ ...smcData, htfMode: e.target.value })}
                              className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 text-xs focus:outline-none focus:border-amber-500 cursor-pointer"
                            >
                              <option value="touch">👆 Touch — Chạm OB trong 24 nến (Lỏng)</option>
                              <option value="price_in_ob">🎯 Price In OB — Entry nằm trong OB (Chặt, SMC)</option>
                            </select>
                          </div>

                          <p className="text-[10.5px] text-amber-300/90 italic bg-amber-950/20 p-2 rounded-lg border border-amber-800/30 mt-1">
                            {(smcData.htfMode || "touch") === "price_in_ob"
                              ? "🎯 Strict SMC: Lệnh chỉ vào khi entry price đang NẰM TRONG vùng HTF OB tại lúc MSB — đúng nhất về lý thuyết, ít lệnh hơn nhưng chất hơn."
                              : "💡 Touch Mode: Lệnh vào khi giá đã chạm qua HTF OB trong 24 nến trước MSB — nhiều lệnh hơn, lọc bẫy lưng chừng sóng cơ bản."}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* FVG Breakout Candle Filter (Lọc Nến Breakout Phải Tạo FVG Imbalance) */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={smcData.fvgFilterEnable === true}
                            onChange={(e) => setSmcData({ ...smcData, fvgFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0 cursor-pointer"
                          />
                          <span className="text-slate-200 font-bold text-xs flex items-center gap-1.5">
                            <span>✨</span> Lọc Nến Breakout Phải Tạo FVG (Fair Value Gap Filter)
                          </span>
                        </label>
                        <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${smcData.fvgFilterEnable === true ? "bg-amber-500/20 text-amber-300" : "bg-slate-800 text-slate-500"}`}>
                          {smcData.fvgFilterEnable === true ? "ĐANG BẬT" : "TẮT (MỌI BREAKOUT)"}
                        </span>
                      </div>
                      <p className="text-[10.5px] text-slate-400 italic">
                        💡 <strong>Khi BẬT:</strong> Hệ thống chỉ mở lệnh khi cây nến phá vỡ MSB (Breakout Candle) tạo ra khoảng trống mất cân bằng <strong>FVG (Fair Value Gap)</strong> theo nguyên lý ICT 3 nến. Giúp lọc sạch các pha phá vỡ yếu ớt, thiếu động lượng hoặc phá vỡ giả.
                      </p>
                    </div>

                    {/* MSB Structure Visibility Toggles */}
                    <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5 mt-2">
                      <span className="text-xs font-bold text-slate-200 block">Các thành phần cấu trúc hiển thị:</span>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <input
                            type="checkbox"
                            checked={smcData.showMSBLines !== false}
                            onChange={(e) => setSmcData({ ...smcData, showMSBLines: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <span className="text-[11px] text-slate-300">Đường Anchor / Mid / Same</span>
                        </label>
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <input
                            type="checkbox"
                            checked={smcData.showTP !== false}
                            onChange={(e) => setSmcData({ ...smcData, showTP: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <span className="text-[11px] text-slate-300">Đường Chốt Lời (TP1 & TP2)</span>
                        </label>
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <input
                            type="checkbox"
                            checked={smcData.showSL !== false}
                            onChange={(e) => setSmcData({ ...smcData, showSL: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <span className="text-[11px] text-slate-300">Đường Cắt Lỗ (SL)</span>
                        </label>
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 cursor-pointer hover:border-slate-700">
                          <input
                            type="checkbox"
                            checked={smcStyle.showLabels !== false}
                            onChange={(e) => setSmcStyle({ ...smcStyle, showLabels: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <span className="text-[11px] text-slate-300">Hiện nhãn mức giá (Labels)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ICHIMOKU CLOUD INPUTS */}
              {indicatorId === "ichimoku_cloud" && (
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Chiều dài Đường Chuyển Đổi (Tenkan-sen):</span>
                    <input
                      type="number"
                      value={ichimokuData.tenkan}
                      onChange={(e) => setIchimokuData({ ...ichimokuData, tenkan: parseInt(e.target.value) || 9 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Chiều dài Đường Cơ Sở (Kijun-sen):</span>
                    <input
                      type="number"
                      value={ichimokuData.kijun}
                      onChange={(e) => setIchimokuData({ ...ichimokuData, kijun: parseInt(e.target.value) || 26 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Khoảng thời gian cốt lõi (Senkou Span B):</span>
                    <input
                      type="number"
                      value={ichimokuData.senkouB}
                      onChange={(e) => setIchimokuData({ ...ichimokuData, senkouB: parseInt(e.target.value) || 52 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Độ dịch chuyển Kumo (Displacement):</span>
                    <input
                      type="number"
                      value={ichimokuData.displacement}
                      onChange={(e) => setIchimokuData({ ...ichimokuData, displacement: parseInt(e.target.value) || 26 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
              )}

              {/* MULTI EMA INPUTS */}
              {indicatorId === "multi_ema_ribbon" && (
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">EMA 1 (Ngắn hạn):</span>
                    <input
                      type="number"
                      value={multiEmaData.ema1}
                      onChange={(e) => setMultiEmaData({ ...multiEmaData, ema1: parseInt(e.target.value) || 34 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">EMA 2 (Trung hạn):</span>
                    <input
                      type="number"
                      value={multiEmaData.ema2}
                      onChange={(e) => setMultiEmaData({ ...multiEmaData, ema2: parseInt(e.target.value) || 89 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">EMA 3 (Dài hạn):</span>
                    <input
                      type="number"
                      value={multiEmaData.ema3}
                      onChange={(e) => setMultiEmaData({ ...multiEmaData, ema3: parseInt(e.target.value) || 200 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">EMA 4 (Siêu chu kỳ):</span>
                    <input
                      type="number"
                      value={multiEmaData.ema4}
                      onChange={(e) => setMultiEmaData({ ...multiEmaData, ema4: parseInt(e.target.value) || 610 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                </div>
              )}

              {/* BOLLINGER BANDS INPUTS */}
              {indicatorId === "bollinger_bands" && (
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Chu kỳ (Period):</span>
                    <input
                      type="number"
                      value={bbData.period}
                      onChange={(e) => setBbData({ ...bbData, period: parseInt(e.target.value) || 20 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Độ lệch chuẩn (StdDev):</span>
                    <input
                      type="number"
                      step="0.1"
                      value={bbData.stdDev}
                      onChange={(e) => setBbData({ ...bbData, stdDev: parseFloat(e.target.value) || 2.0 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                </div>
              )}

              {/* MACD INPUTS */}
              {indicatorId === "macd_oscillator" && (
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Chu kỳ Nhanh (Fast Length):</span>
                    <input
                      type="number"
                      value={macdData.fast}
                      onChange={(e) => setMacdData({ ...macdData, fast: parseInt(e.target.value) || 12 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Chu kỳ Chậm (Slow Length):</span>
                    <input
                      type="number"
                      value={macdData.slow}
                      onChange={(e) => setMacdData({ ...macdData, slow: parseInt(e.target.value) || 26 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-300">Đường Tín Hiệu (Signal Smoothing):</span>
                    <input
                      type="number"
                      value={macdData.signal}
                      onChange={(e) => setMacdData({ ...macdData, signal: parseInt(e.target.value) || 9 })}
                      className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                    />
                  </div>
                </div>
              )}
            

              {/* HMA INDICATOR (hma_indicator) */}
              {indicatorId === "hma_indicator" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                    <span className="text-xs font-semibold text-slate-200">Chu kỳ HMA (Period)</span>
                    <input
                      type="number"
                      min="2"
                      max="500"
                      value={hmaData.period || 21}
                      onChange={(e) => setHmaData({ ...hmaData, period: parseInt(e.target.value) || 21 })}
                      className="w-24 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-100 text-xs text-right focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>
              )}

{/* HMA BREAKOUT SYSTEM INPUTS (hma_breakout_system) */}
              {indicatorId === "hma_breakout_system" && (
                <>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-cyan-400 tracking-wider mb-2 block">
                      CẤU HÌNH HỆ THỐNG HMA BREAKOUT PRO (SWING REVERSAL)
                    </span>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {HMA_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActivePreset(preset.id);
                            setHmaSystemData((prev) => ({ ...prev, ...preset.params }));
                          }}
                          className={`p-2.5 rounded-xl text-left border transition cursor-pointer ${
                            activePreset === preset.id
                              ? "bg-cyan-500/15 border-cyan-400 text-cyan-300 font-bold shadow-sm"
                              : "bg-slate-950/50 border-slate-800 hover:bg-slate-950 hover:border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="text-xs font-bold block">{preset.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5 line-clamp-1">
                            {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">

                    {/* HMA Period */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Chu kỳ Đường HMA (Period):</span>
                        <span className="text-[10.5px] text-slate-400">Độ mượt đường HMA (M1 khuyên dùng: 21 hoặc 24, Swing: 55)</span>
                      </div>
                      <input
                        type="number"
                        min="5"
                        max="500"
                        value={hmaSystemData.period ?? hmaSystemData.hmaPeriod ?? 21}
                        onChange={(e) => {
                          const val = Math.max(2, parseInt(e.target.value) || 21);
                          setHmaSystemData({ ...hmaSystemData, period: val, hmaPeriod: val });
                        }}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-cyan-400 font-mono text-xs font-bold"
                      />
                    </div>


                    {/* Lookback Window */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Số nến quét tìm đỉnh / đáy (Lookback Window):</span>
                        <span className="text-[10.5px] text-slate-400">Quét tìm đáy thấp nhất / đỉnh cao nhất trong N nến (Khuyên dùng: 100 - 200)</span>
                      </div>
                      <input
                        type="number"
                        min="20"
                        max="500"
                        value={hmaSystemData.lookbackBars || 100}
                        onChange={(e) => setHmaSystemData({ ...hmaSystemData, lookbackBars: Math.max(20, parseInt(e.target.value) || 100) })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-amber-300 font-mono text-xs font-bold"
                      />
                    </div>

                    {/* Max Risk Pips */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Khoảng cách SL tối đa (Max Risk Pips):</span>
                        <span className="text-[10.5px] text-slate-400">Bỏ qua nến giật râu làm SL quá xa (Mặc định: 80 pips = 8 giá)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          min="20"
                          max="300"
                          step="5"
                          value={hmaSystemData.maxRiskPips ?? 80.0}
                          onChange={(e) => setHmaSystemData({ ...hmaSystemData, maxRiskPips: Math.max(10, parseFloat(e.target.value) || 80.0) })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-amber-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">pips</span>
                      </div>
                    </div>

                    {/* Max Bars From Extreme */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Khoảng cách nến tối đa (Max Bars From Extreme):</span>
                        <span className="text-[10.5px] text-slate-400">Số nến tối đa từ nến đỉnh/đáy đến cây nến phá vỡ (Mặc định: 35)</span>
                      </div>
                      <input
                        type="number"
                        min="5"
                        max="100"
                        value={hmaSystemData.maxBarsFromExtreme || 35}
                        onChange={(e) => setHmaSystemData({ ...hmaSystemData, maxBarsFromExtreme: Math.max(5, parseInt(e.target.value) || 35) })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-cyan-400 font-mono text-xs font-bold"
                      />
                    </div>

                    {/* TP Ratio */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Mức Chốt Lời Mục Tiêu (TP Ratio R:R):</span>
                        <span className="text-[10.5px] text-slate-400">Tỷ lệ Risk:Reward cho lệnh chốt lời (Target R:R)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="10.0"
                          value={hmaSystemData.r1 || 1.5}
                          onChange={(e) => setHmaSystemData({ ...hmaSystemData, r1: parseFloat(e.target.value) || 1.5 })}
                          className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">R</span>
                      </div>
                    </div>

                    {/* SL Buffer Mode & Parameters */}
                    <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-slate-200 text-xs font-semibold block">Khoảng đệm Cắt Lỗ SL (Buffer):</span>
                          <span className="text-[10.5px] text-slate-400">Tự động điều chỉnh theo ATR (5 - 30 pips) hoặc cố định</span>
                        </div>
                        <select
                          value={hmaSystemData.slBufferMode || "ATR"}
                          onChange={(e) => setHmaSystemData({ ...hmaSystemData, slBufferMode: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-cyan-300 font-semibold focus:outline-none"
                        >
                          <option value="ATR">⚡ Tự động theo ATR (5-30p)</option>
                          <option value="FIXED">🔧 Thủ công Cố định (Fixed)</option>
                        </select>
                      </div>

                      {hmaSystemData.slBufferMode === "FIXED" && (
                        <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 font-mono text-xs">
                          <span className="text-slate-400 text-[11px]">SL Buffer Cố định:</span>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="50"
                              value={hmaSystemData.slBufferPips ?? 20.0}
                              onChange={(e) => setHmaSystemData({ ...hmaSystemData, slBufferPips: parseFloat(e.target.value) || 0 })}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-rose-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">pips</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Cooldown Bars */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Số nến giãn cách tín hiệu (Cooldown Bars):</span>
                        <span className="text-[10.5px] text-slate-400">Khoảng nghỉ tối thiểu giữa 2 lần vào lệnh</span>
                      </div>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={hmaSystemData.hmaCooldownBars ?? hmaSystemData.cooldownBars ?? 5}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 5;
                          setHmaSystemData({ ...hmaSystemData, hmaCooldownBars: val, cooldownBars: val });
                        }}
                        className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                      />
                    </div>

                    {/* ===================== EARLY BREAK-EVEN LOCK (TÙY CHỌN DỜI BE SỚM) ===================== */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-emerald-500/30 space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-bold text-xs flex items-center gap-1.5">
                            🛡️ Dời SL Về Hòa Vốn Khi Đạt % TP (Early Break-Even Lock)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setHmaSystemData({ ...hmaSystemData, dynamicBeEnable: !hmaSystemData.dynamicBeEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            hmaSystemData.dynamicBeEnable
                              ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {hmaSystemData.dynamicBeEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {hmaSystemData.dynamicBeEnable
                          ? `Bật: Khi giá đi được ${hmaSystemData.beTriggerPct || 75}% quãng đường tới TP, dời SL khóa +${hmaSystemData.beLockPips ?? 5} pips (nếu lệnh có TP nhỏ hơn mức này sẽ tự động fallback về BE +5 pips an toàn).`
                          : "Tắt: Giữ nguyên mức SL ban đầu cho đến khi chạm TP hoặc dính SL."}
                      </p>

                      <div className={`grid grid-cols-2 gap-3 pt-1 transition-opacity ${hmaSystemData.dynamicBeEnable ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                        {/* % TP Trigger */}
                        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Ngưỡng kích hoạt (% TP):</span>
                            <span className="text-[10px] text-slate-400">% quãng đường tới TP</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="30"
                              max="95"
                              step="5"
                              disabled={!hmaSystemData.dynamicBeEnable}
                              value={hmaSystemData.beTriggerPct ?? 75}
                              onChange={(e) => setHmaSystemData({ ...hmaSystemData, beTriggerPct: parseFloat(e.target.value) || 75 })}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">%</span>
                          </div>
                        </div>

                        {/* Lock Pips */}
                        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Pips khóa lời (BE+):</span>
                            <span className="text-[10px] text-slate-400">Nếu vượt TP tự auto về BE +5p</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="0"
                              max="300"
                              step="5"
                              disabled={!hmaSystemData.dynamicBeEnable}
                              value={hmaSystemData.beLockPips ?? 5}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setHmaSystemData({ ...hmaSystemData, beLockPips: isNaN(val) ? 0 : Math.max(val, 0) });
                              }}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">pips</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ===================== CẮT LỆNH NGƯỢC CHIỀU (CLOSE ON OPPOSITE) ===================== */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-amber-500/30 space-y-2 mt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-amber-400 font-bold text-xs flex items-center gap-1.5">
                            🔄 Cắt Lệnh Khi Xuất Hiện Lệnh Ngược Chiều (Close on Opposite)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setHmaSystemData({ ...hmaSystemData, closeOnOpposite: !Boolean(hmaSystemData.closeOnOpposite !== false) })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            hmaSystemData.closeOnOpposite !== false
                              ? "bg-amber-500/25 text-amber-300 border border-amber-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {hmaSystemData.closeOnOpposite !== false ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {hmaSystemData.closeOnOpposite !== false
                          ? "Bật: Đang có lệnh SELL mà xuất hiện nến phá vỡ BUY (hoặc ngược lại) thì tự động đóng hết lệnh cũ để vào theo chiều mới, tránh giữ lệnh đối ứng làm triệt tiêu lợi nhuận."
                          : "Tắt: Giữ lệnh cũ chạy độc lập đến khi chạm TP hoặc dính SL, cho phép đồng thời lệnh BUY và SELL."}
                      </p>
                    </div>

                    {/* ===================== TRADING SESSION & HOURS FILTER ===================== */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-cyan-500/30 space-y-3 mt-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-cyan-400 font-bold text-xs flex items-center gap-1.5">
                            ⏰ Lọc Khung Giờ / Phiên Giao Dịch (Trading Session Filter)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setHmaSystemData({ ...hmaSystemData, sessionFilterEnable: !hmaSystemData.sessionFilterEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            hmaSystemData.sessionFilterEnable
                              ? "bg-cyan-500/25 text-cyan-300 border border-cyan-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {hmaSystemData.sessionFilterEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT (24/24) ✕"}
                        </button>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {hmaSystemData.sessionFilterEnable
                          ? "Bật: Chỉ mở tín hiệu trong phiên được chọn (Giúp loại bỏ phiên Mỹ biến động giật hoặc backtest so sánh hiệu quả từng phiên)."
                          : "Tắt: Quét và nhận tín hiệu liên tục 24/24 cho tất cả các phiên."}
                      </p>

                      <div className={`space-y-2.5 pt-1 transition-opacity ${hmaSystemData.sessionFilterEnable ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Chọn Phiên Giao Dịch (Giờ VN - UTC+7):</span>
                            <span className="text-[10px] text-slate-400">Chọn nhanh phiên Á, Âu, Mỹ hoặc Loại bỏ phiên Mỹ</span>
                          </div>
                          <select
                            disabled={!hmaSystemData.sessionFilterEnable}
                            value={hmaSystemData.sessionMode || "ALL"}
                            onChange={(e) => {
                              const m = e.target.value;
                              let sStart = hmaSystemData.sessionStart || "06:00";
                              let sEnd = hmaSystemData.sessionEnd || "19:30";
                              if (m === "ASIA") { sStart = "06:00"; sEnd = "14:00"; }
                              else if (m === "LONDON") { sStart = "14:00"; sEnd = "21:00"; }
                              else if (m === "NY") { sStart = "19:30"; sEnd = "03:30"; }
                              else if (m === "ASIA_LONDON") { sStart = "06:00"; sEnd = "19:30"; }
                              else if (m === "LONDON_NY") { sStart = "14:00"; sEnd = "03:30"; }
                              setHmaSystemData({
                                ...hmaSystemData,
                                sessionMode: m,
                                sessionStart: sStart,
                                sessionEnd: sEnd
                              });
                            }}
                            className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-xs text-cyan-300 font-bold focus:outline-none"
                          >
                            <option value="ALL">🌐 Tất Cả Phiên (24/24)</option>
                            <option value="ASIA">🌏 Chỉ Phiên Á (06:00 - 14:00 VN)</option>
                            <option value="LONDON">🏰 Chỉ Phiên Âu (14:00 - 21:00 VN)</option>
                            <option value="NY">🗽 Chỉ Phiên Mỹ (19:30 - 03:30 VN)</option>
                            <option value="ASIA_LONDON">⭐ Phiên Á + Âu (BỎ PHIÊN MỸ: 06:00 - 19:30 VN)</option>
                            <option value="LONDON_NY">⚡ Phiên Âu + Mỹ (14:00 - 03:30 VN)</option>
                            <option value="CUSTOM">🔧 Tùy Chỉnh Giờ Bắt Đầu - Kết Thúc</option>
                          </select>
                        </div>

                        {/* Custom Time Range Inputs */}
                        {(hmaSystemData.sessionMode === "CUSTOM" || hmaSystemData.sessionFilterEnable) && (
                          <div className="grid grid-cols-2 gap-3 p-2.5 rounded-lg bg-slate-900/60 border border-slate-800/80">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-300 text-xs font-semibold">Giờ bắt đầu:</span>
                              <input
                                type="text"
                                disabled={!hmaSystemData.sessionFilterEnable}
                                value={hmaSystemData.sessionStart || "06:00"}
                                onChange={(e) => setHmaSystemData({ ...hmaSystemData, sessionStart: e.target.value })}
                                placeholder="06:00"
                                className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-center text-cyan-300 font-mono text-xs font-bold"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-slate-300 text-xs font-semibold">Giờ kết thúc:</span>
                              <input
                                type="text"
                                disabled={!hmaSystemData.sessionFilterEnable}
                                value={hmaSystemData.sessionEnd || "19:30"}
                                onChange={(e) => setHmaSystemData({ ...hmaSystemData, sessionEnd: e.target.value })}
                                placeholder="19:30"
                                className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-center text-amber-300 font-mono text-xs font-bold"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>



                  </div>
                </>
              )}

              {/* BOB VOLMAN SYSTEM INPUTS (bob_volman_system) */}
              {indicatorId === "bob_volman_system" && (
                <>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-cyan-400 tracking-wider mb-2 block">
                      CẤU HÌNH HỆ THỐNG BOB VOLMAN PRO (PRICE ACTION SCALPING)
                    </span>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {VOLMAN_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActivePreset(preset.id);
                            setVolmanData((prev) => ({ ...prev, ...preset.params }));
                          }}
                          className={`p-2.5 rounded-xl text-left border transition cursor-pointer ${
                            activePreset === preset.id
                              ? "bg-cyan-500/15 border-cyan-400 text-cyan-300 font-bold shadow-sm"
                              : "bg-slate-950/50 border-slate-800 hover:bg-slate-950 hover:border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="text-xs font-bold block">{preset.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5 line-clamp-1">
                            {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    {/* Setup Toggles */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <span className="text-slate-200 text-xs font-semibold block">Các Mô Hình Kích Hoạt Lệnh (2 Mô hình chuẩn):</span>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={volmanData.volmanEnableTB !== false}
                            onChange={(e) => setVolmanData({ ...volmanData, volmanEnableTB: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-cyan-300 font-bold block">1. Phá Trendline (TB)</span>
                            <span className="text-[10px] text-slate-400 block">Trendline Break + Buildup nén</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-2 p-2.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={volmanData.volmanEnableBB !== false}
                            onChange={(e) => setVolmanData({ ...volmanData, volmanEnableBB: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-emerald-300 font-bold block">2. Phá Kháng Cự/Hỗ Trợ (BB)</span>
                            <span className="text-[10px] text-slate-400 block">Block Break qua cản ngang</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* EMA Period */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Chu kỳ EMA Xu Hướng (Trend EMA):</span>
                        <span className="text-[10.5px] text-slate-400">Đường EMA căn cứ theo nguyên lý Bob Volman (Mặc định: 20 EMA)</span>
                      </div>
                      <input
                        type="number"
                        min="5"
                        max="200"
                        value={volmanData.volmanEmaPeriod || 20}
                        onChange={(e) => setVolmanData({ ...volmanData, volmanEmaPeriod: Math.max(5, parseInt(e.target.value) || 20) })}
                        className="w-24 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-cyan-400 font-mono text-xs font-bold"
                      />
                    </div>

                    {/* TP Target Ratio */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Mục Tiêu Chốt Lời (TP Target):</span>
                        <span className="text-[10.5px] text-slate-400">Tỷ lệ Lợi Nhuận / Rủi Ro R:R (chuẩn 1.5R)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="10.0"
                          value={volmanData.volmanR1 || 1.5}
                          onChange={(e) => setVolmanData({ ...volmanData, volmanR1: parseFloat(e.target.value) || 1.5 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">R</span>
                      </div>
                    </div>

                    {/* SL Buffer behind Buildup */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Khoảng đệm Cắt Lỗ SL (SL Buffer Pips):</span>
                        <span className="text-[10.5px] text-slate-400">Cộng thêm ngoài vùng nén Buildup để tránh quét cản</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="50"
                          value={volmanData.volmanSlBufferPips ?? 5.0}
                          onChange={(e) => setVolmanData({ ...volmanData, volmanSlBufferPips: parseFloat(e.target.value) || 0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-rose-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">pips</span>
                      </div>
                    </div>

                    {/* Cooldown Bars */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Số nến giãn cách tín hiệu (Cooldown Bars):</span>
                        <span className="text-[10.5px] text-slate-400">Khoảng nghỉ tối thiểu giữa 2 lần vào lệnh</span>
                      </div>
                      <input
                        type="number"
                        min="1"
                        max="500"
                        value={volmanData.volmanCooldownBars ?? 5}
                        onChange={(e) => setVolmanData({ ...volmanData, volmanCooldownBars: parseInt(e.target.value) || 5 })}
                        className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-slate-100 font-mono text-xs"
                      />
                    </div>

                    {/* MA Trend Filter */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={volmanData.volmanMaFilterEnable || false}
                            onChange={(e) => setVolmanData({ ...volmanData, volmanMaFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">✨ Lọc Xu Hướng Theo Đường MA (MA Trend Filter)</span>
                            <span className="text-[10px] text-slate-400 block">Chỉ Buy khi giá trên MA, chỉ Sell khi giá dưới MA</span>
                          </div>
                        </label>
                      </div>

                      {volmanData.volmanMaFilterEnable && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
                          <div>
                            <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">Loại Đường MA (Type):</span>
                            <select
                              value={volmanData.volmanMaType || "EMA"}
                              onChange={(e) => setVolmanData({ ...volmanData, volmanMaType: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-slate-200 text-xs font-semibold focus:outline-none focus:border-cyan-500"
                            >
                              <option value="EMA">EMA (Hàm Mũ - Mặc định)</option>
                              <option value="SMA">SMA (Đơn Giản)</option>
                            </select>
                          </div>
                          <div>
                            <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">Chu kỳ Lọc (Period):</span>
                            <input
                              type="number"
                              min="10"
                              max="1000"
                              value={volmanData.volmanMaPeriod || 200}
                              onChange={(e) => setVolmanData({ ...volmanData, volmanMaPeriod: Math.max(5, parseInt(e.target.value) || 200) })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-amber-400 font-mono text-xs font-bold focus:outline-none focus:border-cyan-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Max Consecutive Same-Direction Filter */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={volmanData.volmanMaxConsecutiveEnable || false}
                            onChange={(e) => setVolmanData({ ...volmanData, volmanMaxConsecutiveEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">🛡️ Giới Hạn Lệnh Cùng Chiều Liên Tiếp (Max Consecutive Orders)</span>
                            <span className="text-[10px] text-slate-400 block">Chặn tín hiệu cùng chiều khi vượt quá số lượng tối đa (buộc phải đảo chiều mới vào tiếp)</span>
                          </div>
                        </label>
                      </div>

                      {volmanData.volmanMaxConsecutiveEnable && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Số lệnh cùng chiều tối đa:</span>
                            <span className="text-[10.5px] text-slate-400">Ví dụ: 3 lệnh Sell liên tiếp thì lệnh tiếp theo bắt buộc phải là Buy (và ngược lại)</span>
                          </div>
                          <input
                            type="number"
                            min="1"
                            max="20"
                            value={volmanData.volmanMaxConsecutiveSameDir ?? 3}
                            onChange={(e) => setVolmanData({ ...volmanData, volmanMaxConsecutiveSameDir: Math.max(1, parseInt(e.target.value) || 3) })}
                            className="w-20 bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-amber-400 font-mono text-xs font-bold focus:outline-none focus:border-cyan-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ORB SESSION SYSTEM INPUTS (orb_session_system) */}
              {indicatorId === "orb_session_system" && (
                <>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-amber-400 tracking-wider mb-2 block">
                      CẤU HÌNH CHIẾN LƯỢC ORB SESSION PRO (OPENING RANGE BREAKOUT)
                    </span>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      {ORB_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          onClick={() => {
                            setActivePreset(preset.id);
                            setOrbSessionData((prev) => ({ ...prev, ...preset.params }));
                          }}
                          className={`p-2.5 rounded-xl text-left border transition cursor-pointer ${
                            activePreset === preset.id
                              ? "bg-amber-500/15 border-amber-400 text-amber-300 font-bold shadow-sm"
                              : "bg-slate-950/50 border-slate-800 hover:bg-slate-950 hover:border-slate-700 text-slate-300"
                          }`}
                        >
                          <span className="text-xs font-bold block">{preset.label}</span>
                          <span className="text-[10px] text-slate-400 block mt-0.5 line-clamp-1">
                            {preset.desc}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2 border-t border-slate-800">
                    {/* Session Selection Toggles */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <span className="text-slate-200 text-xs font-semibold block">Các Phiên Giao Dịch Áp Dụng (Nến M5 Mở Cửa):</span>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={orbSessionData.orbAsiaEnable !== false}
                            onChange={(e) => setOrbSessionData({ ...orbSessionData, orbAsiaEnable: e.target.checked })}
                            className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-cyan-300 font-bold block">1. Phiên Á (Tokyo)</span>
                            <span className="text-[10px] text-slate-400 block">08h30 VN (01:30 UTC)</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={orbSessionData.orbLondonEnable !== false}
                            onChange={(e) => setOrbSessionData({ ...orbSessionData, orbLondonEnable: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">2. Phiên Âu (London)</span>
                            <span className="text-[10px] text-slate-400 block">15h00 VN (08:00 UTC)</span>
                          </div>
                        </label>
                        <label className="flex items-center gap-2 p-2 rounded-lg bg-slate-900 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                          <input
                            type="checkbox"
                            checked={Boolean(orbSessionData.orbNyEnable)}
                            onChange={(e) => setOrbSessionData({ ...orbSessionData, orbNyEnable: e.target.checked })}
                            className="rounded border-slate-700 text-purple-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-purple-300 font-bold block">3. Phiên Mỹ (NY)</span>
                            <span className="text-[10px] text-slate-400 block">20h30 VN (13:30 UTC)</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Entry Mode Selector */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Chế độ Vào Lệnh (Entry Mode):</span>
                        <span className="text-[10.5px] text-slate-400">Cơ chế vào lệnh khi nến phá vỡ biên hộp M5</span>
                      </div>
                      <select
                        value={orbSessionData.orbEntryMode || "SMART_HYBRID"}
                        onChange={(e) => setOrbSessionData({ ...orbSessionData, orbEntryMode: e.target.value })}
                        className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-amber-300 font-bold focus:outline-none focus:border-amber-500"
                      >
                        <option value="SMART_HYBRID">⚡ Smart Hybrid (Khuyên dùng)</option>
                        <option value="LIMIT_50_BREAK">📏 Limit 50% Nến Breakout</option>
                        <option value="LIMIT_EDGE">🎯 Limit Retest Tại Mép Cản M5</option>
                        <option value="MARKET_BREAK">🚀 Market Ngay (Phá vỡ là vào)</option>
                      </select>
                    </div>

                    {/* TP Target Ratio */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Mục Tiêu Chốt Lời (TP Target R:R):</span>
                        <span className="text-[10.5px] text-slate-400">Tỷ lệ Lợi Nhuận / Rủi Ro (Mặc định: 3.0R)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.1"
                          min="1.0"
                          max="10.0"
                          value={orbSessionData.orbRr || 3.0}
                          onChange={(e) => setOrbSessionData({ ...orbSessionData, orbRr: parseFloat(e.target.value) || 3.0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">R</span>
                      </div>
                    </div>

                    {/* SL Buffer Mode & Parameters */}
                    <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-slate-200 text-xs font-semibold block">Khoảng đệm Cắt Lỗ SL (Buffer):</span>
                          <span className="text-[10.5px] text-slate-400">Đệm ngoài mép cản theo ATR (2 - 15 pips) hoặc cố định</span>
                        </div>
                        <select
                          value={orbSessionData.orbSlBufferMode || "FIXED"}
                          onChange={(e) => setOrbSessionData({ ...orbSessionData, orbSlBufferMode: e.target.value })}
                          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-amber-300 font-semibold focus:outline-none"
                        >
                          <option value="FIXED">🔧 Thủ công Cố định (Fixed)</option>
                          <option value="ATR">⚡ Tự động theo ATR (2-15p)</option>
                        </select>
                      </div>

                      {orbSessionData.orbSlBufferMode === "FIXED" ? (
                        <div className="flex items-center justify-between pt-1 border-t border-slate-800/60 font-mono text-xs">
                          <span className="text-slate-400 text-[11px]">SL Buffer Cố định:</span>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="500"
                              value={orbSessionData.orbSlBufferPips ?? 2.0}
                              onChange={(e) => setOrbSessionData({ ...orbSessionData, orbSlBufferPips: parseFloat(e.target.value) || 0 })}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-rose-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">pips</span>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/60 font-mono text-xs">
                          <div className="flex items-center justify-between p-1.5 rounded bg-slate-900 border border-slate-800">
                            <span className="text-slate-400 text-[10.5px]">Hệ số ATR:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="0.1"
                                min="0.5"
                                max="3.0"
                                value={orbSessionData.orbSlAtrMult ?? 1.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbSlAtrMult: parseFloat(e.target.value) || 1.0 })}
                                className="w-14 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-amber-300 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">x</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-1.5 rounded bg-slate-900 border border-slate-800">
                            <span className="text-slate-400 text-[10.5px]">Min / Max:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="30"
                                value={orbSessionData.orbSlBufferMinPips ?? 2.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbSlBufferMinPips: parseFloat(e.target.value) || 2.0 })}
                                className="w-10 bg-slate-950 border border-slate-700 rounded px-1 py-0.5 text-right text-rose-400 font-bold text-[11px]"
                              />
                              <span className="text-slate-500">-</span>
                              <input
                                type="number"
                                step="1"
                                min="5"
                                max="50"
                                value={orbSessionData.orbSlBufferMaxPips ?? 15.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbSlBufferMaxPips: parseFloat(e.target.value) || 15.0 })}
                                className="w-10 bg-slate-950 border border-slate-700 rounded px-1 py-0.5 text-right text-rose-400 font-bold text-[11px]"
                              />
                              <span className="text-slate-400 text-[10px]">p</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* SL Risk Filter (Lọc khoảng cách Cắt Lỗ SL) */}
                    <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-slate-200 text-xs font-semibold block">Bộ Lọc Độ Dài Cắt Lỗ (SL Filter):</span>
                          <span className="text-[10.5px] text-slate-400">Bỏ qua lệnh nếu khoảng cách SL quá ngắn hoặc quá dài</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOrbSessionData({ ...orbSessionData, orbSlFilterEnable: !orbSessionData.orbSlFilterEnable })}
                          className={`px-2.5 py-0.5 text-xs font-bold rounded-lg transition cursor-pointer ${
                            orbSessionData.orbSlFilterEnable
                              ? "bg-amber-500/25 text-amber-300 border border-amber-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {orbSessionData.orbSlFilterEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {orbSessionData.orbSlFilterEnable && (
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/60 font-mono text-xs">
                          <div className="flex items-center justify-between p-1.5 rounded bg-slate-900 border border-slate-800">
                            <span className="text-slate-400 text-[10.5px]">SL Tối thiểu:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="50"
                                value={orbSessionData.orbMinSlPips ?? 3.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbMinSlPips: parseFloat(e.target.value) || 3.0 })}
                                className="w-14 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-slate-200 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">pips</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between p-1.5 rounded bg-slate-900 border border-slate-800">
                            <span className="text-slate-400 text-[10.5px]">SL Tối đa (Max Risk):</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="5"
                                min="20"
                                max="500"
                                value={orbSessionData.orbMaxSlPips ?? 150.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbMaxSlPips: parseFloat(e.target.value) || 150.0 })}
                                className="w-14 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-rose-400 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">pips</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ===================== DYNAMIC LIMIT ON LARGE SL ===================== */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-amber-500/30 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-amber-400 font-bold text-xs flex items-center gap-1.5">
                            ⚡ Vào Lệnh Chờ Limit Khi SL Quá Lớn (ORB Dynamic Limit Entry)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOrbSessionData({ ...orbSessionData, orbDynamicLimitEnable: !orbSessionData.orbDynamicLimitEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            orbSessionData.orbDynamicLimitEnable
                              ? "bg-amber-500/25 text-amber-300 border border-amber-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {orbSessionData.orbDynamicLimitEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {orbSessionData.orbDynamicLimitEnable
                          ? `Bật: Khi SL ban đầu vượt quá ${orbSessionData.orbLimitThresholdPips || 100} pips, tự động chuyển sang Limit chờ hồi ${orbSessionData.orbLimitPullbackPct || 50}% khoảng cách về SL (hủy sau ${orbSessionData.orbLimitCancelBars || 10} nến không khớp).`
                          : "Tắt: Luôn vào lệnh theo chế độ Entry Mode thông thường."}
                      </p>

                      {orbSessionData.orbDynamicLimitEnable && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 border-t border-slate-800/60 font-mono text-xs">
                          <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                            <span className="text-slate-300 text-[11px]">Ngưỡng SL kích hoạt:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="5"
                                min="20"
                                max="300"
                                value={orbSessionData.orbLimitThresholdPips ?? 100.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbLimitThresholdPips: parseFloat(e.target.value) || 100.0 })}
                                className="w-16 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-amber-300 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">p</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                            <span className="text-slate-300 text-[11px]">Tỷ lệ hồi (Pullback):</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="5"
                                min="10"
                                max="90"
                                value={orbSessionData.orbLimitPullbackPct ?? 50.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbLimitPullbackPct: parseFloat(e.target.value) || 50.0 })}
                                className="w-14 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-amber-300 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">%</span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                            <span className="text-slate-300 text-[11px]">Hủy Limit sau:</span>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="50"
                                value={orbSessionData.orbLimitCancelBars ?? 10}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbLimitCancelBars: parseInt(e.target.value) || 10 })}
                                className="w-14 bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-right text-amber-300 font-bold"
                              />
                              <span className="text-slate-400 text-[10px]">nến</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ===================== EARLY BREAK-EVEN LOCK (DỜI BE SỚM) ===================== */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-emerald-500/30 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-emerald-400 font-bold text-xs flex items-center gap-1.5">
                            🛡️ Dời SL Về Hòa Vốn Khi Đạt % TP (Break-Even Lock)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOrbSessionData({ ...orbSessionData, orbDynamicBeEnable: !orbSessionData.orbDynamicBeEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            orbSessionData.orbDynamicBeEnable
                              ? "bg-emerald-500/25 text-emerald-300 border border-emerald-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {orbSessionData.orbDynamicBeEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      <p className="text-[11px] text-slate-400 leading-relaxed">
                        {orbSessionData.orbDynamicBeEnable
                          ? `Bật: Khi giá đi được ${orbSessionData.orbBeTriggerPct || 75}% quãng đường tới TP, hệ thống tự động dời SL về mức hòa vốn +${orbSessionData.orbBeLockPips ?? 5} pips khóa lãi an toàn.`
                          : "Tắt: Giữ nguyên mức SL ban đầu cho đến khi chạm TP hoặc dính SL."}
                      </p>

                      <div className={`grid grid-cols-2 gap-3 pt-1 transition-opacity ${orbSessionData.orbDynamicBeEnable ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                        {/* % TP Trigger */}
                        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Ngưỡng kích hoạt (% TP):</span>
                            <span className="text-[10px] text-slate-400">% quãng đường tới TP</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="30"
                              max="95"
                              step="5"
                              disabled={!orbSessionData.orbDynamicBeEnable}
                              value={orbSessionData.orbBeTriggerPct ?? 75}
                              onChange={(e) => setOrbSessionData({ ...orbSessionData, orbBeTriggerPct: parseFloat(e.target.value) || 75 })}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">%</span>
                          </div>
                        </div>

                        {/* Lock Pips */}
                        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900 border border-slate-800">
                          <div>
                            <span className="text-slate-200 text-xs font-semibold block">Pips khóa lời (BE+):</span>
                            <span className="text-[10px] text-slate-400">Pips cộng dương qua Entry</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="0"
                              max="30"
                              step="1"
                              disabled={!orbSessionData.orbDynamicBeEnable}
                              value={orbSessionData.orbBeLockPips ?? 5}
                              onChange={(e) => setOrbSessionData({ ...orbSessionData, orbBeLockPips: parseFloat(e.target.value) || 0 })}
                              className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                            />
                            <span className="text-slate-400 text-xs">pips</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Max Breakout Distance for Smart Hybrid */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Ngưỡng Breakout Tối Đa (Max Break Pips):</span>
                        <span className="text-[10.5px] text-slate-400">Nếu vượt quá khoảng cách này sẽ chuyển sang Limit 50% để tránh SL quá dài</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="1"
                          min="5"
                          max="50"
                          value={orbSessionData.orbMaxBreakPips ?? 15.0}
                          onChange={(e) => setOrbSessionData({ ...orbSessionData, orbMaxBreakPips: parseFloat(e.target.value) || 15.0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-amber-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">pips</span>
                      </div>
                    </div>

                    {/* Max Limit Wait Candles */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Số Nến Chờ Khớp Limit Tối Đa (Max Wait Bars):</span>
                        <span className="text-[10.5px] text-slate-400">Tự động hủy lệnh Limit nếu sau X nến M5 giá không quay về khớp (Mặc định: 6 nến = 30 phút)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="1"
                          min="1"
                          max="50"
                          value={orbSessionData.orbMaxLimitBars ?? 6}
                          onChange={(e) => setOrbSessionData({ ...orbSessionData, orbMaxLimitBars: parseInt(e.target.value) || 6 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-cyan-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">nến</span>
                      </div>
                    </div>

                    {/* Cancel on TP Touch First */}
                    <label className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800 hover:border-slate-700 cursor-pointer transition">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Tự Hủy Lệnh Khi Chạm TP Trước:</span>
                        <span className="text-[10.5px] text-slate-400">Hủy lệnh Limit nếu sóng phá vỡ đã chạy thẳng chạm mốc TP mà chưa hồi về đón</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={orbSessionData.orbCancelOnTp !== false}
                        onChange={(e) => setOrbSessionData({ ...orbSessionData, orbCancelOnTp: e.target.checked })}
                        className="rounded border-slate-700 text-emerald-500 focus:ring-0"
                      />
                    </label>

                    {/* EMA 200 Trend Filter & Dual Trend Mode */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <span className="text-slate-200 text-xs font-semibold block flex items-center gap-1.5">
                            <span>📈 Bộ Lọc Xu Hướng EMA 200:</span>
                          </span>
                          <span className="text-[10.5px] text-slate-400">
                            Chọn phong cách lọc xu hướng hoặc giao dịch chế độ kép (Dual Trend)
                          </span>
                        </div>
                        <select
                          value={orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF")}
                          onChange={(e) => {
                            const mode = e.target.value;
                            setOrbSessionData({
                              ...orbSessionData,
                              orbEma200Mode: mode,
                              orbEma200Filter: mode !== "OFF"
                            });
                          }}
                          className="bg-slate-900 border border-purple-500/50 rounded-lg px-2.5 py-1 text-xs text-purple-300 font-bold focus:outline-none focus:border-purple-400"
                        >
                          <option value="OFF">⚪ 1. Tắt lọc MA (Đánh 2 đầu tự do)</option>
                          <option value="STRICT">🔒 2. Lọc Thuận Trend (Trên BUY / Dưới SELL)</option>
                          <option value="DUAL_TREND">⚡ 3. Chế độ Kép Thông Minh (Giao dịch cả 2 style)</option>
                        </select>
                      </div>
                      <div className="text-[11px] text-slate-400 leading-relaxed bg-slate-900/60 p-2.5 rounded-lg border border-slate-800/60">
                        {(orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF")) === "DUAL_TREND" && (
                          <span className="text-amber-300">
                            ⚡ <strong>Chế độ Kép Thông Minh (Combined):</strong> Giao dịch kết hợp đồng thời cả 2 chiến lược (Lọc MA nghiêm ngặt + Đánh tự do không lọc). Hệ thống giữ trọn vẹn toàn bộ các cơ hội breakout & revert tự do, đồng thời bổ sung thêm các cơ hội bắt trend độc lập của hệ thống lọc MA.
                          </span>
                        )}
                        {(orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF")) === "STRICT" && (
                          <span className="text-cyan-300">
                            🔒 <strong>Lọc Thuận Trend Tuyệt Đối:</strong> Khi giá trên EMA 200 chỉ cho phép BUY (bỏ qua SELL). Khi giá dưới EMA 200 chỉ cho phép SELL (bỏ qua BUY).
                          </span>
                        )}
                        {(orbSessionData.orbEma200Mode || (orbSessionData.orbEma200Filter ? "STRICT" : "OFF")) === "OFF" && (
                          <span className="text-slate-400">
                            ⚪ <strong>Tắt lọc MA:</strong> Đánh tự do cả 2 đầu phá vỡ của hộp phiên (Breakout LÊN = BUY, Breakout XUỐNG = SELL) bất kể vị trí EMA 200.
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Timeout Exit Enable Toggle (Hết phiên ép đóng lệnh / Giữ lệnh thuần TP-SL) */}
                    <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-slate-200 text-xs font-semibold block">Hết Phiên Ép Đóng Lệnh (Session Timeout Exit):</span>
                          <span className="text-[10.5px] text-slate-400">
                            {orbSessionData.orbTimeoutExitEnable !== false 
                              ? "BẬT: Tự động đóng lệnh khi hết phiên (Timeout) nếu chưa chạm TP/SL" 
                              : "TẮT: Giữ lệnh xuyên phiên cho đến khi chạm thuần TP hoặc SL"}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOrbSessionData({ ...orbSessionData, orbTimeoutExitEnable: !orbSessionData.orbTimeoutExitEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            orbSessionData.orbTimeoutExitEnable !== false
                              ? "bg-amber-500/25 text-amber-300 border border-amber-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {orbSessionData.orbTimeoutExitEnable !== false ? "ĐANG BẬT (Ép cắt hết phiên)" : "ĐANG TẮT (Thuần TP/SL)"}
                        </button>
                      </div>
                    </div>

                    {/* Revert Strategy (Đảo chiều sau Fakeout / Quét SL) */}
                    <div className="p-3.5 rounded-xl bg-slate-950/80 border border-orange-500/30 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-orange-400 text-xs font-bold flex items-center gap-1.5">
                            <span>🔄 Đảo Chiều Sau Fakeout (Revert Strategy):</span>
                          </span>
                          <span className="text-[10.5px] text-slate-400 block mt-0.5">
                            Khi lệnh chính bị dính SL (bị quét Fakeout), hệ thống tự động kích hoạt lệnh đảo chiều ngược lại
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOrbSessionData({ ...orbSessionData, orbRevertEnable: !orbSessionData.orbRevertEnable })}
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                            orbSessionData.orbRevertEnable
                              ? "bg-orange-500/25 text-orange-300 border border-orange-500/50 shadow-sm"
                              : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200"
                          }`}
                        >
                          {orbSessionData.orbRevertEnable ? "ĐANG BẬT ✓" : "ĐANG TẮT ✕"}
                        </button>
                      </div>

                      {orbSessionData.orbRevertEnable && (
                        <div className="space-y-3 pt-2 border-t border-slate-800">
                          <div>
                            <span className="text-slate-300 text-xs font-semibold block mb-1">Kiểu Vào Lệnh Revert (Entry Mode):</span>
                            <select
                              value={orbSessionData.orbRevertEntryMode || "LIMIT_50_BOX"}
                              onChange={(e) => setOrbSessionData({ ...orbSessionData, orbRevertEntryMode: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 font-medium focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                            >
                              <option value="LIMIT_50_BOX">Limit tại 50% Thân Hộp Phiên (Midpoint Box)</option>
                              <option value="SMART_HYBRID">Smart Hybrid (Market nến ngắn &le; {orbSessionData.orbMaxBreakPips ?? 15}p / Limit hồi 50%)</option>
                              <option value="MARKET">Vào Lệnh Market Ngay Khi Quét SL</option>
                              <option value="LIMIT_50_BREAK">Limit Hồi 50% Cây Nến Chạm SL</option>
                              <option value="LIMIT_EDGE">Limit Tại Mép Hộp Đối Diện</option>
                            </select>
                          </div>

                          <div className="grid grid-cols-3 gap-2.5">
                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">Target RR Revert:</span>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-orange-400 font-bold">1:</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="0.1"
                                  max="50"
                                  value={orbSessionData.orbRevertRr ?? 1.5}
                                  onChange={(e) => setOrbSessionData({ ...orbSessionData, orbRevertRr: parseFloat(e.target.value) || 1.5 })}
                                  className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                                />
                              </div>
                            </div>

                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">Số Nến Hủy Limit:</span>
                              <input
                                type="number"
                                step="1"
                                min="1"
                                max="200"
                                value={orbSessionData.orbRevertCancelBars ?? 6}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbRevertCancelBars: parseInt(e.target.value) || 6 })}
                                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                              />
                            </div>

                            <div className="bg-slate-900/90 border border-slate-800 rounded-lg p-2">
                              <span className="text-[11px] text-slate-400 block mb-1">SL Buffer (pips):</span>
                              <input
                                type="number"
                                step="0.5"
                                min="0"
                                max="500"
                                value={orbSessionData.orbRevertSlBufferPips ?? 2.0}
                                onChange={(e) => setOrbSessionData({ ...orbSessionData, orbRevertSlBufferPips: parseFloat(e.target.value) || 0 })}
                                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-bold focus:border-orange-500"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* LIQUIDITY SWEEP PRO INPUTS */}
              {indicatorId === "liquidity_sweep_pro" && (
                <>
                  <div className="space-y-3">
                    {/* Auto M1 -> M5 Structure Mapping */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepM1AnchorM5 !== false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepM1AnchorM5: e.target.checked })}
                            className="rounded border-slate-700 text-purple-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-purple-300 font-bold block">🧭 Đồng bộ Setup M5 sang M1 (Chống nhiễu M1)</span>
                            <span className="text-[10px] text-slate-400 block">Khi xem nến M1, hệ thống tự động lấy cấu trúc Swing & Setup chuẩn của M5 chiếu lên M1 (tránh nhiễu quét liên tục của M1)</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Swings Length (Managed by Admin Master - Hidden from modal as requested) */}

                    {/* Highest Peak / Lowest Bottom Filter (Major Structure Filter) */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepHighestPeakFilter || false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepHighestPeakFilter: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">🏔️ Chỉ Quét Đỉnh Cao Nhất / Đáy Thấp Nhất Đoạn (Major Swing Only)</span>
                            <span className="text-[10px] text-slate-400 block">Chỉ đánh đỉnh cao nhất hoặc đáy thấp nhất trong phân đoạn, loại bỏ đỉnh/đáy thấp lọt thỏm (tránh ăn SL khi xu hướng đi xa)</span>
                          </div>
                        </label>
                      </div>

                      {liquiditySweepData.sweepHighestPeakFilter && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                          <div>
                            <span className="text-[10.5px] text-slate-300 font-semibold block">Phạm vi xét đỉnh/đáy cao nhất (Peak Lookback):</span>
                            <span className="text-[9.5px] text-slate-500 block">Số nến trước đó để đảm bảo là đỉnh cao nhất / đáy thấp nhất đoạn</span>
                          </div>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="10"
                              max="200"
                              step="5"
                              value={liquiditySweepData.sweepPeakLookback ?? 30}
                              onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepPeakLookback: parseInt(e.target.value) || 30 })}
                              className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-amber-300 font-bold"
                            />
                            <span className="text-slate-400 text-xs">nến</span>
                          </div>
                        </div>
                      )}
                    </div>


                    {/* Entry Mode */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                        <div className="min-w-0 flex-1">
                          <span className="text-slate-200 text-xs font-semibold block">Chế độ vào lệnh (Entry Mode):</span>
                          <span className="text-[10.5px] text-slate-400 block whitespace-normal">Cách thức kích hoạt lệnh khi xuất hiện nến quét</span>
                        </div>
                        <select
                          value={liquiditySweepData.sweepEntryMode === "MARKET" ? "MARKET" : "RETEST"}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepEntryMode: e.target.value })}
                          className="w-full sm:w-auto shrink-0 max-w-full sm:max-w-[340px] bg-slate-900 border border-purple-500/50 rounded-lg px-2.5 py-1.5 text-xs text-purple-300 font-bold focus:outline-none focus:border-purple-400 truncate cursor-pointer"
                        >
                          <option value="RETEST">🎯 Limit</option>
                          <option value="MARKET">⚡ Market</option>
                        </select>
                      </div>
                    </div>

                    {/* Close on Opposite */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={liquiditySweepData.sweepCloseOnOpposite || false}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepCloseOnOpposite: e.target.checked })}
                          className="rounded border-slate-700 text-amber-500 focus:ring-0"
                        />
                        <div>
                          <span className="text-xs text-amber-300 font-bold block">🔄 Đóng lệnh khi có Setup ngược chiều (Close on Opposite)</span>
                          <span className="text-[10.5px] text-slate-400 block">Tự động chốt lệnh đang mở và hủy lệnh Limit chờ khi xuất hiện nến quét ngược chiều</span>
                        </div>
                      </label>
                    </div>

                    {/* Dynamic Break-Even (Hòa Vốn Tự Động) */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepDynamicBeEnable || false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepDynamicBeEnable: e.target.checked })}
                            className="rounded border-slate-700 text-emerald-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-emerald-300 font-bold block">🎯 Dời SL Về Hòa Vốn Tự Động (Dynamic Break-Even)</span>
                            <span className="text-[10px] text-slate-400 block">Tự động dời SL về Entry + Khóa lãi khi giá chạy được một phần quãng đường tới TP</span>
                          </div>
                        </label>
                      </div>

                      {liquiditySweepData.sweepDynamicBeEnable && (
                        <div className="pt-2 border-t border-slate-800/80 space-y-2.5">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">Kiểu kích hoạt:</span>
                              <select
                                value={liquiditySweepData.sweepBeTriggerMode || "PCT_TP"}
                                onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepBeTriggerMode: e.target.value })}
                                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-emerald-300 font-bold focus:outline-none focus:border-emerald-500"
                              >
                                <option value="PCT_TP">Theo % TP</option>
                                <option value="PIPS">Theo số Pips</option>
                              </select>
                            </div>
                            <div>
                              <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">
                                {liquiditySweepData.sweepBeTriggerMode === "PIPS" ? "Ngưỡng Pips kích hoạt:" : "Ngưỡng % TP kích hoạt:"}
                              </span>
                              {liquiditySweepData.sweepBeTriggerMode === "PIPS" ? (
                                <div className="flex items-center gap-1 font-mono text-xs">
                                  <input
                                    type="number" min="5" max="200" step="5"
                                    value={liquiditySweepData.sweepBeTriggerPips ?? 40.0}
                                    onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepBeTriggerPips: parseFloat(e.target.value) || 40.0 })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-emerald-300 font-bold"
                                  />
                                  <span className="text-slate-400 text-xs">pips</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 font-mono text-xs">
                                  <input
                                    type="number" min="10" max="90" step="5"
                                    value={liquiditySweepData.sweepBeTriggerPct ?? 50.0}
                                    onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepBeTriggerPct: parseFloat(e.target.value) || 50.0 })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-emerald-300 font-bold"
                                  />
                                  <span className="text-slate-400 text-xs">%</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-[10.5px] text-slate-300 font-semibold block">Khóa lãi tối thiểu (Lock Profit):</span>
                              <span className="text-[9.5px] text-slate-500 block">Dời SL cách Entry N pips về hướng có lời</span>
                            </div>
                            <div className="flex items-center gap-1 font-mono text-xs">
                              <input
                                type="number" min="0" max="20" step="0.5"
                                value={liquiditySweepData.sweepBeLockPips ?? 2.0}
                                onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepBeLockPips: parseFloat(e.target.value) || 2.0 })}
                                className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-emerald-300 font-bold"
                              />
                              <span className="text-slate-400 text-xs">pips</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Max Wait Bars */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Số nến tối đa chờ xác nhận (Max Wait Bars):</span>
                        <span className="text-[10.5px] text-slate-400">Hủy quét nếu sau N nến không có xác nhận đảo chiều (Chuẩn Price Action = 5 nến)</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          min="2"
                          max="15"
                          step="1"
                          value={liquiditySweepData.sweepMaxWaitBars ?? 5}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepMaxWaitBars: Math.max(2, Math.min(15, parseInt(e.target.value) || 5)) })}
                          className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1 text-right text-amber-400 font-mono text-xs font-bold"
                        />
                        <span className="text-slate-400 text-xs">nến</span>
                      </div>
                    </div>

                    {/* Extend Sweep Area & Max Bars */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepExtend !== false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepExtend: e.target.checked })}
                            className="rounded border-slate-700 text-emerald-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-emerald-300 font-bold block">📐 Kéo dài vùng quét (Extend Sweep Area)</span>
                            <span className="text-[10px] text-slate-400 block">Kéo dài hộp quét thanh khoản sang phải cho tới khi bị nến đóng phá vỡ</span>
                          </div>
                        </label>
                      </div>

                      {liquiditySweepData.sweepExtend !== false && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                          <span className="text-[10.5px] text-slate-400 font-semibold block">Số nến tối đa (Max Bars):</span>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="10"
                              max="1000"
                              step="50"
                              value={liquiditySweepData.sweepMaxBars ?? 300}
                              onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepMaxBars: parseInt(e.target.value) || 300 })}
                              className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-emerald-300 font-bold"
                            />
                            <span className="text-slate-400 text-xs">nến</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* R:R Ratio */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Tỷ lệ Lợi Nhuận / Rủi Ro (R:R Ratio):</span>
                        <span className="text-[10.5px] text-slate-400">TP1 chốt 1.0R, TP2 chốt theo tỷ lệ R:R</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="10.0"
                          value={liquiditySweepData.sweepRr ?? 2.0}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepRr: parseFloat(e.target.value) || 2.0 })}
                          className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-emerald-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">R</span>
                      </div>
                    </div>

                    {/* SL Buffer */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-200 text-xs font-semibold block">Khoảng đệm Cắt Lỗ SL (SL Buffer Pips):</span>
                        <span className="text-[10.5px] text-slate-400">Cộng thêm ngoài đỉnh/đáy râu quét để chống râu nến quét phụ</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max="50"
                          value={liquiditySweepData.sweepSlBufferPips ?? 1.5}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepSlBufferPips: parseFloat(e.target.value) || 0 })}
                          className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1 text-right text-rose-400 font-bold"
                        />
                        <span className="text-slate-400 text-xs">pips</span>
                      </div>
                    </div>

                    {/* Max Risk Pips (Min Risk SL hidden from public UI - Admin Controlled) */}
                    <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div>
                        <span className="text-slate-300 text-xs block font-semibold">Cắt Lỗ Tối Đa (Max Risk SL):</span>
                        <span className="text-[10px] text-slate-500 block">Bỏ qua tín hiệu nếu khoảng cách SL vượt quá giá trị này</span>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-xs">
                        <input
                          type="number"
                          step="5"
                          min="10"
                          max="300"
                          value={liquiditySweepData.sweepMaxRiskPips ?? 150.0}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepMaxRiskPips: parseFloat(e.target.value) || 150.0 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-right text-slate-100 font-bold"
                        />
                        <span className="text-slate-400 text-xs">p</span>
                      </div>
                    </div>

                    {/* Cooldown Bars & Limit Cancel Bars */}
                    <div className="grid grid-cols-2 gap-3 p-2.5 rounded-xl bg-slate-950/60 border border-slate-800">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 text-xs">Nến giãn cách (Cooldown):</span>
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={liquiditySweepData.sweepCooldownBars ?? 3}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepCooldownBars: parseInt(e.target.value) || 3 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-right text-slate-100 font-bold text-xs"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-300 text-xs">Hủy Limit sau (Cancel Bars):</span>
                        <input
                          type="number"
                          min="5"
                          max="100"
                          value={liquiditySweepData.sweepLimitCancelBars ?? 30}
                          onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepLimitCancelBars: parseInt(e.target.value) || 30 })}
                          className="w-16 bg-slate-950 border border-slate-700 rounded px-2 py-1 text-right text-amber-300 font-bold text-xs"
                        />
                      </div>
                    </div>

                    {/* Macro Trend Filter (Optional) */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepTrendFilterEnable || false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepTrendFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">🌐 Bộ Lọc Xu Hướng Vĩ Mô (EMA Macro Trend - Tùy Chọn)</span>
                            <span className="text-[10px] text-slate-400 block">Chỉ Buy khi nến trên EMA & Sell khi nến dưới EMA (Mặc định tắt)</span>
                          </div>
                        </label>
                      </div>

                      {liquiditySweepData.sweepTrendFilterEnable && (
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/80">
                          <span className="text-[10.5px] text-slate-400 font-semibold block">Chu kỳ EMA Xu Hướng:</span>
                          <div className="flex items-center gap-1 font-mono text-xs">
                            <input
                              type="number"
                              min="20"
                              max="500"
                              step="10"
                              value={liquiditySweepData.sweepTrendEmaPeriod ?? 200}
                              onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepTrendEmaPeriod: parseInt(e.target.value) || 200 })}
                              className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-right text-amber-300 font-bold"
                            />
                            <span className="text-slate-400 text-xs">nến</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Session Filter */}
                    <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2.5">
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={liquiditySweepData.sweepSessionFilterEnable || false}
                            onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepSessionFilterEnable: e.target.checked })}
                            className="rounded border-slate-700 text-amber-500 focus:ring-0"
                          />
                          <div>
                            <span className="text-xs text-amber-300 font-bold block">🕒 Bộ Lọc Khung Giờ Hoạt Động (Session Filter)</span>
                            <span className="text-[10px] text-slate-400 block">Chỉ kích hoạt tín hiệu quét thanh khoản trong khung giờ quy định</span>
                          </div>
                        </label>
                      </div>

                      {liquiditySweepData.sweepSessionFilterEnable && (
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
                          <div>
                            <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">Giờ bắt đầu:</span>
                            <input
                              type="time"
                              value={liquiditySweepData.sweepSessionStart || "00:00"}
                              onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepSessionStart: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-amber-300 font-mono text-xs font-bold focus:outline-none focus:border-amber-500"
                            />
                          </div>
                          <div>
                            <span className="text-[10.5px] text-slate-400 font-semibold block mb-1">Giờ kết thúc:</span>
                            <input
                              type="time"
                              value={liquiditySweepData.sweepSessionEnd || "23:59"}
                              onChange={(e) => setLiquiditySweepData({ ...liquiditySweepData, sweepSessionEnd: e.target.value })}
                              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-amber-300 font-mono text-xs font-bold focus:outline-none focus:border-amber-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ================= STYLE / ĐỊNH DẠNG TAB ================= */}
          {activeTab === "style" && (
            <div className="space-y-2.5">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block mb-1">
                TÙY CHỈNH MÀU SẮC & ĐƯỜNG NÉT VẼ
              </span>

              {/* CLASSICAL SMC PRO INDICATOR STYLE (smc_structure) */}
              {indicatorId === "smc_structure" && (
                <div className="space-y-2.5">
                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Khối Lệnh (Order Blocks - OB):</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-emerald-400 font-semibold">+OB (Khối lệnh Mua):</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.bullishOBColor || "#10b981"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, bullishOBColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-rose-400 font-semibold">-OB (Khối lệnh Bán):</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.bearishOBColor || "#ef4444"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, bearishOBColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Vùng Mất Cân Bằng (Fair Value Gaps - FVG):</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-cyan-400 font-semibold">FVG Tăng Giá (Bull FVG):</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.bullishFVGColor || "#06b6d4"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, bullishFVGColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-pink-400 font-semibold">FVG Giảm Giá (Bear FVG):</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.bearishFVGColor || "#f43f5e"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, bearishFVGColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Đường Cấu Trúc (BOS & CHoCH):</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-sky-400 font-semibold">Đường Phá Vỡ BOS:</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.bosColor || "#38bdf8"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, bosColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-amber-400 font-semibold">Đường Đổi Tính Chất CHoCH:</span>
                        <input
                          type="color"
                          value={classicalSmcStyle.chochColor || "#f59e0b"}
                          onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, chochColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn tên vùng (+OB, -OB, FVG, BOS, CHoCH)</span>
                    <input
                      type="checkbox"
                      checked={classicalSmcStyle.showLabels !== false}
                      onChange={(e) => setClassicalSmcStyle({ ...classicalSmcStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}

              {/* MSB DYNAMIC TRADING SYSTEM STYLE (msb_dynamic) */}
              {indicatorId === "msb_dynamic" && (
                <div className="space-y-2">
                  <LineStyleRow
                    label="Đường Vào Lệnh Entry (Limit)"
                    visible={smcStyle.entry?.visible !== false}
                    onToggleVisible={(val) => setSmcStyle({ ...smcStyle, entry: { ...smcStyle.entry, visible: val } })}
                    color={smcStyle.entry?.color || "#facc15"}
                    onChangeColor={(val) => setSmcStyle({ ...smcStyle, entry: { ...smcStyle.entry, color: val } })}
                    width={smcStyle.entry?.width || 1}
                    onChangeWidth={(val) => setSmcStyle({ ...smcStyle, entry: { ...smcStyle.entry, width: val } })}
                    style={smcStyle.entry?.style !== undefined ? smcStyle.entry?.style : 2}
                    onChangeStyle={(val) => setSmcStyle({ ...smcStyle, entry: { ...smcStyle.entry, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường Cắt Lỗ SL (Dừng Lỗ Bảo Vệ)"
                    visible={smcStyle.sl?.visible !== false}
                    onToggleVisible={(val) => setSmcStyle({ ...smcStyle, sl: { ...smcStyle.sl, visible: val } })}
                    color={smcStyle.sl?.color || "#f97316"}
                    onChangeColor={(val) => setSmcStyle({ ...smcStyle, sl: { ...smcStyle.sl, color: val } })}
                    width={smcStyle.sl?.width || 1}
                    onChangeWidth={(val) => setSmcStyle({ ...smcStyle, sl: { ...smcStyle.sl, width: val } })}
                    style={smcStyle.sl?.style || 0}
                    onChangeStyle={(val) => setSmcStyle({ ...smcStyle, sl: { ...smcStyle.sl, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường Chốt Lời TP1 (Mục Tiêu Chuẩn)"
                    visible={smcStyle.tp1?.visible !== false}
                    onToggleVisible={(val) => setSmcStyle({ ...smcStyle, tp1: { ...smcStyle.tp1, visible: val } })}
                    color={smcStyle.tp1?.color || "#84cc16"}
                    onChangeColor={(val) => setSmcStyle({ ...smcStyle, tp1: { ...smcStyle.tp1, color: val } })}
                    width={smcStyle.tp1?.width || 1}
                    onChangeWidth={(val) => setSmcStyle({ ...smcStyle, tp1: { ...smcStyle.tp1, width: val } })}
                    style={smcStyle.tp1?.style || 0}
                    onChangeStyle={(val) => setSmcStyle({ ...smcStyle, tp1: { ...smcStyle.tp1, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường Chốt Lời TP2 (Mục Tiêu Mở Rộng)"
                    visible={smcStyle.tp2?.visible !== false}
                    onToggleVisible={(val) => setSmcStyle({ ...smcStyle, tp2: { ...smcStyle.tp2, visible: val } })}
                    color={smcStyle.tp2?.color || "#22c55e"}
                    onChangeColor={(val) => setSmcStyle({ ...smcStyle, tp2: { ...smcStyle.tp2, color: val } })}
                    width={smcStyle.tp2?.width || 1}
                    onChangeWidth={(val) => setSmcStyle({ ...smcStyle, tp2: { ...smcStyle.tp2, width: val } })}
                    style={smcStyle.tp2?.style || 0}
                    onChangeStyle={(val) => setSmcStyle({ ...smcStyle, tp2: { ...smcStyle.tp2, style: val } })}
                  />

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn mức giá trên biểu đồ (Labels)</span>
                    <input
                      type="checkbox"
                      checked={smcStyle.showLabels !== false}
                      onChange={(e) => setSmcStyle({ ...smcStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-amber-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}

              {/* ICHIMOKU STYLE */}
              {indicatorId === "ichimoku_cloud" && (
                <div className="space-y-2">
                  <LineStyleRow
                    label="Đường chuyển đổi (Tenkan-sen)"
                    visible={ichimokuStyle.tenkan.visible}
                    onToggleVisible={(val) => setIchimokuStyle({ ...ichimokuStyle, tenkan: { ...ichimokuStyle.tenkan, visible: val } })}
                    color={ichimokuStyle.tenkan.color}
                    onChangeColor={(val) => setIchimokuStyle({ ...ichimokuStyle, tenkan: { ...ichimokuStyle.tenkan, color: val } })}
                    width={ichimokuStyle.tenkan.width}
                    onChangeWidth={(val) => setIchimokuStyle({ ...ichimokuStyle, tenkan: { ...ichimokuStyle.tenkan, width: val } })}
                    style={ichimokuStyle.tenkan.style}
                    onChangeStyle={(val) => setIchimokuStyle({ ...ichimokuStyle, tenkan: { ...ichimokuStyle.tenkan, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường Cơ sở (Kijun-sen)"
                    visible={ichimokuStyle.kijun.visible}
                    onToggleVisible={(val) => setIchimokuStyle({ ...ichimokuStyle, kijun: { ...ichimokuStyle.kijun, visible: val } })}
                    color={ichimokuStyle.kijun.color}
                    onChangeColor={(val) => setIchimokuStyle({ ...ichimokuStyle, kijun: { ...ichimokuStyle.kijun, color: val } })}
                    width={ichimokuStyle.kijun.width}
                    onChangeWidth={(val) => setIchimokuStyle({ ...ichimokuStyle, kijun: { ...ichimokuStyle.kijun, width: val } })}
                    style={ichimokuStyle.kijun.style}
                    onChangeStyle={(val) => setIchimokuStyle({ ...ichimokuStyle, kijun: { ...ichimokuStyle.kijun, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường trễ (Chikou Span)"
                    visible={ichimokuStyle.chikou.visible}
                    onToggleVisible={(val) => setIchimokuStyle({ ...ichimokuStyle, chikou: { ...ichimokuStyle.chikou, visible: val } })}
                    color={ichimokuStyle.chikou.color}
                    onChangeColor={(val) => setIchimokuStyle({ ...ichimokuStyle, chikou: { ...ichimokuStyle.chikou, color: val } })}
                    width={ichimokuStyle.chikou.width}
                    onChangeWidth={(val) => setIchimokuStyle({ ...ichimokuStyle, chikou: { ...ichimokuStyle.chikou, width: val } })}
                    style={ichimokuStyle.chikou.style}
                    onChangeStyle={(val) => setIchimokuStyle({ ...ichimokuStyle, chikou: { ...ichimokuStyle.chikou, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường dẫn A (Senkou Span A)"
                    visible={ichimokuStyle.spanA.visible}
                    onToggleVisible={(val) => setIchimokuStyle({ ...ichimokuStyle, spanA: { ...ichimokuStyle.spanA, visible: val } })}
                    color={ichimokuStyle.spanA.color}
                    onChangeColor={(val) => setIchimokuStyle({ ...ichimokuStyle, spanA: { ...ichimokuStyle.spanA, color: val } })}
                    width={ichimokuStyle.spanA.width}
                    onChangeWidth={(val) => setIchimokuStyle({ ...ichimokuStyle, spanA: { ...ichimokuStyle.spanA, width: val } })}
                    style={ichimokuStyle.spanA.style}
                    onChangeStyle={(val) => setIchimokuStyle({ ...ichimokuStyle, spanA: { ...ichimokuStyle.spanA, style: val } })}
                  />

                  <LineStyleRow
                    label="Kỳ đường trễ 2 (Senkou Span B)"
                    visible={ichimokuStyle.spanB.visible}
                    onToggleVisible={(val) => setIchimokuStyle({ ...ichimokuStyle, spanB: { ...ichimokuStyle.spanB, visible: val } })}
                    color={ichimokuStyle.spanB.color}
                    onChangeColor={(val) => setIchimokuStyle({ ...ichimokuStyle, spanB: { ...ichimokuStyle.spanB, color: val } })}
                    width={ichimokuStyle.spanB.width}
                    onChangeWidth={(val) => setIchimokuStyle({ ...ichimokuStyle, spanB: { ...ichimokuStyle.spanB, width: val } })}
                    style={ichimokuStyle.spanB.style}
                    onChangeStyle={(val) => setIchimokuStyle({ ...ichimokuStyle, spanB: { ...ichimokuStyle.spanB, style: val } })}
                  />

                  {/* Cloud Fill Colors */}
                  <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 space-y-2.5">
                    <label className="flex items-center gap-2.5 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={ichimokuStyle.cloudFill}
                        onChange={(e) => setIchimokuStyle({ ...ichimokuStyle, cloudFill: e.target.checked })}
                        className="rounded border-slate-700 text-cyan-500 focus:ring-0 cursor-pointer"
                      />
                      <span className="text-xs font-bold text-slate-200">Đổ màu Đám Mây (Cloud Fill)</span>
                    </label>

                    {ichimokuStyle.cloudFill && (
                      <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800">
                          <span className="text-[11px] text-emerald-400 font-semibold">Màu Mây Tăng:</span>
                          <input
                            type="color"
                            value={ichimokuStyle.cloudBullColor || "#10b981"}
                            onChange={(e) => setIchimokuStyle({ ...ichimokuStyle, cloudBullColor: e.target.value })}
                            className="w-5 h-5 rounded border border-slate-700 bg-transparent cursor-pointer"
                          />
                        </div>

                        <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800">
                          <span className="text-[11px] text-rose-400 font-semibold">Màu Mây Giảm:</span>
                          <input
                            type="color"
                            value={ichimokuStyle.cloudBearColor || "#ef4444"}
                            onChange={(e) => setIchimokuStyle({ ...ichimokuStyle, cloudBearColor: e.target.value })}
                            className="w-5 h-5 rounded border border-slate-700 bg-transparent cursor-pointer"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* MULTI EMA STYLE */}
              {indicatorId === "multi_ema_ribbon" && (
                <div className="space-y-2">
                  <LineStyleRow
                    label="Đường EMA 34 (Sonic Wave)"
                    visible={multiEmaStyle.ema1.visible}
                    onToggleVisible={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema1: { ...multiEmaStyle.ema1, visible: val } })}
                    color={multiEmaStyle.ema1.color}
                    onChangeColor={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema1: { ...multiEmaStyle.ema1, color: val } })}
                    width={multiEmaStyle.ema1.width}
                    onChangeWidth={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema1: { ...multiEmaStyle.ema1, width: val } })}
                    style={multiEmaStyle.ema1.style}
                    onChangeStyle={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema1: { ...multiEmaStyle.ema1, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường EMA 89 (Dragon)"
                    visible={multiEmaStyle.ema2.visible}
                    onToggleVisible={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema2: { ...multiEmaStyle.ema2, visible: val } })}
                    color={multiEmaStyle.ema2.color}
                    onChangeColor={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema2: { ...multiEmaStyle.ema2, color: val } })}
                    width={multiEmaStyle.ema2.width}
                    onChangeWidth={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema2: { ...multiEmaStyle.ema2, width: val } })}
                    style={multiEmaStyle.ema2.style}
                    onChangeStyle={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema2: { ...multiEmaStyle.ema2, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường EMA 200 (Trend Line)"
                    visible={multiEmaStyle.ema3.visible}
                    onToggleVisible={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema3: { ...multiEmaStyle.ema3, visible: val } })}
                    color={multiEmaStyle.ema3.color}
                    onChangeColor={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema3: { ...multiEmaStyle.ema3, color: val } })}
                    width={multiEmaStyle.ema3.width}
                    onChangeWidth={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema3: { ...multiEmaStyle.ema3, width: val } })}
                    style={multiEmaStyle.ema3.style}
                    onChangeStyle={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema3: { ...multiEmaStyle.ema3, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường EMA 610 (Barrier)"
                    visible={multiEmaStyle.ema4.visible}
                    onToggleVisible={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema4: { ...multiEmaStyle.ema4, visible: val } })}
                    color={multiEmaStyle.ema4.color}
                    onChangeColor={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema4: { ...multiEmaStyle.ema4, color: val } })}
                    width={multiEmaStyle.ema4.width}
                    onChangeWidth={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema4: { ...multiEmaStyle.ema4, width: val } })}
                    style={multiEmaStyle.ema4.style}
                    onChangeStyle={(val) => setMultiEmaStyle({ ...multiEmaStyle, ema4: { ...multiEmaStyle.ema4, style: val } })}
                  />
                </div>
              )}

              {/* BOLLINGER BANDS STYLE */}
              {indicatorId === "bollinger_bands" && (
                <div className="space-y-2">
                  <LineStyleRow
                    label="Đường Upper Band (Dải trên)"
                    visible={bbStyle.upper.visible}
                    onToggleVisible={(val) => setBbStyle({ ...bbStyle, upper: { ...bbStyle.upper, visible: val } })}
                    color={bbStyle.upper.color}
                    onChangeColor={(val) => setBbStyle({ ...bbStyle, upper: { ...bbStyle.upper, color: val } })}
                    width={bbStyle.upper.width}
                    onChangeWidth={(val) => setBbStyle({ ...bbStyle, upper: { ...bbStyle.upper, width: val } })}
                    style={bbStyle.upper.style}
                    onChangeStyle={(val) => setBbStyle({ ...bbStyle, upper: { ...bbStyle.upper, style: val } })}
                  />

                  <LineStyleRow
                    label="Đường Lower Band (Dải dưới)"
                    visible={bbStyle.lower.visible}
                    onToggleVisible={(val) => setBbStyle({ ...bbStyle, lower: { ...bbStyle.lower, visible: val } })}
                    color={bbStyle.lower.color}
                    onChangeColor={(val) => setBbStyle({ ...bbStyle, lower: { ...bbStyle.lower, color: val } })}
                    width={bbStyle.lower.width}
                    onChangeWidth={(val) => setBbStyle({ ...bbStyle, lower: { ...bbStyle.lower, width: val } })}
                    style={bbStyle.lower.style}
                    onChangeStyle={(val) => setBbStyle({ ...bbStyle, lower: { ...bbStyle.lower, style: val } })}
                  />
                </div>
              )}

              {/* MATRIX PRO STYLE */}
              {indicatorId === "matrix_pro" && (
                <div className="space-y-2">
                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer">
                    <span className="text-slate-300">Dải đường Trung Bình EMA Ribbon (9, 21, 50, 200)</span>
                    <input
                      type="checkbox"
                      checked={matrixData.showEMA === true}
                      onChange={(e) => setMatrixData({ ...matrixData, showEMA: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer">
                    <span className="text-slate-300">Đường mức giá Entry, TP1, TP2, SL trên nến</span>
                    <input
                      type="checkbox"
                      checked={matrixData.showTPSL !== false}
                      onChange={(e) => setMatrixData({ ...matrixData, showTPSL: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer">
                    <span className="text-slate-300">Biểu đồ phụ Động lượng RSI (14)</span>
                    <input
                      type="checkbox"
                      checked={matrixData.showRSI === true}
                      onChange={(e) => setMatrixData({ ...matrixData, showRSI: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          {/* ================= VISIBILITY TAB ================= */}
          {activeTab === "visibility" && (
            <div className="space-y-2 text-slate-400">
              <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider block">
                HIỂN THỊ THEO KHUNG THỜI GIAN
              </span>
              <p className="text-xs">
                Chỉ báo {currentDef.name} này tự động hoạt động đồng bộ trên tất cả các khung thời gian: M1, M5, M15, M30, H1, H4, D1.
              </p>
            

              {/* HMA INDICATOR STYLE */}
              {indicatorId === "hma_indicator" && (
                <div className="space-y-3">
                  <LineStyleRow
                    label="Đường Hull HMA"
                    visible={hmaStyle.visible !== false}
                    onToggleVisible={(val) => setHmaStyle({ ...hmaStyle, visible: val })}
                    color={hmaStyle.bullColor || "#10b981"}
                    onChangeColor={(val) => setHmaStyle({ ...hmaStyle, bullColor: val })}
                    width={hmaStyle.width || 2}
                    onChangeWidth={(val) => setHmaStyle({ ...hmaStyle, width: val })}
                    style={hmaStyle.style || 0}
                    onChangeStyle={(val) => setHmaStyle({ ...hmaStyle, style: val })}
                  />
                </div>
              )}

              {/* HMA BREAKOUT SYSTEM STYLE (hma_breakout_system) */}
              {indicatorId === "hma_breakout_system" && (
                <div className="space-y-2.5">
                  {/* Base Highlight Boxes */}
                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Vùng Hộp Nến Cực Trị (Highlight Base Boxes):</span>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-cyan-400 font-semibold">Hộp Đáy Mua (Lowest Base):</span>
                        <input
                          type="color"
                          value={hmaSystemStyle.buyBoxColor || "#06b6d4"}
                          onChange={(e) => setHmaSystemStyle({ ...hmaSystemStyle, buyBoxColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-amber-400 font-semibold">Hộp Đỉnh Bán (Highest Base):</span>
                        <input
                          type="color"
                          value={hmaSystemStyle.sellBoxColor || "#f59e0b"}
                          onChange={(e) => setHmaSystemStyle({ ...hmaSystemStyle, sellBoxColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Trade Lines */}
                  <div className="space-y-2">
                    <LineStyleRow
                      label="Đường Vào Lệnh Entry (Market)"
                      visible={hmaSystemStyle.entry?.visible !== false}
                      onToggleVisible={(val) => setHmaSystemStyle({ ...hmaSystemStyle, entry: { ...hmaSystemStyle.entry, visible: val } })}
                      color={hmaSystemStyle.entry?.color || "#facc15"}
                      onChangeColor={(val) => setHmaSystemStyle({ ...hmaSystemStyle, entry: { ...hmaSystemStyle.entry, color: val } })}
                      width={hmaSystemStyle.entry?.width || 1}
                      onChangeWidth={(val) => setHmaSystemStyle({ ...hmaSystemStyle, entry: { ...hmaSystemStyle.entry, width: val } })}
                      style={hmaSystemStyle.entry?.style !== undefined ? hmaSystemStyle.entry?.style : 2}
                      onChangeStyle={(val) => setHmaSystemStyle({ ...hmaSystemStyle, entry: { ...hmaSystemStyle.entry, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Cắt Lỗ SL (Dừng Lỗ Bảo Vệ)"
                      visible={hmaSystemStyle.sl?.visible !== false}
                      onToggleVisible={(val) => setHmaSystemStyle({ ...hmaSystemStyle, sl: { ...hmaSystemStyle.sl, visible: val } })}
                      color={hmaSystemStyle.sl?.color || "#ef4444"}
                      onChangeColor={(val) => setHmaSystemStyle({ ...hmaSystemStyle, sl: { ...hmaSystemStyle.sl, color: val } })}
                      width={hmaSystemStyle.sl?.width || 1}
                      onChangeWidth={(val) => setHmaSystemStyle({ ...hmaSystemStyle, sl: { ...hmaSystemStyle.sl, width: val } })}
                      style={hmaSystemStyle.sl?.style || 0}
                      onChangeStyle={(val) => setHmaSystemStyle({ ...hmaSystemStyle, sl: { ...hmaSystemStyle.sl, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP1 (Mục Tiêu 1)"
                      visible={hmaSystemStyle.tp1?.visible !== false}
                      onToggleVisible={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp1: { ...hmaSystemStyle.tp1, visible: val } })}
                      color={hmaSystemStyle.tp1?.color || "#84cc16"}
                      onChangeColor={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp1: { ...hmaSystemStyle.tp1, color: val } })}
                      width={hmaSystemStyle.tp1?.width || 1}
                      onChangeWidth={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp1: { ...hmaSystemStyle.tp1, width: val } })}
                      style={hmaSystemStyle.tp1?.style || 0}
                      onChangeStyle={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp1: { ...hmaSystemStyle.tp1, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP2 (Mục Tiêu 2)"
                      visible={hmaSystemStyle.tp2?.visible !== false}
                      onToggleVisible={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp2: { ...hmaSystemStyle.tp2, visible: val } })}
                      color={hmaSystemStyle.tp2?.color || "#22c55e"}
                      onChangeColor={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp2: { ...hmaSystemStyle.tp2, color: val } })}
                      width={hmaSystemStyle.tp2?.width || 1}
                      onChangeWidth={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp2: { ...hmaSystemStyle.tp2, width: val } })}
                      style={hmaSystemStyle.tp2?.style || 0}
                      onChangeStyle={(val) => setHmaSystemStyle({ ...hmaSystemStyle, tp2: { ...hmaSystemStyle.tp2, style: val } })}
                    />
                  </div>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn mức giá Entry/SL/TP (Labels)</span>
                    <input
                      type="checkbox"
                      checked={hmaSystemStyle.showLabels !== false}
                      onChange={(e) => setHmaSystemStyle({ ...hmaSystemStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}

              {/* BOB VOLMAN PRO STYLE (bob_volman_system) */}
              {indicatorId === "bob_volman_system" && (
                <div className="space-y-2">
                  <LineStyleRow
                    label="Đường 20 EMA Xu Hướng"
                    visible={volmanStyle.emaLine?.visible !== false}
                    onToggleVisible={(val) => setVolmanStyle({ ...volmanStyle, emaLine: { ...volmanStyle.emaLine, visible: val } })}
                    color={volmanStyle.emaLine?.color || "#06b6d4"}
                    onChangeColor={(val) => setVolmanStyle({ ...volmanStyle, emaLine: { ...volmanStyle.emaLine, color: val } })}
                    width={volmanStyle.emaLine?.width || 2}
                    onChangeWidth={(val) => setVolmanStyle({ ...volmanStyle, emaLine: { ...volmanStyle.emaLine, width: val } })}
                    style={volmanStyle.emaLine?.style || 0}
                    onChangeStyle={(val) => setVolmanStyle({ ...volmanStyle, emaLine: { ...volmanStyle.emaLine, style: val } })}
                  />

                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                      <span className="text-slate-300 text-xs">Màu Buildup Mua</span>
                      <input
                        type="color"
                        value={volmanStyle.buyBuildupColor || "#06b6d4"}
                        onChange={(e) => setVolmanStyle({ ...volmanStyle, buyBuildupColor: e.target.value })}
                        className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                      <span className="text-slate-300 text-xs">Màu Buildup Bán</span>
                      <input
                        type="color"
                        value={volmanStyle.sellBuildupColor || "#f43f5e"}
                        onChange={(e) => setVolmanStyle({ ...volmanStyle, sellBuildupColor: e.target.value })}
                        className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                      <span className="text-slate-300 text-xs">Màu Cản Ngang</span>
                      <input
                        type="color"
                        value={volmanStyle.barrierColor || "#f59e0b"}
                        onChange={(e) => setVolmanStyle({ ...volmanStyle, barrierColor: e.target.value })}
                        className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                      <span className="text-slate-300 text-xs">Màu Trendline</span>
                      <input
                        type="color"
                        value={volmanStyle.trendlineColor || "#c084fc"}
                        onChange={(e) => setVolmanStyle({ ...volmanStyle, trendlineColor: e.target.value })}
                        className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                      />
                    </div>
                  </div>

                  <div className="space-y-1 pt-1">
                    <LineStyleRow
                      label="Đường Giá Vào Lệnh (Entry Line)"
                      visible={volmanStyle.entry?.visible !== false}
                      onToggleVisible={(val) => setVolmanStyle({ ...volmanStyle, entry: { ...volmanStyle.entry, visible: val } })}
                      color={volmanStyle.entry?.color || "#38bdf8"}
                      onChangeColor={(val) => setVolmanStyle({ ...volmanStyle, entry: { ...volmanStyle.entry, color: val } })}
                      width={volmanStyle.entry?.width || 1.5}
                      onChangeWidth={(val) => setVolmanStyle({ ...volmanStyle, entry: { ...volmanStyle.entry, width: val } })}
                      style={volmanStyle.entry?.style || 0}
                      onChangeStyle={(val) => setVolmanStyle({ ...volmanStyle, entry: { ...volmanStyle.entry, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Cắt Lỗ SL (Sau Buildup)"
                      visible={volmanStyle.sl?.visible !== false}
                      onToggleVisible={(val) => setVolmanStyle({ ...volmanStyle, sl: { ...volmanStyle.sl, visible: val } })}
                      color={volmanStyle.sl?.color || "#ef4444"}
                      onChangeColor={(val) => setVolmanStyle({ ...volmanStyle, sl: { ...volmanStyle.sl, color: val } })}
                      width={volmanStyle.sl?.width || 1.5}
                      onChangeWidth={(val) => setVolmanStyle({ ...volmanStyle, sl: { ...volmanStyle.sl, width: val } })}
                      style={volmanStyle.sl?.style || 2}
                      onChangeStyle={(val) => setVolmanStyle({ ...volmanStyle, sl: { ...volmanStyle.sl, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP (Target)"
                      visible={volmanStyle.tp1?.visible !== false}
                      onToggleVisible={(val) => setVolmanStyle({ ...volmanStyle, tp1: { ...volmanStyle.tp1, visible: val } })}
                      color={volmanStyle.tp1?.color || "#10b981"}
                      onChangeColor={(val) => setVolmanStyle({ ...volmanStyle, tp1: { ...volmanStyle.tp1, color: val } })}
                      width={volmanStyle.tp1?.width || 1.5}
                      onChangeWidth={(val) => setVolmanStyle({ ...volmanStyle, tp1: { ...volmanStyle.tp1, width: val } })}
                      style={volmanStyle.tp1?.style || 2}
                      onChangeStyle={(val) => setVolmanStyle({ ...volmanStyle, tp1: { ...volmanStyle.tp1, style: val } })}
                    />
                  </div>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn mức giá Entry/SL/TP (Labels)</span>
                    <input
                      type="checkbox"
                      checked={volmanStyle.showLabels !== false}
                      onChange={(e) => setVolmanStyle({ ...volmanStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}

              {/* ORB SESSION PRO STYLE (orb_session_system) */}
              {indicatorId === "orb_session_system" && (
                <div className="space-y-2.5">
                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Màu Sắc Vùng Hộp Phiên Mở Cửa (Session Boxes):</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-cyan-400 font-semibold">Phiên Á (08h30):</span>
                        <input
                          type="color"
                          value={orbSessionStyle.asiaBoxColor || "#38bdf8"}
                          onChange={(e) => setOrbSessionStyle({ ...orbSessionStyle, asiaBoxColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-amber-400 font-semibold">Phiên Âu (15h00):</span>
                        <input
                          type="color"
                          value={orbSessionStyle.londonBoxColor || "#eab308"}
                          onChange={(e) => setOrbSessionStyle({ ...orbSessionStyle, londonBoxColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-purple-400 font-semibold">Phiên Mỹ (20h30):</span>
                        <input
                          type="color"
                          value={orbSessionStyle.nyBoxColor || "#a855f7"}
                          onChange={(e) => setOrbSessionStyle({ ...orbSessionStyle, nyBoxColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1 pt-1">
                    <LineStyleRow
                      label="Đường Giá Vào Lệnh (Entry Line)"
                      visible={orbSessionStyle.entry?.visible !== false}
                      onToggleVisible={(val) => setOrbSessionStyle({ ...orbSessionStyle, entry: { ...orbSessionStyle.entry, visible: val } })}
                      color={orbSessionStyle.entry?.color || "#38bdf8"}
                      onChangeColor={(val) => setOrbSessionStyle({ ...orbSessionStyle, entry: { ...orbSessionStyle.entry, color: val } })}
                      width={orbSessionStyle.entry?.width || 1.5}
                      onChangeWidth={(val) => setOrbSessionStyle({ ...orbSessionStyle, entry: { ...orbSessionStyle.entry, width: val } })}
                      style={orbSessionStyle.entry?.style || 0}
                      onChangeStyle={(val) => setOrbSessionStyle({ ...orbSessionStyle, entry: { ...orbSessionStyle.entry, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Cắt Lỗ SL (Mép Đối Diện)"
                      visible={orbSessionStyle.sl?.visible !== false}
                      onToggleVisible={(val) => setOrbSessionStyle({ ...orbSessionStyle, sl: { ...orbSessionStyle.sl, visible: val } })}
                      color={orbSessionStyle.sl?.color || "#ef4444"}
                      onChangeColor={(val) => setOrbSessionStyle({ ...orbSessionStyle, sl: { ...orbSessionStyle.sl, color: val } })}
                      width={orbSessionStyle.sl?.width || 1.5}
                      onChangeWidth={(val) => setOrbSessionStyle({ ...orbSessionStyle, sl: { ...orbSessionStyle.sl, width: val } })}
                      style={orbSessionStyle.sl?.style || 2}
                      onChangeStyle={(val) => setOrbSessionStyle({ ...orbSessionStyle, sl: { ...orbSessionStyle.sl, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP (Target 3.0R)"
                      visible={orbSessionStyle.tp?.visible !== false}
                      onToggleVisible={(val) => setOrbSessionStyle({ ...orbSessionStyle, tp: { ...orbSessionStyle.tp, visible: val } })}
                      color={orbSessionStyle.tp?.color || "#10b981"}
                      onChangeColor={(val) => setOrbSessionStyle({ ...orbSessionStyle, tp: { ...orbSessionStyle.tp, color: val } })}
                      width={orbSessionStyle.tp?.width || 1.5}
                      onChangeWidth={(val) => setOrbSessionStyle({ ...orbSessionStyle, tp: { ...orbSessionStyle.tp, width: val } })}
                      style={orbSessionStyle.tp?.style || 2}
                      onChangeStyle={(val) => setOrbSessionStyle({ ...orbSessionStyle, tp: { ...orbSessionStyle.tp, style: val } })}
                    />
                  </div>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn mức giá Entry/SL/TP (Labels)</span>
                    <input
                      type="checkbox"
                      checked={orbSessionStyle.showLabels !== false}
                      onChange={(e) => setOrbSessionStyle({ ...orbSessionStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}

              {/* LIQUIDITY SWEEP PRO STYLE (liquidity_sweep_pro) */}
              {(indicatorId === "liquidity_sweep_pro" || indicatorId === "scalping_pro_system") && (
                <div className="space-y-2.5">
                  <div className="p-3 bg-slate-950/70 border border-slate-800 rounded-xl space-y-2.5">
                    <span className="text-xs font-bold text-slate-200 block">Màu Sắc Mức Thanh Khoản & Cấu Trúc MSS:</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-amber-400 font-semibold">BSL (Buy-Side Liq):</span>
                        <input
                          type="color"
                          value={liquiditySweepStyle.bslColor || "#f59e0b"}
                          onChange={(e) => setLiquiditySweepStyle({ ...liquiditySweepStyle, bslColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-cyan-400 font-semibold">SSL (Sell-Side Liq):</span>
                        <input
                          type="color"
                          value={liquiditySweepStyle.sslColor || "#06b6d4"}
                          onChange={(e) => setLiquiditySweepStyle({ ...liquiditySweepStyle, sslColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                      <div className="flex items-center justify-between px-2.5 py-2 rounded-lg bg-slate-900 border border-slate-800">
                        <span className="text-xs text-purple-400 font-semibold">MSS Phá Vỡ:</span>
                        <input
                          type="color"
                          value={liquiditySweepStyle.mssColor || "#a855f7"}
                          onChange={(e) => setLiquiditySweepStyle({ ...liquiditySweepStyle, mssColor: e.target.value })}
                          className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer p-0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1 pt-1">
                    <LineStyleRow
                      label="Đường Giá Vào Lệnh (Entry Line)"
                      visible={liquiditySweepStyle.entry?.visible !== false}
                      onToggleVisible={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, entry: { ...liquiditySweepStyle.entry, visible: val } })}
                      color={liquiditySweepStyle.entry?.color || "#8b5cf6"}
                      onChangeColor={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, entry: { ...liquiditySweepStyle.entry, color: val } })}
                      width={liquiditySweepStyle.entry?.width || 1.5}
                      onChangeWidth={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, entry: { ...liquiditySweepStyle.entry, width: val } })}
                      style={liquiditySweepStyle.entry?.style || 0}
                      onChangeStyle={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, entry: { ...liquiditySweepStyle.entry, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Cắt Lỗ SL (Sau Đỉnh/Đáy Quét)"
                      visible={liquiditySweepStyle.sl?.visible !== false}
                      onToggleVisible={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, sl: { ...liquiditySweepStyle.sl, visible: val } })}
                      color={liquiditySweepStyle.sl?.color || "#ef4444"}
                      onChangeColor={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, sl: { ...liquiditySweepStyle.sl, color: val } })}
                      width={liquiditySweepStyle.sl?.width || 1.5}
                      onChangeWidth={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, sl: { ...liquiditySweepStyle.sl, width: val } })}
                      style={liquiditySweepStyle.sl?.style || 2}
                      onChangeStyle={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, sl: { ...liquiditySweepStyle.sl, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP1 (Đáy/Đỉnh Đối Diện / 1R)"
                      visible={liquiditySweepStyle.tp1?.visible !== false}
                      onToggleVisible={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp1: { ...liquiditySweepStyle.tp1, visible: val } })}
                      color={liquiditySweepStyle.tp1?.color || "#10b981"}
                      onChangeColor={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp1: { ...liquiditySweepStyle.tp1, color: val } })}
                      width={liquiditySweepStyle.tp1?.width || 1.5}
                      onChangeWidth={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp1: { ...liquiditySweepStyle.tp1, width: val } })}
                      style={liquiditySweepStyle.tp1?.style || 2}
                      onChangeStyle={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp1: { ...liquiditySweepStyle.tp1, style: val } })}
                    />

                    <LineStyleRow
                      label="Đường Chốt Lời TP2 (Target 2.0R)"
                      visible={liquiditySweepStyle.tp2?.visible !== false}
                      onToggleVisible={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp2: { ...liquiditySweepStyle.tp2, visible: val } })}
                      color={liquiditySweepStyle.tp2?.color || "#059669"}
                      onChangeColor={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp2: { ...liquiditySweepStyle.tp2, color: val } })}
                      width={liquiditySweepStyle.tp2?.width || 1.5}
                      onChangeWidth={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp2: { ...liquiditySweepStyle.tp2, width: val } })}
                      style={liquiditySweepStyle.tp2?.style || 2}
                      onChangeStyle={(val) => setLiquiditySweepStyle({ ...liquiditySweepStyle, tp2: { ...liquiditySweepStyle.tp2, style: val } })}
                    />
                  </div>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị Khối Vùng Quét (Sweep Zones)</span>
                    <input
                      type="checkbox"
                      checked={liquiditySweepStyle.showZones === true}
                      onChange={(e) => setLiquiditySweepStyle({ ...liquiditySweepStyle, showZones: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>

                  <label className="flex items-center justify-between p-2.5 rounded-lg bg-slate-950/40 border border-slate-800 cursor-pointer mt-1">
                    <span className="text-slate-300">Hiển thị thẻ nhãn mức giá Entry/SL/TP & MSS (Labels)</span>
                    <input
                      type="checkbox"
                      checked={liquiditySweepStyle.showLabels !== false}
                      onChange={(e) => setLiquiditySweepStyle({ ...liquiditySweepStyle, showLabels: e.target.checked })}
                      className="rounded border-slate-700 text-cyan-500 focus:ring-0"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
          <button
            onClick={handleResetDefaults}
            className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1 font-semibold transition cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Mặc định</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
            >
              Hủy bỏ
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-extrabold transition cursor-pointer shadow-md shadow-cyan-500/20"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
